import apiClient from "@/api/client";
import type { OnLeaveBrief } from "@/features/contractors/api/leaves";
import type { ClientBranch } from "@/features/clients/types";
import { saveBlobResponse } from "@/utils/download";
import type { WpwMailBulkAction, WpwMailBulkResult, WpwMailLetter, WpwMailSettings } from "./types";
import type {
  FieldPhotosState,
  ParcelStatusMatrix,
  AttachableLetter,
  ProceedingLinkCandidates,
  CaseCloseValues,
  ResolutionParcels,
  MdcpPanelState,
  StageProceedingInput,
  StageRecipientsSuggestion,
  BulkCreateApplicationsResult,
  BulkGenerateFormatkiResult,
  DeviceFootprintRate,
  AccessBasisType,
  ParcelConflictDecision,
  ParcelConflictResolution,
  AsyncTaskRecord,
  CorrespondenceRegister,
  Designer,
  DesignerInput,
  DxfLayerCatalogEntry,
  DxfLayerPreset,
  DxfValidationReport,
  KpiActionItem,
  KpiItemPing,
  KpiComment,
  KpiReview,
  MetadataPreset,
  Milestone,
  MilestoneNote,
  Office,
  ParcelExportFacets,
  ParcelExportFilters,
  ParcelExportPreview,
  OwnerAccess,
  OwnerAccessEvent,
  Parcel,
  ParcelAccessMode,
  TitleReconcileParcelResult,
  TitleReconcileSummary,
  ParcelOwner,
  ParcelRouteSegment,
  PortfolioRow,
  PowerProject,
  Proceeding,
  ProceedingItem,
  ProceedingType,
  ProjectAccessPermission,
  ProjectContact,
  ProjectContactInput,
  ProjectContactProject,
  ProjectEvent,
  CostEstimate,
  DeliverableTarget,
  RouteVariant,
  RouteVersion,
  RouteVersionDeleteResult,
  TechnicalDeliverable,
  TechnicalProject,
  TopicStatus,
  WpwHandover,
  WpwNeverSentParcel,
  ConnectionAttention,
  KwOverview,
  ParcelKwFile,
  KwOwnerRow,
  WypisFile,
  WypisPageEntry,
  WypisDetachSummary,
  AdministrativeDecisionRow,
  ClauseRevertImpact,
  ApplicationsOverview,
  ApplicationTypeConfig,
  OfficialApplicationDetail,
  OfficialApplicationQueueRow,
  OfficialApplicationRow,
  PaidFeeCandidate,
  ApplicationPoaCandidate,
  PoaProjectOption,
  PowerOfAttorneyRow,
  WypisCandidateGroup,
  WypisOrder,
  WypisOrderCreatePayload,
  WypisOrderRow,
  WypisOrderStatus,
  WypisProposalParcel,
  MzOrder,
  MzOrderCreatePayload,
  MzOrderRow,
  MzOrderStatus,
  MzResultFile,
  MzSuggestContext,
  MpzpCandidatesResponse,
  MpzpOrder,
  MpzpOrderCreatePayload,
  MpzpOrderStatus,
  MpzpOrderForParcel,
  MpzpOrderRow,
  MpzpResultFile,
  WypisResultFile,
  ConnectionFile,
  FolderTreeResponse,
  ShareableOrder,
  DocAnonymization,
  DocAnonymizationModel,
  DocTemplate,
  DocTemplateCheck,
  DocTemplateVersion,
  DocVariableGroup,
  GeneratedDoc,
  TomTitlesOverview,
  RegulatoryAttachment,
  RegulatoryChecklist,
  FormatkaDefaultCell,
  FormatkaFieldDefault,
  FormatkaTemplateCheck,
  FormatkaMode,
  FormatkaPaper,
  FormatkaScope,
  FormatkaType,
  FormatkaSheetSet,
  FormatkaTemplate,
  FormatkaTemplateVersion,
  SheetFrame,
  WypisMergeProposal,
  OfficeDocScan,
  OfficeDocBatch,
  ParcelNote,
  RegisterProjectOption,
  RegulacjaArea,
  ReportSchedule,
  UlicpArea,
  WpwConsentScope,
  WpwMassScopeReview,
  ZEAnnexRequest,
  ZEAnnexScope,
  ZEDocFormat,
  ZEReport,
} from "./types";

const BASE = "/power-projects";

// apiClient defaults to application/json; FormData posts must override it or DRF
// rejects the multipart body with 415 Unsupported Media Type.
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

// Like unwrap, but keeps meta.warnings — soft, non-blocking notices the API returns
// alongside the payload (e.g. creating a wypis order outside the company ordering days).
async function unwrapWithWarnings<T>(
  p: Promise<{ data: { data: T; meta?: { warnings?: string[] } } }>,
): Promise<{ data: T; warnings: string[] }> {
  const r = await p;
  return { data: r.data.data, warnings: r.data.meta?.warnings ?? [] };
}

// ── portfolio + connection profile ────────────────────────────────────────────
export const getPortfolio = () =>
  unwrap<PortfolioRow[]>(apiClient.get(`${BASE}/portfolio/`));

/** Portfel w widoku kompaktowym (~12 kolumn, bez kolumny postępu) — do list wyboru
 *  projektu. Pełny wiersz liczy postęp każdego projektu, a picker pokazuje nazwę. */
export const getPortfolioCompact = () =>
  unwrap<PortfolioRow[]>(apiClient.get(`${BASE}/portfolio/`, { params: { view: "compact" } }));

// „Pobierz projekty (.xlsx)" z portfela — portfel filtruje i sortuje w przeglądarce, więc
// ID-ki idą POST-em w bieżącym porządku, a plik pokrywa dokładnie odfiltrowane wiersze.
// with_value = kolumna „Wartość" (tylko gdy włączono „Pokaż kwoty"; serwer i tak wpisuje
// kwotę wyłącznie dla przedsięwzięć, do których użytkownik ma prawo do kwot).
export const downloadPortfolioXlsx = async (ids: string[], withValue: boolean) => {
  const resp = await apiClient.post(
    `${BASE}/portfolio/export-xlsx/`,
    { ids, with_value: withValue },
    { responseType: "blob" },
  );
  saveBlobResponse(resp, "Projekty_ZE.xlsx");
};

// Katalog kroków + stan wybranego kroku per projekt (kolumna „Kamień" w portfelu).
// Bez `key` przychodzi sama lista wyboru — stany dociągamy dopiero po wyborze kroku.
export const getPortfolioMilestoneColumn = (key?: string) =>
  unwrap<import("./types").MilestoneColumn>(
    apiClient.get(`${BASE}/portfolio/milestone-column/`, { params: key ? { key } : {} }),
  );

// Termin najbliższego raportu okresowego do operatora per projekt (kolumna
// „Najbliższy raport" w portfelu) — jedno zapytanie zamiast zakładki „Raporty" w każdym
// projekcie z osobna.
export const getPortfolioReportColumn = (params: import("./types").ReportColumnParams = {}) =>
  unwrap<import("./types").ReportColumn>(
    apiClient.get(`${BASE}/portfolio/report-column/`, {
      params: {
        ...params,
        // DRF czyta `?status=a,b`, nie powtórzony klucz.
        status: params.status?.length ? params.status.join(",") : undefined,
      },
    }),
  );

// Szukanie po danych powiązanych z projektami (działki, KW, WNU/WPW, zamówienia, pliki,
// kamienie, warstwy DXF) — tego nie ma w wierszu portfela, więc liczy je serwer na żądanie.
export const getPortfolioDeepSearch = (q: string) =>
  unwrap<import("./types").DeepSearchResult>(
    apiClient.get(`${BASE}/portfolio/deep-search/`, { params: { q } }),
  );

export const getConnection = (id: string) =>
  unwrap<PowerProject>(apiClient.get(`${BASE}/connections/${id}/`));

export const getConnectionByProject = (projectId: string) =>
  unwrap<PowerProject | null>(apiClient.get(`${BASE}/connections/by-project/${projectId}/`));

// Karta specyfiki („trudny klient"): flagi + ostrzeżenia + dziennik dla workspace'u.
export const getConnectionCare = (connectionId: string) =>
  unwrap<import("./types").CareSummary>(apiClient.get(`${BASE}/connections/${connectionId}/care/`));

// Rejestr „szczególnych oddziałów" — wszystkie karty specyfiki w jednym miejscu.
export const getCareOverview = () =>
  unwrap<{ rows: import("./types").CareOverviewRow[] }>(
    apiClient.get(`${BASE}/care-overview/`),
  ).then((d) => d.rows);

// Wpis dziennika obserwacji z poziomu projektu (ląduje na najniższym przypiętym
// poziomie: pododdział > oddział > klient, z linkiem zwrotnym do projektu).
export const addConnectionCareNote = (connectionId: string, text: string) =>
  unwrap<import("./types").CareNoteBrief>(
    apiClient.post(`${BASE}/connections/${connectionId}/care/notes/`, { text }),
  );

// Przypisani podwykonawcy — firmy, które pracowały na przedsięwzięciu (zebrane z zamówień).
export const getProjectContractors = (connectionId: string) =>
  unwrap<{ contractors: import("./types").ProjectContractorRow[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/contractors/`),
  ).then((d) => d.contractors);

// Zamówienia jednego podwykonawcy na przedsięwzięciu z danymi zapotrzebowania i
// załącznikami (read-only) — rozwijany wiersz zakładki „Podwykonawcy".
export const getContractorOrders = (connectionId: string, contractorId: string) =>
  unwrap<{ orders: import("./types").ContractorOrderRow[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/contractor-orders/`, {
      params: { contractor: contractorId },
    }),
  ).then((d) => d.orders);

export const createConnection = (payload: Partial<PowerProject>) =>
  unwrap<PowerProject>(apiClient.post(`${BASE}/connections/`, payload));

/** Sugerowana strefa PUWG 2000 dla NOWEGO profilu ZE, policzona z położenia rysunku
 *  przetargu ZE (EPSG:2180), z którego powstało przedsięwzięcie. `suggestion` = null, gdy
 *  przedsięwzięcie nie ma przetargu ZE albo przetarg nie ma geometrii — wtedy `reason`. */
export type SridSuggestion = {
  srid: number;
  lng: number;
  lat: number;
  source: "drawing" | "parcels";
  crosses_zone_boundary: boolean;
  variant_labels: string[];
  tender_id: string;
  tender_title: string;
};
export const suggestSrid = (projectId: string) =>
  apiClient
    .get<{ data: SridSuggestion | null; meta?: { reason?: string } }>(
      `${BASE}/connections/suggest-srid/`,
      { params: { project: projectId } },
    )
    .then((r) => ({ suggestion: r.data.data, reason: r.data.meta?.reason ?? null }));

export const updateConnection = (id: string, payload: Partial<PowerProject>) =>
  unwrap<PowerProject>(apiClient.patch(`${BASE}/connections/${id}/`, payload));

// Rename the linked przedsięwzięcie (enterprises.Project.name). CEO / dyrektor techniczny only.
// Change the przedsięwzięcie's client/branch (CEO / dyrektor techniczny only).
export const setConnectionClient = (
  id: string,
  payload: { client: string | null; client_branch: string | null },
) => unwrap<PowerProject>(apiClient.patch(`${BASE}/connections/${id}/project-client/`, payload));

export const renameConnectionProject = (id: string, name: string) =>
  unwrap<PowerProject>(apiClient.patch(`${BASE}/connections/${id}/project-name/`, { name }));

// Link/unlink the source ZE tender (przetarg źródłowy). CEO / dyrektor techniczny only.
export const setSourceTender = (id: string, sourceTender: string | null) =>
  unwrap<PowerProject>(
    apiClient.patch(`${BASE}/connections/${id}/source-tender/`, { source_tender: sourceTender }),
  );

// Mark the topic status (REALIZACJA / PÓŁKA / KOŃCZENIE / ZAMKNIĘTY). CEO / dyrektor techniczny only.
export const setTopicStatus = (id: string, topicStatus: TopicStatus) =>
  unwrap<PowerProject>(
    apiClient.patch(`${BASE}/connections/${id}/topic-status/`, { topic_status: topicStatus }),
  );

// Remove the ZE profile (the hub przedsięwzięcie is kept). CEO / dyrektor techniczny only.
export const deleteConnection = (id: string) =>
  apiClient.delete(`${BASE}/connections/${id}/`);

// `completed_at` is the REAL date the step closed (not the day it was clicked) — the key
// is sent only when the caller means to change it; `null` clears the date.
export const setMilestone = (
  id: string,
  mkey: string,
  payload: {
    state?: string;
    detail?: string;
    due_date?: string | null;
    completed_at?: string | null;
  },
) => unwrap(apiClient.patch(`${BASE}/connections/${id}/milestones/${mkey}/`, payload));

// Add a manual (custom) milestone to a project's board.
export const addMilestone = (
  id: string, payload: { label: string; state?: string; detail?: string; due_date?: string | null },
) => unwrap<Milestone>(apiClient.post(`${BASE}/connections/${id}/milestones/`, payload));

// Remove a milestone (custom only — built-in cards stay on every board).
export const deleteMilestone = (id: string, mkey: string) =>
  unwrap<{ deleted: boolean }>(apiClient.delete(`${BASE}/connections/${id}/milestones/${mkey}/`));

export const reorderMilestones = (id: string, mtypes: string[]) =>
  unwrap<{ milestone_layout: string[] | null }>(
    apiClient.post(`${BASE}/connections/${id}/reorder-milestones/`, { mtypes }),
  );

// Drop this user's saved card order → back to the shared default layout.
export const resetMilestoneLayout = (id: string) =>
  unwrap<{ milestone_layout: null }>(
    apiClient.delete(`${BASE}/connections/${id}/reorder-milestones/`),
  );

// ── milestone notes thread (newest first) ────────────────────────────────────
export const getMilestoneNotes = (id: string, mkey: string) =>
  unwrap<MilestoneNote[]>(apiClient.get(`${BASE}/connections/${id}/milestones/${mkey}/notes/`));

export const addMilestoneNote = (
  id: string,
  mkey: string,
  text: string,
  mentioned_users: string[] = [],
) =>
  unwrap<MilestoneNote>(
    apiClient.post(`${BASE}/connections/${id}/milestones/${mkey}/notes/`, { text, mentioned_users }),
  );

export const deleteMilestoneNote = (id: string, mkey: string, noteId: string) =>
  unwrap<{ deleted: boolean }>(
    apiClient.delete(`${BASE}/connections/${id}/milestones/${mkey}/notes/${noteId}/`),
  );

export const recomputeParcels = (id: string, routeVersion?: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${id}/recompute-parcels/`,
      routeVersion ? { route_version: routeVersion } : {}),
  );

// Re-fetch geometry for działki whose stored coordinates landed outside Poland
// (legacy SRID mistag — "działki na Atlantyku"). Background task; returns its id.
export const refetchOceanParcels = (id: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${id}/refetch-ocean-parcels/`, {}),
  );

// ── routes ─────────────────────────────────────────────────────────────────────
export const getVariants = (connection: string) =>
  unwrap<RouteVariant[]>(apiClient.get(`${BASE}/route-variants/`, { params: { connection } }));

export const createVariant = (connection: string, label: string) =>
  unwrap<RouteVariant>(apiClient.post(`${BASE}/route-variants/`, { connection, label }));

// Delete a whole route variant — cascade-removes all its versions (DXF/preview data,
// parcel route segments, formatki). Backend gates on koordynator/dyrektor techniczny/CEO.
export const deleteVariant = (id: string) =>
  apiClient.delete(`${BASE}/route-variants/${id}/`);

// Mark a variant as the connection's binding route (recomputes every parcel status).
// Same gate as delete (koordynator / dyrektor techniczny / CEO).
export const setBindingVariant = (id: string) =>
  unwrap<RouteVariant>(apiClient.post(`${BASE}/route-variants/${id}/set-binding/`, {}));

export const uploadRouteVersion = (
  variant: string,
  dxf: File,
  opts?: { baseMap?: File | null; layerPreset?: string | null; comment?: string },
) => {
  const fd = new FormData();
  fd.append("variant", variant);
  fd.append("dxf_file", dxf);
  if (opts?.baseMap) fd.append("base_map_file", opts.baseMap);
  if (opts?.layerPreset) fd.append("layer_preset", opts.layerPreset);
  if (opts?.comment) fd.append("comment", opts.comment);
  // Async: the upload kicks off a background task (parse → preview → parcels). The
  // response carries its id so the UI can poll a live progress log.
  return unwrap<{ task_id: string }>(apiClient.post(`${BASE}/route-versions/`, fd, MULTIPART));
};

// Edit an existing version in place. Only the fields the upload form takes are sent;
// omitted ones stay untouched. Swapping the DXF or the preset makes the server re-run
// parse → preview → działki in the background — then the response carries a task_id like
// uploadRouteVersion. A metadata-only edit applies at once and returns the version, so
// callers must branch on which shape came back.
export const editRouteVersion = (
  versionId: string,
  opts: {
    dxf?: File | null;
    baseMap?: File | null;
    layerPreset?: string | null;
    comment?: string;
  },
) => {
  const fd = new FormData();
  if (opts.dxf) fd.append("dxf_file", opts.dxf);
  if (opts.baseMap) fd.append("base_map_file", opts.baseMap);
  if (opts.layerPreset !== undefined) fd.append("layer_preset", opts.layerPreset ?? "");
  if (opts.comment !== undefined) fd.append("comment", opts.comment);
  return unwrap<{ task_id: string } | RouteVersion>(
    apiClient.post(`${BASE}/route-versions/${versionId}/edit/`, fd, MULTIPART),
  );
};

// Async: convert the version's DXF between coordinate systems (EPSG) — point-by-point
// reprojection of the stored file, then the same reparse pipeline as an edit (parse →
// preview → działki). Same task-log contract as uploadRouteVersion.
export const reprojectRouteVersion = (
  versionId: string,
  payload: { source_srid: number; target_srid: number },
) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/route-versions/${versionId}/reproject/`, payload),
  );

// Delete one route version (koordynator projektu / dyrektor techniczny / CEO). The
// variant's newest remaining version becomes current; the last version takes the whole
// variant with it — the answer says which happened.
export const deleteRouteVersion = (versionId: string) =>
  unwrap<RouteVersionDeleteResult>(apiClient.delete(`${BASE}/route-versions/${versionId}/`));

// Async: copy a drawing variant of the project's source ZE tender into a NEW route
// variant (first version, source=tender_copy) + discover its parcels. Same task-log
// contract as uploadRouteVersion.
export const importTenderVariant = (
  connectionId: string,
  payload: { ze_variant: string; label?: string; preset?: string },
) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/import-tender-variant/`, payload),
  );

// ── MDCP (zamówienie geodezyjne + punkty foto) ──
export const listMdcpPoints = (params: { connection?: string; order?: string }) =>
  unwrap<import("./types").MdcpPhotoPoint[] | { results: import("./types").MdcpPhotoPoint[] }>(
    apiClient.get(`${BASE}/mdcp-points/`, { params: { page_size: 500, ...params } }),
  ).then((d) => (Array.isArray(d) ? d : d.results ?? []));

export const createMdcpPoint = (body: {
  connection: string; lat: number; lng: number; azimuth_deg: number; note?: string;
}) =>
  unwrap<import("./types").MdcpPhotoPoint>(apiClient.post(`${BASE}/mdcp-points/`, body));

export const updateMdcpPoint = (id: string, patch: Partial<{
  lat: number; lng: number; azimuth_deg: number; note: string; is_done: boolean;
}>) =>
  unwrap<import("./types").MdcpPhotoPoint>(apiClient.patch(`${BASE}/mdcp-points/${id}/`, patch));

export const deleteMdcpPoint = (id: string) => apiClient.delete(`${BASE}/mdcp-points/${id}/`);

export const getMdcpPanel = (connectionId: string) =>
  unwrap<MdcpPanelState>(apiClient.get(`${BASE}/connections/${connectionId}/mdcp/`));

// „Zdjęcia z terenu": geotagowane pliki wynikowe zamówień projektu + odległość od zakresu.
export const getFieldPhotos = (connectionId: string) =>
  unwrap<FieldPhotosState>(apiClient.get(`${BASE}/connections/${connectionId}/field-photos/`));

// Postępowanie ofertowe na MDCP: POST zakłada szkic ogłoszenia dla etapu, DELETE odstępuje
// od postępowania (etap wraca do zwykłego zlecania, punkty się odmrażają).
export const createMdcpProceeding = (connectionId: string, payload: StageProceedingInput) =>
  unwrap<MdcpPanelState>(
    apiClient.post(`${BASE}/connections/${connectionId}/mdcp/proceeding/`, payload),
  );
/* Krąg oferentów + preselekt AI dla ogłoszenia, którego jeszcze nie ma. Ten sam adres dla
   wszystkich etapów (slug w ścieżce) — formularz wystawienia też jest wspólny. */
export const getStageProceedingRecipients = (
  connectionId: string,
  stage: string,
  payload: { positions?: string[]; voivodeship?: string } = {},
) =>
  unwrap<StageRecipientsSuggestion>(
    apiClient.post(
      `${BASE}/connections/${connectionId}/${stage}/proceeding/recipients/`,
      payload,
    ),
  );

export const releaseMdcpProceeding = (connectionId: string) =>
  unwrap<MdcpPanelState>(
    apiClient.delete(`${BASE}/connections/${connectionId}/mdcp/proceeding/`),
  );

// Bufory (obrysy) zamówień MDCP — projekt ma ich wiele, mapa wybiera jeden z listy.
// Każda z poniższych operacji zwraca CAŁĄ listę + wskazanie wybranego obrysu:
//  POST   /mdcp/buffers/            = nowy obrys (z geometrii z mapy albo auto z trasy),
//  PUT    /mdcp/buffers/<id>/       = zapis ręcznie przeciągniętych wierzchołków,
//  PATCH  /mdcp/buffers/<id>/       = wybór z listy (is_active), nazwa, szerokość,
//  POST   /mdcp/buffers/<id>/       = „Przywróć auto" (reseed z aktualnej trasy),
//  DELETE /mdcp/buffers/<id>/       = usunięcie (odmowa, gdy obrys poszedł w zamówienie).
type MdcpBufferList = import("./types").MdcpBufferList;
export const getMdcpBuffers = (connectionId: string) =>
  unwrap<MdcpBufferList>(apiClient.get(`${BASE}/connections/${connectionId}/mdcp/buffers/`));
export const createMdcpBuffer = (
  connectionId: string,
  payload: { geometry?: import("./types").GeoJsonGeometry; name?: string; buffer_width_m?: number },
) => unwrap<MdcpBufferList>(apiClient.post(`${BASE}/connections/${connectionId}/mdcp/buffers/`, payload));
export const saveMdcpBuffer = (
  connectionId: string,
  bufferId: string,
  geometry: import("./types").GeoJsonGeometry,
) =>
  unwrap<MdcpBufferList>(
    apiClient.put(`${BASE}/connections/${connectionId}/mdcp/buffers/${bufferId}/`, { geometry }),
  );
export const updateMdcpBuffer = (
  connectionId: string,
  bufferId: string,
  patch: { name?: string; is_active?: boolean; buffer_width_m?: number },
) =>
  unwrap<MdcpBufferList>(
    apiClient.patch(`${BASE}/connections/${connectionId}/mdcp/buffers/${bufferId}/`, patch),
  );
export const reseedMdcpBuffer = (connectionId: string, bufferId: string) =>
  unwrap<MdcpBufferList>(
    apiClient.post(`${BASE}/connections/${connectionId}/mdcp/buffers/${bufferId}/`, {}),
  );
export const deleteMdcpBuffer = (connectionId: string, bufferId: string) =>
  unwrap<MdcpBufferList>(
    apiClient.delete(`${BASE}/connections/${connectionId}/mdcp/buffers/${bufferId}/`),
  );

export const createMdcpOrder = (connectionId: string, fd: FormData) =>
  unwrap<MdcpPanelState>(apiClient.post(`${BASE}/connections/${connectionId}/mdcp/`, fd, MULTIPART));

// AI (Claude) pre-fill of the MDCP order form: proposed line items + quantities for the
// picked contractor. Editable proposal only — failure surfaces as a form warning.
export interface MdcpAiSuggestion {
  items: { line_item_type: string; quantity: number; reason: string }[];
  note: string;
  model: string;
}
export const suggestMdcpItems = (connectionId: string, contractorId: string) =>
  unwrap<MdcpAiSuggestion>(
    apiClient.post(`${BASE}/connections/${connectionId}/mdcp/suggest-items/`, { contractor: contractorId }),
  );

// Contractors eligible for an MDCP order (work type „Geodeta" on the card OR a framework
// agreement with MDCP-ish positions),
// each with the distance from its nearest base/HQ to the project and the verdict of the
// declared area of operation against the order's parcels. The backend already sorts:
// area covers → no area entered / order area unknown → area outside, then by distance.
export type MdcpCoverageStatus = "covers" | "missing" | "unknown_area" | "outside";
export interface MdcpContractorRow {
  id: string;
  name: string;
  distance_km: number | null;
  base_label: string;
  coverage_status: MdcpCoverageStatus;
  /** Human-readable reason for the ranking, e.g. „obszar działania: wielkopolskie (powiaty: kaliski) — pokrywa". */
  coverage_label: string;
  matched_items: string[];
  /** Skąd firma jest w kręgu: typ prac w kartotece i/lub pozycja MDCP w cenniku. */
  eligible_via?: ("work_type" | "line_item")[];
  work_types?: string[];
}
export interface MdcpContractorsResponse {
  contractors: MdcpContractorRow[];
  /** Order area derived from the route parcels: „wielkopolskie: kaliski, ostrowski" (empty when unknown). */
  area_label: string;
}
export const getMdcpContractors = (connectionId: string) =>
  unwrap<MdcpContractorsResponse>(
    apiClient.get(`${BASE}/connections/${connectionId}/mdcp/contractors/`),
  );

// Design (PT/PAB/PZT) contractor filter — hard filter, lexical match on framework positions,
// enriched with workload counts (total active orders + active orders under a coordinator).
export interface DesignContractorRow {
  id: string;
  name: string;
  /** Qualified by the structural category „Dokumentacja projektowa” on an active price-list
   *  position (never by the position's wording). In the default mode every row is eligible;
   *  with `all` the non-eligible ones carry `reason` / `reason_label`. */
  eligible: boolean;
  reason: "no_agreement" | "no_design_position" | "untagged_lookalike" | null;
  reason_label: string | null;
  matched_items: string[];
  /** Positions that merely READ like design documentation but have no category set. */
  lookalike_items: string[];
  active_orders_total: number;
  active_orders_coordinator: number | null;
  // Ocena widoczna wyłącznie dla GK — endpoint jest niedostępny dla podwykonawców.
  rating: number | null;
  ratings_count: number;
}
export interface CoordinatorRow {
  id: string;
  name: string;
}
export interface DesignContractorsResponse {
  contractors: DesignContractorRow[];
  coordinators: CoordinatorRow[];
}
export const getDesignContractors = (params: {
  docType?: string;
  coordinator?: string;
  connection?: string;
  /** Every active contractor (with `eligible` + why-not reasons) instead of the eligible set. */
  all?: boolean;
}) =>
  unwrap<DesignContractorsResponse>(
    apiClient.get(`${BASE}/design-contractors/`, {
      params: {
        ...(params.docType ? { doc_type: params.docType } : {}),
        ...(params.coordinator ? { coordinator: params.coordinator } : {}),
        ...(params.connection ? { connection: params.connection } : {}),
        ...(params.all ? { all: 1 } : {}),
      },
    }),
  );

export interface TechnicalSubcontractorRow {
  id: string;
  name: string;
  projects: { project_id: string; connection_id: string; name: string; order_count: number; active_order_count: number }[];
  coordinators: { id: string; name: string }[];
  project_count: number;
  active_orders: number;
  // Ocena widoczna wyłącznie dla GK — widok tylko dla dyrektora technicznego.
  rating: number | null;
  ratings_count: number;
}
export const getTechnicalSubcontractors = () =>
  unwrap<TechnicalSubcontractorRow[]>(
    apiClient.get(`${BASE}/advisory-technical-subcontractors/`),
  );

export interface CheckpointTemplateRow {
  code: string;
  label: string;
  offset_days: number;
}
/** Checkpoint set for a design-order path (technical | cost_estimate | mdcp). MDCP proposes
 *  „Mapa robocza" (+7 dni) i „MDCP – mapa uwierzytelniona" (+30 dni). Default (no path) =
 *  technical set. */
export const getCheckpointTemplate = (path?: string) =>
  unwrap<{ checkpoints: CheckpointTemplateRow[] }>(
    apiClient.get(`${BASE}/technical-checkpoint-template/`, {
      params: path ? { path } : undefined,
    }),
  ).then((d) => d.checkpoints);

// MDCP w zamówieniu (nasza strona zamówienia i geodeta): punkty + zdjęcia per punkt
// + kontekst mapy (aktualna trasa i działki pod nią).
export const getMdcpOrderPoints = (orderId: string) =>
  unwrap<import("./types").MdcpOrderMap>(
    apiClient.get(`${BASE}/mdcp/order/${orderId}/points/`),
  );

export const uploadMdcpPointPhoto = (pointId: string, file: File) => {
  const fd = new FormData();
  fd.append("image", file);
  return unwrap<import("./types").MdcpPointPhoto>(
    apiClient.post(`${BASE}/mdcp-points/${pointId}/photos/`, fd, MULTIPART),
  );
};

export const deleteMdcpPointPhoto = (photoId: string) =>
  apiClient.delete(`${BASE}/mdcp-point-photos/${photoId}/`);

// Odbiór zdjęć MDCP przez zamawiającego. Bez `photoIds` operacja obejmuje CAŁY materiał
// zamówienia („odrzuć wszystkie" / „przywróć wszystkie"); z listą — wskazane zdjęcia.
export const rejectMdcpPhotos = (orderId: string, reason: string, photoIds?: string[]) =>
  unwrap<{ rejected: number; photos: import("./types").MdcpPointPhoto[] }>(
    apiClient.post(`${BASE}/mdcp/order/${orderId}/photos/reject/`, {
      reason,
      ...(photoIds ? { photo_ids: photoIds } : {}),
    }),
  );

export const restoreMdcpPhotos = (orderId: string, photoIds?: string[]) =>
  unwrap<{ restored: number; photos: import("./types").MdcpPointPhoto[] }>(
    apiClient.post(`${BASE}/mdcp/order/${orderId}/photos/restore/`, {
      ...(photoIds ? { photo_ids: photoIds } : {}),
    }),
  );

// Unified project event timeline — filter by any tag (parcel/office/route_version/kind…).
export const getProjectEvents = (params: Record<string, string>) =>
  unwrap<ProjectEvent[]>(apiClient.get(`${BASE}/project-events/`, { params: { page_size: 500, ...params } }));

// Route course through a działka per version (geometry + length).
export const getParcelRouteSegments = (params: Record<string, string>) =>
  unwrap<ParcelRouteSegment[]>(apiClient.get(`${BASE}/parcel-route-segments/`, { params: { page_size: 500, ...params } }));

// Id działek leżących pod TĄ wersją trasy (odcinek trasy w działce albo nasz obiekt
// na niej). Działki są wspólne dla przyłączenia — bez tego zawężenia ekrany pracujące
// na wybranej wersji pokazują też działki wariantu alternatywnego.
export const getRouteVersionParcelIds = (versionId: string) =>
  unwrap<{ parcel_ids: string[] }>(
    apiClient.get(`${BASE}/route-versions/${versionId}/parcels/`),
  ).then((r) => r.parcel_ids);

// `checkOverlaps` (przycisk na zakładce mapy): próbkuje całą trasę, także pod istniejącymi
// działkami, i oddaje podziały/scalenia z ewidencji jako `parcel_conflicts` w wyniku zadania
// (do decyzji w ParcelConflictsModal) zamiast dokładać nową działkę na starą.
export const recomputeVersion = (versionId: string, opts: { checkOverlaps?: boolean } = {}) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/route-versions/${versionId}/recompute/`, {
      check_overlaps: !!opts.checkOverlaps,
    }),
  );

export const resolveParcelConflicts = (versionId: string, decisions: ParcelConflictDecision[]) =>
  unwrap<ParcelConflictResolution>(
    apiClient.post(`${BASE}/route-versions/${versionId}/resolve-parcel-conflicts/`, { decisions }),
  );

// Pre-upload layer check (the optional "monit"): parses the DXF server-side against
// the preset's layer dictionary; nothing is stored. Backs the confirmation modal.
export const validateDxf = (dxf: File, layerPreset?: string | null) => {
  const fd = new FormData();
  fd.append("dxf_file", dxf);
  if (layerPreset) fd.append("layer_preset", layerPreset);
  return unwrap<DxfValidationReport>(
    apiClient.post(`${BASE}/route-versions/validate-dxf/`, fd, MULTIPART),
  );
};

// (Re)build the raw-DXF QGIS preview from the already-stored files — the retry path
// when the sidecar build failed at upload time (no re-upload needed).
export const rebuildVersionPreview = (versionId: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/route-versions/${versionId}/rebuild-preview/`, {}));

// Sweep ALL route versions and rebuild missing previews (force = re-render existing
// too). Director/CEO only.
export const rebuildAllDxfPreviews = (force = false) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/route-versions/rebuild-previews/`, { force }));

// ── DXF layer presets + their layer dictionaries ────────────────────────────────
export const getDxfLayerPresets = () =>
  unwrap<DxfLayerPreset[]>(apiClient.get(`/dxf-layer-presets/`, { params: { page_size: 1000 } }));

export const createDxfLayerPreset = (payload: { name: string; description?: string }) =>
  unwrap<DxfLayerPreset>(apiClient.post(`/dxf-layer-presets/`, payload));

export const updateDxfLayerPreset = (id: string, payload: Partial<DxfLayerPreset>) =>
  unwrap<DxfLayerPreset>(apiClient.patch(`/dxf-layer-presets/${id}/`, payload));

export const deleteDxfLayerPreset = (id: string) => apiClient.delete(`/dxf-layer-presets/${id}/`);

// Copy a preset together with its whole layer dictionary. The backend picks a
// unique "… (kopia)" name — rename the copy via the edit modal afterwards.
export const duplicateDxfLayerPreset = (id: string) =>
  unwrap<DxfLayerPreset>(apiClient.post(`/dxf-layer-presets/${id}/duplicate/`, {}));

// Re-recognise every CURRENT route version using this preset from its stored DXF —
// picks up layers just mapped in the preset (route, objects, unmapped-layer report) and
// re-runs ULDK parcel discovery, no per-project re-upload. Director/CEO only.
export const reloadPresetProjects = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`/dxf-layer-presets/${id}/reload-projects/`, {}));

// Layer rows (layer_name → kind/semantic_type) belonging to a preset.
export const getDxfLayers = (params?: { preset?: string; activeOnly?: boolean }) =>
  unwrap<DxfLayerCatalogEntry[]>(
    apiClient.get(`/dxf-layer-catalog/`, {
      params: {
        page_size: 1000,
        ...(params?.preset ? { preset: params.preset } : {}),
        ...(params?.activeOnly ? { is_active: true } : {}),
      },
    }),
  );

export const createDxfLayer = (payload: Partial<DxfLayerCatalogEntry>) =>
  unwrap<DxfLayerCatalogEntry>(apiClient.post(`/dxf-layer-catalog/`, payload));

export const updateDxfLayer = (id: string, payload: Partial<DxfLayerCatalogEntry>) =>
  unwrap<DxfLayerCatalogEntry>(apiClient.patch(`/dxf-layer-catalog/${id}/`, payload));

export const deleteDxfLayer = (id: string) => apiClient.delete(`/dxf-layer-catalog/${id}/`);

// ── parcels & owners ─────────────────────────────────────────────────────────
/** Rozbieżności właścicieli KW vs wypis dla całego projektu (A-D1-01). */
export interface OwnerMismatchRow {
  parcel_id: string;
  parcel_number: string;
  obreb: string;
  kw_file_id: string;
  only_in_kw: string[];
  only_in_db: string[];
  matched_count: number;
  accepted_count: number;
  /** Działka bez ani jednego właściciela z wypisu — inny problem niż niezgodność nazwisk. */
  db_empty: boolean;
}

export interface OwnerMismatchReport {
  rows: OwnerMismatchRow[];
  mismatch_count: number;
  checked_parcels: number;
  without_wypis: number;
}

export const getOwnerMismatches = async (connectionId: string): Promise<OwnerMismatchReport> => {
  const r = await apiClient.get<{ data: OwnerMismatchRow[]; meta: Omit<OwnerMismatchReport, "rows"> }>(
    `${BASE}/connections/${connectionId}/owner-mismatches/`,
  );
  return { rows: r.data.data, ...r.data.meta };
};

export const getParcels = (connection: string) =>
  unwrap<Parcel[]>(apiClient.get(`${BASE}/parcels/`, { params: { connection, page_size: 1000 } }));

export const getParcel = (id: string) =>
  unwrap<Parcel>(apiClient.get(`${BASE}/parcels/${id}/`));

export const getParcelStatusMatrix = (id: string) =>
  unwrap<ParcelStatusMatrix>(apiClient.get(`${BASE}/parcels/${id}/status-matrix/`));

export const createParcel = (payload: Partial<Parcel>) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/`, payload));

export const updateParcel = (id: string, payload: Partial<Parcel>) =>
  unwrap<Parcel>(apiClient.patch(`${BASE}/parcels/${id}/`, payload));

export const deleteParcel = (id: string) => apiClient.delete(`${BASE}/parcels/${id}/`);

// ── geoportal "informator": parcel under a clicked map point (ULDK GetParcelByXY) ──
// Independent of our own działki — identifies whatever cadastral parcel sits at the
// clicked lat/lng straight from GUGiK. Returns null when GUGiK has no parcel there.
export interface UldkPointInfo {
  teryt: string | null;
  geometry: import("./types").GeoJsonGeometry | null;
  area_m2: number | null;
  voivodeship: string;
  county: string;
  commune: string;
  region: string;
  parcel: string;
  // Full cadastral attributes from KIEG (grupa rejestrowa, oznaczenie użytku/konturu, data
  // publikacji…) — the exact set the geoportal informator shows, in geoportal order. Empty
  // when KIEG is unavailable (fall back to the admin fields above).
  attributes: { label: string; value: string }[];
}
export const getUldkAtPoint = (lat: number, lng: number) =>
  unwrap<UldkPointInfo | null>(
    apiClient.get(`${BASE}/uldk-at-point/`, { params: { lat, lng } }),
  );

export const fetchParcelUldk = (id: string) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/fetch-uldk/`, {}));

export const fetchParcelOwnership = (id: string) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/fetch-ownership/`, {}));

// Suggested street address for #dzialka_ulica# — reverse geocode (GUGiK UUG → OSM) +
// optional AI reading of the wypis. Returns candidates for the user to review on a map
// and edit; nothing is saved until the user PATCHes street_and_number / locality.
export interface StreetCandidate {
  source: "geoportal" | "osm" | "wypis_ai";
  text: string;
}
export interface StreetSuggestion {
  candidates: StreetCandidate[];
  default: string;
  // Miejscowość from the same geocode pass (UUG → OSM), reported apart from the address
  // strings — „Polna 12" carries no locality. "" when neither geocoder knows it.
  locality: string;
  postal_code: string;
  center: { lat: number; lng: number } | null;
  teryt: string;
}
export const suggestParcelStreet = (id: string) =>
  unwrap<StreetSuggestion>(
    apiClient.post(`${BASE}/parcels/${id}/suggest-street/`, {}),
  );

export const fetchOwnershipCodes = (connectionId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/fetch-ownership-codes/`, {}),
  );

// Read klasoużytek (użytek + klasa bonitacyjna) from the i-geoportal (KIEG) for every
// działka still missing it. Background task; returns its id. Partial coverage — many
// działki come back without data (powiat bez warstwy klasoużytków).
export const fetchLandClasses = (connectionId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/fetch-land-classes/`, {}),
  );

// Słownik stawek powierzchni posadowienia urządzeń (per operator). Czytają wszyscy,
// zapisuje dyrektor techniczny — pole `can_edit` mówi, czy pokazać pola do edycji.
export const listDeviceFootprintRates = () =>
  unwrap<DeviceFootprintRate[]>(apiClient.get(`${BASE}/device-footprint-rates/`, { params: { all: 1 } }));

export const updateDeviceFootprintRate = (
  id: string,
  payload: { width_m?: number; length_m?: number; note?: string },
) => unwrap<DeviceFootprintRate>(apiClient.patch(`${BASE}/device-footprint-rates/${id}/`, payload));

// Powierzchnie do wyłączenia jako GeoJSON — warstwa z kreskowaniem na mapie projektu.
export const getOdrolnienieFootprints = (connectionId: string) =>
  unwrap<GeoJSON.FeatureCollection>(
    apiClient.get(`${BASE}/connections/${connectionId}/odrolnienie-footprints/`),
  );

// Ustal klasoużytek POD każdym obiektem naziemnym (trafo/złącze/słup) — to, czego wykaz
// klasoużytków działki nie rozstrzyga. Zadanie w tle; zwraca id.
// `useAi=false` pomija odczyt z mapy tam, gdzie powiat nie podaje oznaczenia konturu
// (te działki zostaną nierozstrzygnięte, ale nie kosztują wywołań AI).
export const checkLandClassUnderDevices = (
  connectionId: string,
  opts?: { useAi?: boolean; force?: boolean },
) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/check-land-class-under-devices/`, {
      use_ai: opts?.useAi === false ? "0" : "1",
      force: opts?.force ? "1" : "0",
    }),
  );

// Wycinki mapy, na których oparto odczyt klasy pod urządzeniami działki. Adresy plików są
// podpisane i wygasają, więc pobieramy je dopiero przy otwarciu podglądu.
export const getLandClassCrops = (parcelId: string) =>
  unwrap<{ read_id: string; crops: { key: string; caption: string; url: string; filename: string }[] }[]>(
    apiClient.get(`${BASE}/parcels/${parcelId}/land-class-read/`),
  );

// Rozstrzygnięcie człowieka dla jednego odczytu klasy pod urządzeniem. Pusty `klasouzytek`
// wycofuje wcześniejszą decyzję i oddaje sprawę automatowi.
export const setLandClassRead = (
  parcelId: string,
  payload: { read_id: string; klasouzytek: string; note?: string },
) => unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${parcelId}/land-class-read/`, payload));

// Re-read administrative location (województwo/powiat/gmina/obręb) from ULDK for every
// działka in the project still missing part of it. Background task; returns its id.
export const fetchAdminData = (connectionId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/fetch-admin-data/`, {}),
  );

// ── automatyczny przydział JST / instytucji po kodzie własności ──────────────
export interface JstRegistryMatch {
  id: string;
  name: string;
  kind: string;
  department: string;
  full_address: string;
  // Address parts for snapshotting onto owner rows (same contract as the serializer).
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
  phone: string;
  email: string;
}

// Etykieta urzędu w propozycjach — z wydziałem, jeśli rekord go ma (rozróżnia
// np. wydział dróg od wydziału nieruchomości tego samego urzędu).
export const officeLabel = (o: { name: string; department?: string }) =>
  o.department ? `${o.name} — ${o.department}` : o.name;

export interface JstGroupOption {
  institution: string;
  label: string;
  office_kind: string;
  registry_matches: JstRegistryMatch[];
}

export interface JstGroup {
  key: string;
  // null = Skarb Państwa bez podpowiedzi z użytku — user wybiera GDDKiA / KOWR.
  institution: string | null;
  requires_choice: boolean;
  scope: "commune" | "county" | "voivodeship";
  scope_label: string;
  unit_name: string;
  voivodeship: string;
  county: string;
  commune: string;
  options: JstGroupOption[];
  // Grupa „po nazwie właściciela" (kod spoza automatu, np. grupa 6 — ZDP wpisany z
  // wypisu samą nazwą): owner_name/reason opisują skąd propozycja, a każda działka niesie
  // `owner` — istniejący wpis właściciela, któremu przydział podpina urząd.
  owner_name?: string;
  reason?: string;
  parcels: {
    id: string; teryt: string; ownership_code: string; land_use: string;
    owner?: string; owner_name?: string;
  }[];
}

export interface JstProposalResponse {
  groups: JstGroup[];
  skipped: { id: string; teryt: string; reason: string }[];
}

// Jak StarostwoProposal + sugerowany kind tworzonego urzędu.
export interface JstOfficeProposal extends StarostwoProposal {
  kind: string;
}

export interface JstOfficeSuggestion {
  existing: JstRegistryMatch[];
  proposal: JstOfficeProposal | null;
  task_id?: string;
}

export const proposeJstAssign = (connectionId: string, parcelIds?: string[]) =>
  unwrap<JstProposalResponse>(
    apiClient.post(`${BASE}/connections/${connectionId}/jst-assign/propose/`,
      parcelIds?.length ? { parcel_ids: parcelIds } : {}),
  );

// Rejestr odpowiada synchronicznie; brak → zadanie w tle (Claude + web search,
// tylko GDDKiA / KOWR / gmina / drogi powiatowe / drogi wojewódzkie).
export const lookupJstOffice = (
  connectionId: string,
  payload: { institution: string; commune?: string; county?: string; voivodeship?: string; teryts?: string[] },
) =>
  unwrap<JstOfficeSuggestion>(
    apiClient.post(`${BASE}/connections/${connectionId}/jst-assign/lookup/`, payload),
  );

export const applyJstAssign = (
  connectionId: string,
  assignments: { parcel: string; office: string; use_department?: boolean; owner?: string }[],
) =>
  unwrap<{
    created: { parcel: string; teryt: string; office: string; office_name: string }[];
    skipped: { parcel: string; teryt: string; reason: string }[];
    created_count: number;
    skipped_count: number;
  }>(apiClient.post(`${BASE}/connections/${connectionId}/jst-assign/apply/`, { assignments }));

// „Uzgodniona" na działce z właścicielami wymaga `basis` (id podstawy dostępu):
// backend wpisuje wtedy tytuł „Pozyskany" każdemu właścicielowi bez tytułu.
export const setParcelStatus = (
  id: string, statusValue: string, note = "", extra: { basis?: string; secured_at?: string } = {},
) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/set-status/`, { status: statusValue, note, ...extra }));

export const resetParcelStatus = (id: string) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/reset-status/`, {}));

// GK odklikuje, że zmiana trasy na działce nie wymaga działania → gasi badge „do weryfikacji".
export const clearParcelRouteReview = (id: string, note = "") =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/clear-route-review/`, { note }));

// Porównanie tytułów działki z bieżącą trasą (odcinek, typy linii i obiekty, tolerancja 0,7 m) —
// tytuły bez zmiany przebiegu wracają na bieżącą wersję, reszta zostaje do decyzji.
export const reconcileParcelTitles = (id: string) =>
  unwrap<{ result: TitleReconcileParcelResult; parcel: Parcel }>(
    apiClient.post(`${BASE}/parcels/${id}/reconcile-titles/`, {}),
  );

// „Przelicz wszystkie tytuły" — to samo dla każdej działki projektu.
export const reconcileConnectionTitles = (connectionId: string) =>
  unwrap<TitleReconcileSummary>(apiClient.post(`${BASE}/connections/${connectionId}/reconcile-titles/`, {}));

// The single three-way control: consent (wymaga pozyskania) / notification (wymaga
// powiadomienia — demontaż) / not_required (nie wymaga).
export const setParcelAccessMode = (id: string, accessMode: ParcelAccessMode) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/set-access-mode/`, { access_mode: accessMode }));

// Działka podmiotu przyłączanego (inwestora) vs tranzytowa — decyzja koordynatora
// przedsięwzięcia / dyrektora (backend odrzuca 403 dla reszty zespołu).
export const setParcelConnectedEntity = (id: string, value: boolean) =>
  unwrap<Parcel>(
    apiClient.post(`${BASE}/parcels/${id}/set-connected-entity/`, { is_connected_entity: value }));

// „Potrzebny wypis" — ręczne zgłoszenie, że na działkę trzeba zamówić wypis MIMO wpisanych
// właścicieli (panel zamawiania bierze domyślnie tylko działki bez właścicieli).
// `reason` — obowiązkowy przy PONOWNYM zamówieniu (działka ma już wypis, np. zmiana
// właścicieli); backend odrzuca ponowne zgłoszenie bez powodu.
export const setParcelWypisNeeded = (id: string, value: boolean, reason = "") =>
  unwrap<Parcel>(
    apiClient.post(`${BASE}/parcels/${id}/set-wypis-needed/`, { wypis_needed: value, reason }));

// Ręczne „działka wykorzystywana / niewykorzystywana w projekcie" — bije automat
// (przebieg trasy przez działkę, trasa w osi granicy, obiekty na niej lub przy jej
// granicy; linia wzdłuż granicy po stronie sąsiada nie liczy się).
// `null` oddaje sprawę z powrotem automatowi.
export const setParcelUsed = (id: string, value: boolean | null) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/set-used/`, { is_used: value }));

// Ręczna decyzja o odrolnieniu (wyłączeniu z produkcji rolniczej) — odkliknięcie sugestii
// ELGW5 w obie strony; `null` oddaje sprawę z powrotem automatowi. Uwaga opcjonalna.
export const setParcelOdrolnienie = (
  id: string,
  value: "required" | "not_required" | null,
  note = "",
) =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/set-odrolnienie/`, { odrolnienie: value, note }));

// Decyzja koordynatora „działka jest / nie jest drogą" — przykrywa wpisy drogowe z KW
// (te zostają w analizie księgi); `null` wraca do werdyktu z księgi. Przelicza status
// działki (droga + ponad 50% udziałów = „Uzgodniona"); reszta zespołu dostaje 403.
export const setParcelRoad = (id: string, value: boolean | null, note = "") =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${id}/set-road/`, { is_road: value, note }));

// ── notatki działki (jeden wątek: działka + jej właściciele) ──────────────────
export const getParcelNotes = (id: string) =>
  unwrap<ParcelNote[]>(apiClient.get(`${BASE}/parcels/${id}/notes/`));

// `owner` (opcjonalnie) przypina notatkę do współwłaściciela tej działki.
export const addParcelNote = (id: string, text: string, owner?: string | null) =>
  unwrap<ParcelNote>(apiClient.post(`${BASE}/parcels/${id}/notes/`, { text, owner: owner || null }));

export const deleteParcelNote = (id: string, noteId: string) =>
  apiClient.delete(`${BASE}/parcels/${id}/notes/${noteId}/`);

// Status-change history + the per-owner access titles for the parcel.
export const getParcelHistory = (id: string) =>
  unwrap<{ status_events: unknown[]; accesses: OwnerAccess[] }>(
    apiClient.get(`${BASE}/parcels/${id}/history/`));

// includeOutdated — także właściciele NIEAKTUALNI (ponowny wypis), domyślnie schowani.
export const getParcelOwners = (parcelId: string, includeOutdated = false) =>
  unwrap<ParcelOwner[]>(apiClient.get(`${BASE}/parcel-owners/`, {
    params: { parcel: parcelId, page_size: 500, ...(includeOutdated ? { include_outdated: "1" } : {}) },
  }));

export const createOwner = (payload: Partial<ParcelOwner>) =>
  unwrap<ParcelOwner>(apiClient.post(`${BASE}/parcel-owners/`, payload));

export const updateOwner = (id: string, payload: Partial<ParcelOwner>) =>
  unwrap<ParcelOwner>(apiClient.patch(`${BASE}/parcel-owners/${id}/`, payload));

export const deleteOwner = (id: string) => apiClient.delete(`${BASE}/parcel-owners/${id}/`);

export const uploadWypis = (ownerId: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<ParcelOwner>(apiClient.post(`${BASE}/parcel-owners/${ownerId}/upload-wypis/`, fd, MULTIPART));
};

// Ręczne małżeństwo (wspólność małżeńska = jeden podpis): TEN właściciel + współwłaściciel
// tej samej działki dostają wspólną marital_group; leave wyłącza go z małżeństwa.
export const joinOwnerMarital = (ownerId: string, spouseId: string) =>
  unwrap<ParcelOwner>(apiClient.post(`${BASE}/parcel-owners/${ownerId}/join-marital/`, { spouse: spouseId }));
export const leaveOwnerMarital = (ownerId: string) =>
  unwrap<ParcelOwner>(apiClient.post(`${BASE}/parcel-owners/${ownerId}/leave-marital/`, {}));

// Per-owner input for the OMI regulatory application (uwagi → #regulacja_uwagi#).
export const updateOwnerRegulatoryNotes = (ownerId: string, notes: string) =>
  unwrap<ParcelOwner>(apiClient.patch(`${BASE}/parcel-owners/${ownerId}/regulatory/`, { notes }));

// ── access basis registry (rozszerzalny — director adds bases without a migration) ──
export const getAccessBasisTypes = (activeOnly = false) =>
  unwrap<AccessBasisType[]>(
    apiClient.get(`${BASE}/access-basis-types/`, {
      params: { page_size: 1000, ...(activeOnly ? { is_active: true } : {}) },
    }),
  );

export const createAccessBasisType = (payload: Partial<AccessBasisType>) =>
  unwrap<AccessBasisType>(apiClient.post(`${BASE}/access-basis-types/`, payload));

export const updateAccessBasisType = (id: string, payload: Partial<AccessBasisType>) =>
  unwrap<AccessBasisType>(apiClient.patch(`${BASE}/access-basis-types/${id}/`, payload));

export const deleteAccessBasisType = (id: string) => apiClient.delete(`${BASE}/access-basis-types/${id}/`);

// ── per-owner access titles (OwnerAccess) ────────────────────────────────────────
export const getOwnerAccesses = (params: Record<string, string>) =>
  unwrap<OwnerAccess[]>(apiClient.get(`${BASE}/owner-access/`, { params: { page_size: 1000, ...params } }));

export const createOwnerAccess = (ownerId: string, payload: Partial<OwnerAccess> = {}) =>
  unwrap<OwnerAccess>(apiClient.post(`${BASE}/owner-access/`, { owner: ownerId, ...payload }));

// Generic transition (SECURED requires a basis). `basis`/`route_version` are ids.
export const transitionOwnerAccess = (
  id: string,
  payload: { to_status: string; basis?: string | null; route_version?: string | null; secured_at?: string | null; note?: string },
) => unwrap<OwnerAccess>(apiClient.post(`${BASE}/owner-access/${id}/set-status/`, payload));

// Mark SECURED with an explicit basis (route_version defaults to current). Multifile:
// attach supporting documents (signed zgoda / decyzja / skan) → sent as multipart.
export const secureOwnerAccess = (
  id: string,
  payload: { basis: string; route_version?: string | null; secured_at?: string | null; note?: string; files?: File[]; z7_consent_form?: string },
) => {
  const fd = new FormData();
  fd.append("basis", payload.basis);
  if (payload.z7_consent_form !== undefined) fd.append("z7_consent_form", payload.z7_consent_form);
  if (payload.route_version) fd.append("route_version", payload.route_version);
  if (payload.secured_at) fd.append("secured_at", payload.secured_at);
  if (payload.note) fd.append("note", payload.note);
  for (const f of payload.files ?? []) fd.append("files", f);
  return unwrap<OwnerAccess>(apiClient.post(`${BASE}/owner-access/${id}/secure/`, fd, MULTIPART));
};

// Attach more documents to an existing title at any time (multifile).
export const uploadOwnerAccessFiles = (id: string, files: File[]) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return unwrap<OwnerAccess>(apiClient.post(`${BASE}/owner-access/${id}/upload-files/`, fd, MULTIPART));
};

// Kosz przy dokumencie tytułu (zakładka „Właściciele"): odpina plik; ostatni dokument
// tytułu „Pozyskany" cofa pozyskanie (backend wraca tytuł na „Nie rozpoczęto").
export const removeOwnerAccessFile = (id: string, fileId: string) =>
  unwrap<OwnerAccess>(apiClient.delete(`${BASE}/owner-access/${id}/files/${fileId}/`));

export const ownerAccessNotRequired = (id: string, note = "") =>
  unwrap<OwnerAccess>(apiClient.post(`${BASE}/owner-access/${id}/not-required/`, { note }));

export const reportOwnerAccessProblem = (id: string, note: string) =>
  unwrap<OwnerAccess>(apiClient.post(`${BASE}/owner-access/${id}/report-problem/`, { note }));

// "Zaakceptuj uzgodnienie dla aktualnej trasy" — stamp route_version = current.
export const extendOwnerAccessRoute = (id: string, note = "") =>
  unwrap<OwnerAccess>(apiClient.post(`${BASE}/owner-access/${id}/extend-route/`, { note }));

export const getOwnerAccessEvents = (id: string) =>
  unwrap<OwnerAccessEvent[]>(apiClient.get(`${BASE}/owner-access/${id}/events/`));

// ── proceedings (universal correspondence thread) ────────────────────────────────
export const getProceedings = (params: Record<string, string>) =>
  unwrap<Proceeding[]>(apiClient.get(`${BASE}/proceedings/`, { params: { page_size: 500, ...params } }));

// Pojedyncza sprawa — listy doradcy (Kontrola procesu / TODO) mają tylko jej id.
export const getProceeding = (id: string) =>
  unwrap<Proceeding>(apiClient.get(`${BASE}/proceedings/${id}/`));

export const createProceeding = (payload: {
  connection: string;
  scope: string;
  type: string;
  type_note?: string;
  recipient_office?: string | null;
  owners?: string[];
  parcels?: string[];
  route_version?: string | null;
  kpa_days?: number;
  note?: string;
  /** „Dodaj do ponagleń" — dzwoni zakładający, termin = Termin KPA. */
  control_calls?: boolean;
}) => unwrap<Proceeding>(apiClient.post(`${BASE}/proceedings/`, payload));

/** Sprawa w kalendarzu ponagleń: włączenie przypisuje telefony pytającemu. */
export const setProceedingControlCalls = (id: string, enabled: boolean) =>
  unwrap<Proceeding>(apiClient.post(`${BASE}/proceedings/${id}/control-calls/`, { enabled }));

export const updateProceeding = (id: string, payload: Partial<Proceeding> & Record<string, unknown>) =>
  unwrap<Proceeding>(apiClient.patch(`${BASE}/proceedings/${id}/`, payload));

export const deleteProceeding = (id: string) => apiClient.delete(`${BASE}/proceedings/${id}/`);

// Close with an outcome. PARTIAL keeps the agreed parcels (resolved_parcels) on the case;
// on an OFFICE thread POSITIVE/PARTIAL secures the owners they resolve.
export const closeProceeding = (id: string, payload: CaseCloseValues) =>
  unwrap<Proceeding>(apiClient.post(`${BASE}/proceedings/${id}/close/`, payload));

// Działki sprawy do zaznaczenia przy rozstrzygnięciu częściowym (+ podpowiedź WPW).
export const getResolutionParcels = (id: string) =>
  unwrap<ResolutionParcels>(apiClient.get(`${BASE}/proceedings/${id}/resolution-parcels/`));

export const reopenProceeding = (id: string, note = "") =>
  unwrap<Proceeding>(apiClient.post(`${BASE}/proceedings/${id}/reopen/`, { note }));

// Powiązania sprawy z zamówieniami projektu i pakietami wniosków (zakładka „Sprawy").
export const getProceedingLinkCandidates = (id: string) =>
  unwrap<ProceedingLinkCandidates>(apiClient.get(`${BASE}/proceedings/${id}/link-candidates/`));
export const linkProceedingOrder = (id: string, order: string) =>
  unwrap<Proceeding>(apiClient.post(`${BASE}/proceedings/${id}/link-order/`, { order }));
export const unlinkProceedingOrder = (id: string, order: string) =>
  unwrap<Proceeding>(apiClient.post(`${BASE}/proceedings/${id}/unlink-order/`, { order }));
// Sprawa idzie za statusem pakietu: ZAKOŃCZONY zamyka otwartą sprawę (wtedy
// `close.outcome` jest wymagane), NIEZAKOŃCZONY otwiera zamkniętą (wtedy `reopen` = true).
export const linkProceedingApplication = (
  id: string,
  application: string,
  close?: CaseCloseValues,
  reopen?: boolean,
) =>
  unwrap<Proceeding>(
    apiClient.post(`${BASE}/proceedings/${id}/link-application/`, {
      application, ...close, ...(reopen ? { reopen_case: true } : {}),
    }),
  );
// Która pozycja „Wniosek (nasz)" sprawy jest pismem podpiętego pakietu (`item` null = żadna) —
// gdy znak pozycji nie wskazał numeru pakietu. Status pozycji idzie potem za pakietem.
export const assignProceedingPackageItem = (id: string, application: string, item: string | null) =>
  unwrap<Proceeding>(
    apiClient.post(`${BASE}/proceedings/${id}/assign-package-item/`, { application, item }),
  );
// Odpięcie pakietu od sprawy założonej automatycznie przy jego wysyłce USUWA tę sprawę
// (`deleteCase` = potwierdzenie) — wtedy zamiast sprawy wraca {proceeding_deleted: true}.
export type UnlinkedFromDeletedCase = { detached: string; proceeding: string; proceeding_deleted: true };
export const unlinkProceedingApplication = (id: string, application: string, deleteCase?: boolean) =>
  unwrap<Proceeding | UnlinkedFromDeletedCase>(
    apiClient.post(`${BASE}/proceedings/${id}/unlink-application/`, {
      application, ...(deleteCase ? { delete_case: true } : {}),
    }),
  );

// 1-klik z odpowiedzi urzędu: dokłada aktywny bloczek „uzupełnienie" do torów
// działek sprawy (piłka wraca do nas z własnym zegarem lokalizacji).
export const markProceedingUzupelnienie = (id: string, note = "") =>
  unwrap<{ parcels_marked: number }>(
    apiClient.post(`${BASE}/proceedings/${id}/mark-uzupelnienie/`, { note }));

// ── proceeding items (multifile; created via the proceeding's add-* actions) ───────
// The caller assembles the FormData (text fields + repeated `files` parts).
export const addApplication = (proceedingId: string, fd: FormData) =>
  unwrap<ProceedingItem>(apiClient.post(`${BASE}/proceedings/${proceedingId}/items/application/`, fd, MULTIPART));

export const addResponse = (proceedingId: string, fd: FormData) =>
  unwrap<ProceedingItem>(apiClient.post(`${BASE}/proceedings/${proceedingId}/items/response/`, fd, MULTIPART));

export const addEvent = (proceedingId: string, fd: FormData) =>
  unwrap<ProceedingItem>(apiClient.post(`${BASE}/proceedings/${proceedingId}/items/event/`, fd, MULTIPART));

// Application lifecycle (gated by per-project can_approve / can_submit).
export const approveApplication = (itemId: string) =>
  unwrap<ProceedingItem>(apiClient.post(`${BASE}/proceeding-items/${itemId}/approve/`, {}));

export const submitApplication = (itemId: string, dateSent?: string | null) =>
  unwrap<ProceedingItem>(
    apiClient.post(`${BASE}/proceeding-items/${itemId}/submit/`, dateSent ? { date_sent: dateSent } : {}),
  );

// Correction of clerical mistakes on a thread item. Wniosek: any status (approved/submitted
// gated by can_submit on the backend); odpowiedź/zdarzenie: always can_submit. The backend
// picks the editable field set by the item's kind. Metadata only — files go through
// uploadProceedingItemFiles / deleteProceedingItemFile (same gate).
export type ItemEditPayload = Partial<{
  note: string;
  // application
  our_ref: string;
  channel: string;
  channel_ref: string;
  date_sent: string;
  // response
  office_ref: string;
  date_received: string;
  // event
  event_type: string;
}>;

export const updateProceedingItem = (itemId: string, payload: ItemEditPayload) =>
  unwrap<ProceedingItem>(apiClient.patch(`${BASE}/proceeding-items/${itemId}/`, payload));

export const deleteProceedingItem = (itemId: string) =>
  apiClient.delete(`${BASE}/proceeding-items/${itemId}/`);

export const uploadProceedingItemFiles = (itemId: string, files: File[]) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return unwrap<ProceedingItem>(
    apiClient.post(`${BASE}/proceeding-items/${itemId}/files/`, fd, MULTIPART),
  );
};

export const deleteProceedingItemFile = (itemId: string, fileId: string) =>
  unwrap<ProceedingItem>(apiClient.delete(`${BASE}/proceeding-items/${itemId}/files/${fileId}/`));

// "Uznaj jako dostarczone" — a NOTIFICATION letter's zwrotka (or deemed delivery after double
// awizo) closes the addressee: recipient_owner → SECURED (basis=zawiadomienie).
export const confirmNotificationDelivered = (itemId: string, dateDelivered?: string | null) =>
  unwrap<ProceedingItem>(
    apiClient.post(
      `${BASE}/proceeding-items/${itemId}/confirm-delivered/`,
      dateDelivered ? { date_delivered: dateDelivered } : {},
    ),
  );

// "Potwierdź list" — koordynator projektu / dyrektor techniczny odnotowuje, że wysłany list
// (scope=LETTER) dotarł do właściciela. Nie rozstrzyga uzgodnienia — tytuł oznacza się osobno.
export const confirmLetter = (itemId: string, note = "") =>
  unwrap<ProceedingItem>(
    apiClient.post(`${BASE}/proceeding-items/${itemId}/confirm-letter/`, note ? { note } : {}),
  );

// Demolition notifications: inform ALL selected owners (no consent). Fans out one paper letter
// per owner into the DLC_02 dziennik. 1 właściciel = 1 osobna korespondencja: each owner's own
// document(s) go under the `files_<ownerId>` part.
export const sendDemolitionNotifications = (
  connectionId: string,
  owners: { id: string; files: File[] }[],
  note = "",
  letter = false,
) => {
  const fd = new FormData();
  owners.forEach((o) => {
    fd.append("selected_owner_ids", o.id);
    o.files.forEach((f) => fd.append(`files_${o.id}`, f));
  });
  if (note) fd.append("note", note);
  if (letter) fd.append("letter", "1");
  return unwrap<Proceeding>(
    apiClient.post(`${BASE}/connections/${connectionId}/send-demolition-notifications/`, fd, MULTIPART),
  );
};

// ── WPW — ścieżka listowna (listy automatyczne) ──────────────────────────────
/** Listy automatyczne WPW: ?connection= / ?parcel= / ?owner= / ?state= / ?pending=1. */
export const getWpwMailLetters = (params: Record<string, string>) =>
  unwrap<WpwMailLetter[]>(apiClient.get(`${BASE}/wpw-mail-letters/`, { params }));

/** Jednorazowe „Anuluj wysyłkę”: list nie wyjdzie w tym zleceniu WPW; nowe zlecenie uzbraja go ponownie. */
export const cancelWpwMailLetter = (id: string, note = "") =>
  unwrap<WpwMailLetter>(apiClient.post(`${BASE}/wpw-mail-letters/${id}/cancel/`, { note }));

/** Trwała blokada automatycznej wysyłki listu dla JEDNEGO właściciela (karta właściciela). */
export const setOwnerWpwMailBlock = (ownerId: string, blocked: boolean, reason = "") =>
  unwrap<ParcelOwner>(apiClient.post(`${BASE}/parcel-owners/${ownerId}/wpw-mail-block/`, { blocked, reason }));

/** Ręczne „Wstrzymaj”: list czeka, nic go nie wysyła ani nie wznawia, dopóki ktoś nie kliknie „Wznów”. */
export const holdWpwMailLetter = (id: string, note = "") =>
  unwrap<WpwMailLetter>(apiClient.post(`${BASE}/wpw-mail-letters/${id}/hold/`, { note }));

/** Wznów wstrzymany list; po terminie wysyłki to od razu zlecenie wydruku („wyślij"). */
export const resumeWpwMailLetter = (id: string) =>
  unwrap<WpwMailLetter>(apiClient.post(`${BASE}/wpw-mail-letters/${id}/resume/`, {}));

/** Zleć wysyłkę od razu, nie czekając na termin. */
export const sendWpwMailLetterNow = (id: string) =>
  unwrap<WpwMailLetter>(apiClient.post(`${BASE}/wpw-mail-letters/${id}/send-now/`, {}));

/** „Opcje dla zaznaczonych”: ta sama operacja na wielu listach. Każdy list osobno, z bramkami
 * akcji pojedynczej — odrzucone wracają w `failed` (nie cofają pozostałych). */
export const bulkWpwMailLetters = (ids: string[], action: WpwMailBulkAction, note = "") =>
  unwrap<WpwMailBulkResult>(apiClient.post(`${BASE}/wpw-mail-letters/bulk/`, { ids, action, note }));

/** Rozstrzygnięcie zwrotu: uzgodnienie / odmowa / anuluj podpinanie. */
export const decideWpwMailLetter = (id: string, outcome: "agreement" | "refusal" | "cancel_attach", note = "") =>
  unwrap<WpwMailLetter>(apiClient.post(`${BASE}/wpw-mail-letters/${id}/decide/`, { outcome, note }));

/** „Oznacz jako nieaktualny” wysłany list (mapa zmieniła się po wysyłce): zamyka go jak
    odmowę, tytułu właściciela nie rusza — list kolejny pakietu można wysłać. */
export const markWpwMailLetterOutdated = (id: string, note = "") =>
  unwrap<WpwMailLetter>(apiClient.post(`${BASE}/wpw-mail-letters/${id}/mark-outdated/`, { note }));

/** Blokada automatycznej wysyłki listów WPW dla CAŁEJ działki (każdego właściciela). */
export const setParcelWpwMailBlock = (parcelId: string, blocked: boolean, reason = "") =>
  unwrap<Parcel>(apiClient.post(`${BASE}/parcels/${parcelId}/wpw-mail-block/`, { blocked, reason }));

/** Globalny próg dni automatycznej wysyłki (GET: każdy, PATCH: CEO). */
export const getWpwMailSettings = () =>
  unwrap<WpwMailSettings>(apiClient.get(`${BASE}/wpw-mail-settings/`));
export const patchWpwMailSettings = (payload: { auto_dispatch_days: number }) =>
  unwrap<WpwMailSettings>(apiClient.patch(`${BASE}/wpw-mail-settings/`, payload));

/** Pisma wychodzące z Dziennika (DLC_02) do podpięcia pod właściciela jako list. */
export const getAttachableLetters = (connectionId: string) =>
  unwrap<AttachableLetter[]>(apiClient.get(`${BASE}/connections/${connectionId}/attachable-letters/`));

/** Podepnij istniejące pismo z Dziennika pod właściciela (albo kilku naraz — jedna koperta do
 * współwłaścicieli): powstaje sprawa LETTER z osobną pozycją per właściciel, wskazującą na TEN
 * obiekt Dziennika (bez drugiego wpisu); doręczenie potwierdza zwrotka w Dzienniku, wynik listu
 * wpisuje się per właściciel. */
export const attachOwnerLetter = (
  connectionId: string,
  ownerIds: string[],
  pick: { kind: "register" | "print_order"; id: string },
  note = "",
) =>
  unwrap<Proceeding>(
    apiClient.post(`${BASE}/connections/${connectionId}/attach-owner-letter/`, {
      owners: ownerIds,
      ...(pick.kind === "print_order" ? { print_order: pick.id } : { entry: pick.id }),
      ...(note ? { note } : {}),
    }),
  );

/** Odepnij list podpięty z Dziennika od właściciela — tylko dopóki nie ma wyniku. Obiekt
 * w Dzienniku zostaje (znów do podpięcia); sprawa znika, jeśli to była jej jedyna pozycja. */
export const detachLetter = (itemId: string) =>
  unwrap<{ detached: string; proceeding: string; proceeding_deleted: boolean }>(
    apiClient.post(`${BASE}/proceeding-items/${itemId}/detach-letter/`),
  );

/** Wynik listu — „uzgodnienie" pozyskuje tytuł (zgoda właściciela), „odmowa" ustawia tytuł
 * „Odmowa"; gdy każdy list sprawy ma wynik, sprawa zamyka się sama. Nie czeka na zwrotkę
 * (doręczenie to osobny fakt). `files` (opcjonalnie) = pismo z uzgodnieniem / odmową →
 * dokumenty tytułu właściciela; wtedy multipart, bez plików zwykły JSON. */
export const setLetterOutcome = (
  itemId: string,
  outcome: "agreement" | "refusal",
  note = "",
  files: File[] = [],
) => {
  const url = `${BASE}/proceeding-items/${itemId}/letter-outcome/`;
  if (files.length === 0) {
    return unwrap<ProceedingItem>(apiClient.post(url, { outcome, ...(note ? { note } : {}) }));
  }
  const fd = new FormData();
  fd.append("outcome", outcome);
  if (note) fd.append("note", note);
  for (const f of files) fd.append("files", f);
  return unwrap<ProceedingItem>(apiClient.post(url, fd, MULTIPART));
};

// ── proceeding types (registry; idempotent create supports the "Inny" flow) ────────
export const getProceedingTypes = (activeOnly = false) =>
  unwrap<ProceedingType[]>(
    apiClient.get(`${BASE}/proceeding-types/`, {
      params: { page_size: 1000, ...(activeOnly ? { is_active: true } : {}) },
    }),
  );

export const createProceedingType = (name: string) =>
  unwrap<ProceedingType>(apiClient.post(`${BASE}/proceeding-types/`, { name }));

// Rename a type (director-only on the backend; the global registry is shared across projects).
export const updateProceedingType = (id: string, name: string) =>
  unwrap<ProceedingType>(apiClient.patch(`${BASE}/proceeding-types/${id}/`, { name }));

// ── per-project access permissions (panel "Metadane", director) ────────────────────
export const getProjectPermissions = (connection: string) =>
  unwrap<ProjectAccessPermission[]>(
    apiClient.get(`${BASE}/project-permissions/`, { params: { connection, page_size: 1000 } }),
  );

export const setProjectPermission = (payload: {
  connection: string;
  user: string;
  can_approve: boolean;
  can_submit: boolean;
}) => unwrap<ProjectAccessPermission>(apiClient.post(`${BASE}/project-permissions/set/`, payload));

export const deleteProjectPermission = (id: string) =>
  apiClient.delete(`${BASE}/project-permissions/${id}/`);

// ── KPI reviews (recurring per-project review meetings) ─────────────────────────
export const getKpiReviews = (connection: string) =>
  unwrap<KpiReview[]>(apiClient.get(`${BASE}/kpi-reviews/`, { params: { connection, page_size: 500 } }));

export const createKpiReview = (payload: {
  connection: string;
  scheduled_for?: string | null;
  attendees?: string[];
  facilitator?: string | null;
  agenda?: string;
}) => unwrap<KpiReview>(apiClient.post(`${BASE}/kpi-reviews/`, payload));

export const updateKpiReview = (id: string, payload: Partial<KpiReview>) =>
  unwrap<KpiReview>(apiClient.patch(`${BASE}/kpi-reviews/${id}/`, payload));

export const closeKpiReview = (id: string, summary?: string) =>
  unwrap<KpiReview>(apiClient.post(`${BASE}/kpi-reviews/${id}/close/`, summary != null ? { summary } : {}));

export const cancelKpiReview = (id: string) =>
  unwrap<KpiReview>(apiClient.post(`${BASE}/kpi-reviews/${id}/cancel/`, {}));

// `review` names the meeting a task was planned at. Self-service adds send `connection`
// instead and let the backend resolve (or open) the current KPI — nobody writing down
// their own week should have to know which meeting is current.
export const createKpiActionItem = (payload: {
  review?: string;
  connection?: string;
  parent?: string | null;
  description: string;
  assignee?: string | null;
  due_date?: string | null;
  priority?: string;
  // "kpi" wysyła TYLKO pole ustaleń na karcie spotkania; wszystko inne to zapisek własny.
  origin?: "kpi";
}) => unwrap<KpiActionItem>(apiClient.post(`${BASE}/kpi-action-items/`, payload));

export const updateKpiActionItem = (id: string, payload: Partial<KpiActionItem>) =>
  unwrap<KpiActionItem>(apiClient.patch(`${BASE}/kpi-action-items/${id}/`, payload));

export const setKpiActionItemStatus = (id: string, statusValue: string) =>
  unwrap<KpiActionItem>(apiClient.post(`${BASE}/kpi-action-items/${id}/set-status/`, { status: statusValue }));

export const deleteKpiActionItem = (id: string) => apiClient.delete(`${BASE}/kpi-action-items/${id}/`);

// Dzwoneczek: ponaglenie o konkretne zadanie. Adresatów wybiera backend (koordynatorzy
// przedsięwzięcia zawsze + odpowiedzialny) — front nie zna listy koordynatorów.
export const pingKpiActionItem = (id: string) =>
  unwrap<{ item: KpiActionItem; recipients: { id: string; name: string }[] }>(
    apiClient.post(`${BASE}/kpi-action-items/${id}/ping/`),
  );

// Historia ponagleń pod zadaniem — kto, kiedy, do kogo.
export const getKpiItemPings = (id: string) =>
  unwrap<KpiItemPing[]>(apiClient.get(`${BASE}/kpi-action-items/${id}/pings/`));

// Persist drag-drop urgency order within ONE level of the tree (ids = top→bottom).
export const reorderKpiActionItems = (connection: string, ids: string[]) =>
  unwrap<{ reordered: number }>(apiClient.post(`${BASE}/kpi-action-items/reorder/`, { connection, ids }));

// Drag-drop in the tree: drop on a task → become its child (`parent`); drop on a week
// header → move the week (`due_date`). Omitted keys are left alone.
export const reparentKpiActionItem = (
  id: string,
  payload: { parent?: string | null; due_date?: string | null },
) => unwrap<KpiActionItem>(apiClient.post(`${BASE}/kpi-action-items/${id}/reparent/`, payload));

// One click after closing a KPI: re-date every overdue task to the current week.
export const carryOverKpiActionItems = (connection: string, week?: string) =>
  unwrap<{ moved: number }>(
    apiClient.post(`${BASE}/kpi-action-items/carry-over/`, week ? { connection, week } : { connection }),
  );

// Per-user collapse state of the month/week groups in a project's KPI tree.
export const getKpiGroupStates = (connection: string) =>
  unwrap<{ collapsed: string[] }>(
    apiClient.get(`${BASE}/kpi-action-items/group-states/`, { params: { connection } }),
  );

export const setKpiGroupCollapsed = (connection: string, group_key: string, is_collapsed: boolean) =>
  unwrap<{ group_key: string; is_collapsed: boolean }>(
    apiClient.post(`${BASE}/kpi-action-items/group-states/`, { connection, group_key, is_collapsed }),
  );

// Dyskusja pod POJEDYNCZYM zadaniem KPI (kształt odpowiedzi identyczny jak komentarzy
// pod przeglądem — backend dziedziczy serializer, więc front używa tego samego typu).
export const getKpiItemComments = (item: string) =>
  unwrap<KpiComment[]>(apiClient.get(`${BASE}/kpi-item-comments/`, { params: { item, page_size: 500 } }));

export const createKpiItemComment = (item: string, content: string, mentioned_users: string[] = []) =>
  unwrap<KpiComment>(apiClient.post(`${BASE}/kpi-item-comments/`, { item, content, mentioned_users }));

export const updateKpiItemComment = (id: string, content: string, mentioned_users: string[] = []) =>
  unwrap<KpiComment>(apiClient.patch(`${BASE}/kpi-item-comments/${id}/`, { content, mentioned_users }));

export const deleteKpiItemComment = (id: string) => apiClient.delete(`${BASE}/kpi-item-comments/${id}/`);

// KPI discussion thread (notes/comments under a review).
export const getKpiComments = (review: string) =>
  unwrap<KpiComment[]>(apiClient.get(`${BASE}/kpi-comments/`, { params: { review, page_size: 500 } }));

export const createKpiComment = (review: string, content: string, mentioned_users: string[] = []) =>
  unwrap<KpiComment>(apiClient.post(`${BASE}/kpi-comments/`, { review, content, mentioned_users }));

export const updateKpiComment = (id: string, content: string, mentioned_users: string[] = []) =>
  unwrap<KpiComment>(apiClient.patch(`${BASE}/kpi-comments/${id}/`, { content, mentioned_users }));

export const deleteKpiComment = (id: string) => apiClient.delete(`${BASE}/kpi-comments/${id}/`);

// ── metadata presets (per user) ──────────────────────────────────────────────
// ── słownik projektantów uprawnionych ────────────────────────────────────────
export const getDesigners = () =>
  unwrap<Designer[]>(apiClient.get(`${BASE}/designers/`, { params: { page_size: 1000 } }));
export const createDesigner = (payload: DesignerInput) =>
  unwrap<Designer>(apiClient.post(`${BASE}/designers/`, payload));
export const updateDesigner = (id: string, payload: Partial<DesignerInput>) =>
  unwrap<Designer>(apiClient.patch(`${BASE}/designers/${id}/`, payload));
export const deleteDesigner = (id: string) => apiClient.delete(`${BASE}/designers/${id}/`);
// Podpis (PNG) wgrywany osobno — model tworzy się bez pliku, potem przypina się plik.
export const uploadDesignerSignature = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<Designer>(apiClient.post(`${BASE}/designers/${id}/upload-signature/`, fd, MULTIPART));
};

export const getPresets = () =>
  unwrap<MetadataPreset[]>(apiClient.get(`${BASE}/metadata-presets/`));
export const createPreset = (name: string, columns: string[]) =>
  unwrap<MetadataPreset>(apiClient.post(`${BASE}/metadata-presets/`, { name, columns }));
export const updatePreset = (id: string, name: string, columns: string[]) =>
  unwrap<MetadataPreset>(apiClient.patch(`${BASE}/metadata-presets/${id}/`, { name, columns }));
export const deletePreset = (id: string) => apiClient.delete(`${BASE}/metadata-presets/${id}/`);

// ── książka telefoniczna (kontakty z projektów ZE) ─────────────────────────────
export const getProjectContacts = (search?: string) =>
  unwrap<ProjectContact[]>(
    apiClient.get(`${BASE}/contacts/`, { params: { page_size: 1000, ...(search ? { search } : {}) } }),
  );
export const createProjectContact = (payload: ProjectContactInput) =>
  unwrap<ProjectContact>(apiClient.post(`${BASE}/contacts/`, payload));
export const updateProjectContact = (id: string, payload: Partial<ProjectContactInput>) =>
  unwrap<ProjectContact>(apiClient.patch(`${BASE}/contacts/${id}/`, payload));
export const deleteProjectContact = (id: string) => apiClient.delete(`${BASE}/contacts/${id}/`);
export const getProjectContactProjects = (id: string) =>
  unwrap<{ projects: ProjectContactProject[] }>(apiClient.get(`${BASE}/contacts/${id}/projects/`));

// ── offices ──────────────────────────────────────────────────────────────────
export const getOffices = () => unwrap<Office[]>(apiClient.get(`/offices/`, { params: { page_size: 1000 } }));
export const createOffice = (payload: Partial<Office>) =>
  unwrap<Office>(apiClient.post(`/offices/`, payload));
// Rejestr jest WSPÓLNY — ta sama poprawka dotyka każdej działki, sprawy i opłaty, która
// wskazuje ten urząd. Backend przepisuje przy okazji migawki nazwy/adresu na właścicielach
// działek i mówi w meta.warnings, ilu dotyczyła.
export const updateOffice = (id: string, payload: Partial<Office>) =>
  unwrapWithWarnings<Office>(apiClient.patch(`/offices/${id}/`, payload));
export const deleteOffice = (id: string) => apiClient.delete(`/offices/${id}/`);

export interface OfficeUsage {
  total: number;
  by_relation: { label: string; model: string; count: number }[];
}

// Gdzie ten wpis jest używany — pokazywane przed poprawką („zmiana obejmie te miejsca")
// i przed usunięciem.
export const getOfficeUsage = (id: string) =>
  unwrap<OfficeUsage>(apiClient.get(`/offices/${id}/usage/`));

export interface OfficeMergeResult {
  merged: number;
  dry_run: boolean;
  log: string[];
  office: Office;
}

// Scal duplikaty w ten wpis: przepina każdą relację i DEZAKTYWUJE duplikaty (nie usuwa).
export const mergeOffices = (id: string, duplicates: string[], dryRun = false) =>
  unwrap<OfficeMergeResult>(
    apiClient.post(`/offices/${id}/merge/`, { duplicates, dry_run: dryRun }),
  );

// ── order links ────────────────────────────────────────────────────────────────
export interface LinkableOrder {
  id: string;
  order_number: string;
  order_type: string;
  order_type_display?: string;
  status: string;
  contractor_name?: string;
  project_name?: string;
}

// Orders that can be linked to a działka — used for the WPW (terenowe/kompleksowe) picker.
export const getLinkableOrders = (params: Record<string, string>) =>
  unwrap<LinkableOrder[]>(apiClient.get(`/orders/`, { params: { page_size: 200, ...params } }));

export const linkOrderParcel = (order: string, parcel: string, scope_note = "") =>
  unwrap(apiClient.post(`${BASE}/order-parcel-links/`, { order, parcel, scope_note }));
export const unlinkOrderParcel = (id: string) => apiClient.delete(`${BASE}/order-parcel-links/${id}/`);

// ── WPW handover (działka | WPW) — negotiator-declared reality per (order × parcel) ──
// `params` is one of { order } | { connection } | { parcel }. Action endpoints return the
// full updated handover, so every mutation resolves to a fresh WpwHandover.
export const getWpwHandovers = (params: Record<string, string>) =>
  unwrap<WpwHandover[]>(apiClient.get(`${BASE}/wpw-handovers/`, { params }));

// Project parcels never sent into the field for WPW (no owner has a handover). Connection-scoped.
export const getWpwNeverSentParcels = (connectionId: string) =>
  unwrap<WpwNeverSentParcel[]>(
    apiClient.get(`${BASE}/wpw-handovers/never-sent/`, { params: { connection: connectionId } }),
  );

// Per-tab "needs attention" counts for the workspace badges (+ this user's manager/mute state).
export const getConnectionAttention = (connectionId: string) =>
  unwrap<ConnectionAttention>(apiClient.get(`${BASE}/connections/${connectionId}/attention/`));

// Toggle this user's mute of a project's badges (nav aggregate + portfolio).
export const setConnectionBadgeMute = (connectionId: string, muted: boolean) =>
  unwrap<{ is_muted: boolean }>(apiClient.post(`${BASE}/connections/${connectionId}/badge-mute/`, { muted }));

// Sidebar aggregate: how many (non-muted) projects need attention for this user.
export const getPowerProjectsAttentionAggregate = () =>
  unwrap<{ projects: number }>(apiClient.get(`${BASE}/connections/attention-aggregate/`));

// The two acquisition statuses require a document — pass it as `file` (multipart). Always
// sent as FormData (the backend's declared-status endpoint accepts multipart).
export const setWpwDeclaredStatus = (
  id: string,
  status: string,
  opts?: { note?: string; file?: File | null },
) => {
  const fd = new FormData();
  fd.append("status", status);
  if (opts?.note) fd.append("note", opts.note);
  if (opts?.file) fd.append("files", opts.file);
  return unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/declared-status/`, fd, MULTIPART));
};

export const addWpwComment = (id: string, body: string) =>
  unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/comments/`, { body }));

// docKind="porozumienie" otwiera per-plikową decyzję GK (review_status=pending) i chip
// „porozumienie do zatwierdzenia" na działce.
export const addWpwFiles = (id: string, files: File[], docKind: "other" | "porozumienie" = "other") => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  fd.append("doc_kind", docKind);
  return unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/files/`, fd, MULTIPART));
};

// WPW „nie ma w żadnym zamówieniu": koordynator sam wgrywa zgodę (uzgodnienie → tytuł
// „Pozyskany") albo odmowę (tytuł „Odmowa") — bez akceptacji.
export const recordWpwManualOutcome = (
  id: string,
  v: { outcome: "consent" | "refusal"; files: File[]; note?: string },
) => {
  const fd = new FormData();
  for (const f of v.files) fd.append("files", f);
  fd.append("outcome", v.outcome);
  if (v.note) fd.append("note", v.note);
  return unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/manual-outcome/`, fd, MULTIPART));
};

// Decyzja GK o pojedynczym porozumieniu (niezależna od zatwierdzenia statusu WPW).
// Dostępna też z widoku działki w projekcie.
export const reviewWpwFile = (
  id: string,
  fileId: string,
  decision: "approved" | "rejected",
  note = "",
) =>
  unwrap<WpwHandover>(
    apiClient.post(`${BASE}/wpw-handovers/${id}/files/${fileId}/review/`, { decision, note }),
  );

// Usunięcie pliku negocjatora: w historii zostaje sam wpis (nazwa, kto, kiedy) — binarka
// NIE jest przechowywana. Negocjator może usuwać do zatwierdzenia pliku przez GK; GK zawsze.
export const removeWpwFile = (id: string, fileId: string) =>
  unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/files/${fileId}/remove/`, {}));

// GK odpina nieaktualny plik pakietu (gk_docs) od WPW — np. skompilowany pakiet sprzed
// aktualizacji, żeby negocjator nie zebrał podpisów na złym druku. Wpis zostaje w osi czasu.
export const removeWpwGkDoc = (id: string, fileId: string) =>
  unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/gk-docs/${fileId}/remove/`, {}));

// GK→wykonawca: aktualizacja dokumentów wejściowych GK dla tego WPW (np. po zmianie trasy).
// Powiadamia podwykonawcę i zapala u niego badge „Zaktualizowana".
export const updateWpwGkDocs = (id: string, files: File[], note = "") => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  if (note) fd.append("note", note);
  return unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/update-gk-docs/`, fd, MULTIPART));
};

// Podwykonawca oznacza dokumenty GK jako przejrzane → gasi badge „Zaktualizowana".
export const markWpwGkDocsSeen = (id: string) =>
  unwrap<WpwHandover>(apiClient.post(`${BASE}/wpw-handovers/${id}/mark-gk-docs-seen/`, {}));

export const confirmWpwEntry = (
  id: string,
  targetEvent: string | null,
  decision: string,
  note = "",
) =>
  unwrap<WpwHandover>(
    apiClient.post(`${BASE}/wpw-handovers/${id}/confirm/`, { target_event: targetEvent, decision, note }),
  );

// GK undoes its prior decision ("cofnij zatwierdzenie") — reverts the formal promotion.
export const unconfirmWpwEntry = (id: string, targetEvent: string | null) =>
  unwrap<WpwHandover>(
    apiClient.post(`${BASE}/wpw-handovers/${id}/unconfirm/`, { target_event: targetEvent }),
  );

// „Pobierz ZIP" — packs every consent file the negotiator uploaded for a WPW order into one
// archive in the background. Returns a bg-task id; poll it and download `result_file` on success.
export const startWpwHandoverZip = (orderId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/wpw-handovers/download-zip/`, { order: orderId }),
  );

// „Pakiety do druku (ZIP)" — pakiety „Od GK" (gk_docs) wszystkich WPW zamówienia, folder na
// właściciela; pakiety nieaktualne backend pomija. `order` w query: tylko po nim bramka
// wpuszcza zamawiającego bez DLC_01 (odczyt, POST bo startuje zadanie w tle).
export const startWpwPackagesZip = (orderId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/wpw-handovers/download-packages-zip/`, null, {
      params: { order: orderId },
    }),
  );

// „Analiza AI" — GK ELEKTIM zleca przeczytanie przez AI dokumentów wgranych przez negocjatora
// (jeden właściciel albo całe zamówienie). Zwraca id zadania w tle; uwagi lądują w polu
// `ai_analysis` danego WPW i są widoczne dla obu stron.
export const startWpwAgreementAi = (handoverId: string) =>
  unwrap<{ task_id: string; handovers: number }>(
    apiClient.post(`${BASE}/wpw-handovers/${handoverId}/ai-analyze/`, {}),
  );

export const startWpwOrderAgreementAi = (orderId: string) =>
  unwrap<{ task_id: string; handovers: number }>(
    apiClient.post(`${BASE}/wpw-handovers/ai-analyze/`, { order: orderId }),
  );

// ── WPW requisition (Zapotrzebowanie na pozyskanie zgód prywatnych) ───────────────
export interface ContractorOption {
  id: string; name: string; short_name?: string; is_active: boolean;
  /** Typy prac z kartoteki (wpw / surveyor / surveyor_wpw / coordinator / design_reviewer). */
  work_types?: string[];
  /** Najbliższy urlop firmy w oknie składania zamówienia (dziś + 7 dni) — picker spycha ją na dół z dopiskiem. */
  on_leave?: OnLeaveBrief | null;
}
export interface AgreementLineItemType {
  id: string; name: string; unit: string; base_rate: string | null; attachment_required: boolean;
  is_active?: boolean;
  // Pozycja automatyczna (dopisywana sama przy pozycji wyzwalającej) — patrz AutoLineItemRow.
  is_auto?: boolean;
  auto_triggers?: string[];
  auto_trigger_names?: string[];
  auto_trigger_name?: string | null;
}
/** Wiersz z GET /orders/auto-line-items/ — pozycja automatyczna + werdykt dla projektu. */
export type AutoLineItemPreview = import("@/features/orders/components/AutoLineItemRow").AutoLineItemInfo;
export const getAutoLineItemsPreview = (contractorId: string, scope: { connection?: string; project?: string }) =>
  unwrap<AutoLineItemPreview[]>(
    apiClient.get("/orders/auto-line-items/", { params: { contractor: contractorId, ...scope } }),
  );
export interface ContractorAgreement { id: string; title: string; line_item_types: AgreementLineItemType[]; }

// `eligibleFor` zawęża listę do kręgu rodzaju zlecenia ("wpw" = typ „Negocjator WPW" ALBO
// aktywna pozycja WPW w cenniku umowy ramowej — ten sam krąg, co podpowiedź podwykonawcy);
// bez niego wracają wszystkie aktywne firmy.
export const getActiveContractors = (eligibleFor?: "wpw") =>
  unwrap<ContractorOption[]>(
    apiClient.get(`/contractors/`, {
      params: { is_active: "true", ...(eligibleFor ? { eligible_for: eligibleFor } : {}) },
    }),
  );

export const getContractorAgreements = (contractorId: string) =>
  unwrap<ContractorAgreement[]>(apiClient.get(`/contractors/${contractorId}/agreements/`));

// Raise a Requisition for private-owner consents (WPW) on the selected parcels (multipart:
// supports file attachments). Backend gates on the project's DLC permission.
export const createWpwRequisition = (connectionId: string, fd: FormData) =>
  unwrap<{ id: string; order_id: string | null }>(
    apiClient.post(`${BASE}/connections/${connectionId}/create-wpw-requisition/`, fd, MULTIPART),
  );

// ── design stage: Projekt techniczny ─────────────────────────────────────────────
// Every action endpoint returns the full updated aggregate (TechnicalProject for stage-level
// actions, TechnicalDeliverable for per-deliverable ones); mutations invalidate the stage query.
const TP = `${BASE}/technical-project`;
const DEL = `${BASE}/technical-deliverables`;
const CE = `${BASE}/cost-estimate`;

export const getTechnicalProject = (connection: string) =>
  unwrap<TechnicalProject>(apiClient.get(`${TP}/by-connection/`, { params: { connection } }));

// Subcontractor entry point from their Order page (analogous to the WPW bridge).
export const getTechnicalProjectByOrder = (order: string) =>
  unwrap<TechnicalProject>(apiClient.get(`${TP}/by-order/`, { params: { order } }));

export const assignTechnicalEmployee = (id: string, employee: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/assign/`, { employee }));

export const tpSendConcept = (id: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/send-concept/`, {}));
export const tpAcceptConcept = (id: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/accept-concept/`, {}));
export const tpRequestConceptChange = (id: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/request-concept-change/`, {}));
export const tpFinalizeRoute = (id: string, route_version?: string | null) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/finalize-route/`, { route_version: route_version ?? null }));
// GK pulls back the final route acceptance — Faza B opracowania freeze (not hidden) until re-finalised.
export const tpRevertRoute = (id: string, reason?: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/revert-route/`, { reason: reason ?? "" }));
export const tpSubmitBundle = (id: string, target: DeliverableTarget) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/submit-bundle/`, { target }));
export const tpAcceptBundle = (id: string, target: DeliverableTarget) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/accept-bundle/`, { target }));
export const tpRejectBundle = (id: string, target: DeliverableTarget, note = "") =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/reject-bundle/`, { target, note }));
export const tpReceiveZe = (id: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/receive-ze/`, {}));
export const tpSettle = (id: string, note = "") =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/settle/`, { note }));
export const tpAddDeliverable = (id: string, label: string, target?: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/add-deliverable/`, { label, target }));
export const tpReorderDeliverables = (id: string, keys: string[]) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/reorder-deliverables/`, { keys }));

export const setDeliverableState = (id: string, state: string, note = "") =>
  unwrap<TechnicalDeliverable>(apiClient.post(`${DEL}/${id}/set-state/`, { state, note }));
export const acceptDeliverable = (id: string, note = "") =>
  unwrap<TechnicalDeliverable>(apiClient.post(`${DEL}/${id}/accept/`, { note }));
export const requestDeliverableCorrection = (id: string, note = "") =>
  unwrap<TechnicalDeliverable>(apiClient.post(`${DEL}/${id}/request-correction/`, { note }));
export const addDeliverableComment = (id: string, body: string) =>
  unwrap<TechnicalDeliverable>(apiClient.post(`${DEL}/${id}/comments/`, { body }));
export const addDeliverableFiles = (id: string, files: File[]) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return unwrap<TechnicalDeliverable>(apiClient.post(`${DEL}/${id}/files/`, fd, MULTIPART));
};
export const replaceDeliverableFile = (id: string, attachment: string, file: File) => {
  const fd = new FormData();
  fd.append("attachment", attachment);
  fd.append("files", file);
  return unwrap<TechnicalDeliverable>(apiClient.post(`${DEL}/${id}/files/replace/`, fd, MULTIPART));
};
export const removeDeliverableFile = (id: string, attachment: string) =>
  unwrap<TechnicalDeliverable>(apiClient.post(`${DEL}/${id}/files/${attachment}/remove/`));
export const deleteDeliverable = (id: string) =>
  apiClient.post(`${DEL}/${id}/delete-custom/`);

// ── design stage: Kosztorys ───────────────────────────────────────────────────────
export const getCostEstimate = (connection: string) =>
  unwrap<CostEstimate>(apiClient.get(`${CE}/by-connection/`, { params: { connection } }));

export const getCostEstimateByOrder = (order: string) =>
  unwrap<CostEstimate>(apiClient.get(`${CE}/by-order/`, { params: { order } }));

export const assignCostEmployee = (id: string, employee: string) =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/assign/`, { employee }));
export const setCostState = (id: string, state: string, note = "") =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/set-state/`, { state, note }));
export const acceptCostGk = (id: string, note = "") =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/accept-gk/`, { note }));
export const requestCostCorrection = (id: string, note = "") =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/request-correction/`, { note }));
export const settleCost = (id: string, note = "") =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/settle/`, { note }));
export const addCostComment = (id: string, body: string) =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/comments/`, { body }));
export const addCostFiles = (id: string, files: File[]) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/files/`, fd, MULTIPART));
};
export const replaceCostFile = (id: string, attachment: string, file: File) => {
  const fd = new FormData();
  fd.append("attachment", attachment);
  fd.append("files", file);
  return unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/files/replace/`, fd, MULTIPART));
};
export const removeCostFile = (id: string, attachment: string) =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/files/${attachment}/remove/`));

// Create a subcontractor order for a design stage directly from the workspace: the
// requisition is approved into an Order in one go and auto-links here. Returns the fresh stage.
export const createTechnicalRequisition = (id: string, fd: FormData) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/create-requisition/`, fd, MULTIPART));
export const createCostRequisition = (id: string, fd: FormData) =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/create-requisition/`, fd, MULTIPART));

// Druga droga wyłonienia wykonawcy etapu: postępowanie ofertowe. Zakłada SZKIC ogłoszenia
// powiązanego z etapem — publikuje i rozstrzyga się je już w module Przetargi, a zwycięzcę
// zleca z powrotem tutaj (create*Requisition z polem `proceeding`), żeby zamówienie przeszło
// przez te same automaty co zlecenie wprost.
export const createTechnicalProceeding = (id: string, payload: StageProceedingInput) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/create-proceeding/`, payload));
export const releaseTechnicalProceeding = (id: string) =>
  unwrap<TechnicalProject>(apiClient.post(`${TP}/${id}/release-proceeding/`));
export const createCostProceeding = (id: string, payload: StageProceedingInput) =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/create-proceeding/`, payload));
export const releaseCostProceeding = (id: string) =>
  unwrap<CostEstimate>(apiClient.post(`${CE}/${id}/release-proceeding/`));

// ── DLC_02 — firmowy dziennik korespondencji (CorrespondenceRegister) ─────────────
export const getCorrespondenceRegister = (params?: Record<string, string>) =>
  unwrap<CorrespondenceRegister[]>(
    apiClient.get(`/correspondence-register/`, { params: { page_size: 1000, ...params } }),
  );

// Badge counts: items awaiting my acknowledgment, items awaiting dekretacja, and my own
// letters still sitting in the dispatch queue (my_dispatch — self-scoped, every DLC_02 user).
export const getRegisterCounts = () =>
  unwrap<{
    to_acknowledge: number;
    to_file: number;
    to_send: number;
    returned: number;
    my_dispatch: number;
  }>(apiClient.get(`/correspondence-register/counts/`));

// Lekki słownik przedsięwzięć dla formularzy dziennika — ten sam zbiór co portfolio ZE,
// ale dostępny dla każdego użytkownika DLC_02 (portfolio wymaga modułu DLC_01).
export const getRegisterProjectOptions = () =>
  unwrap<RegisterProjectOption[]>(apiClient.get(`/correspondence-register/project-options/`));

// Podpowiedzi do filtra „Korespondent" — nazwy występujące w dzienniku, bez powtórzeń.
// Zakres ten sam co lista, więc na „Mojej korespondencji" trzeba podać { mine: "true" }.
export const getRegisterCounterpartyOptions = (params?: Record<string, string>) =>
  unwrap<string[]>(
    apiClient.get(`/correspondence-register/counterparty-options/`, { params }),
  );

// Manual intake of an OUTGOING item (default create; normally fed by DLC_01).
export const createOutgoingRegister = (fd: FormData) =>
  unwrap<CorrespondenceRegister>(apiClient.post(`/correspondence-register/`, fd, MULTIPART));

// Rejestracja wpływu — register an INCOMING item (optionally routed to person/project).
export const registerIncoming = (fd: FormData) =>
  unwrap<CorrespondenceRegister>(apiClient.post(`/correspondence-register/incoming/`, fd, MULTIPART));

// Edycja niezadekretowanego wpływu — PATCH the full form state of an INCOMING entry still
// awaiting dekretacja. New scans go as repeated `files`, removals as repeated `remove_files`.
export const updateIncomingRegister = (id: string, fd: FormData) =>
  unwrap<CorrespondenceRegister>(apiClient.patch(`/correspondence-register/${id}/`, fd, MULTIPART));

// Podział wpływu na pozycje — replace a RECEIVED incoming entry with 2+ new register entries,
// distributing its attachments. The multipart-only viewset takes `parts` as one JSON field.
export const splitIncomingRegister = (
  id: string,
  parts: { files: string[]; target_users: string[]; target_project?: string | null; note?: string }[],
) => {
  const fd = new FormData();
  fd.append("parts", JSON.stringify(parts));
  return unwrap<CorrespondenceRegister[]>(
    apiClient.post(`/correspondence-register/${id}/split/`, fd, MULTIPART),
  );
};

// Advance the dispatch lifecycle (paper queue). FormData may carry a confirmation_file.
export const transitionRegister = (id: string, fd: FormData) =>
  unwrap<CorrespondenceRegister>(apiClient.post(`/correspondence-register/${id}/transition/`, fd, MULTIPART));

// Nr listu dopisany do pisma, które JUŻ wyszło (wysłane / doręczone / zwrot / niedoręczalne).
// Numer nadania schodzi z potwierdzenia z poczty, więc rzadko jest pod ręką w chwili zmiany
// statusu; wymaga uprawnienia „Firmowy obieg dokumentów" (ten sam krąg, który pisma wysyła).
export const setRegisterTrackingNumber = (id: string, trackingNumber: string) => {
  const fd = new FormData();
  fd.append("tracking_number", trackingNumber);
  return unwrap<CorrespondenceRegister>(
    apiClient.post(`/correspondence-register/${id}/tracking-number/`, fd, MULTIPART),
  );
};

// Dekretacja — route an incoming entry to a person/project and mark it FILED.
// The register viewset only parses multipart/form-data, so post FormData (not JSON);
// omit empty fields so they never arrive as the literal string "null".
export const fileIncomingRegister = (
  id: string,
  payload: { target_users?: string[]; target_project?: string | null; note?: string },
) => {
  const fd = new FormData();
  // Repeat target_users once per selected person — the DRF ListField reads them via getlist.
  (payload.target_users ?? []).forEach((uid) => fd.append("target_users", uid));
  if (payload.target_project) fd.append("target_project", payload.target_project);
  if (payload.note) fd.append("note", payload.note);
  return unwrap<CorrespondenceRegister>(apiClient.post(`/correspondence-register/${id}/file/`, fd, MULTIPART));
};

// Wycofanie przekazania pisma firmie prowadzącej projekt — wiersz znika z jej rejestru spraw,
// w dzienniku projektu zostaje ślad z powodem.
export const withdrawRegisterFromContractor = (id: string, reason: string) => {
  const fd = new FormData();
  if (reason) fd.append("reason", reason);
  return unwrap<CorrespondenceRegister>(
    apiClient.post(`/correspondence-register/${id}/withdraw-from-contractor/`, fd, MULTIPART),
  );
};

// Podpięcie projektu — autor wpisu albo adresat dekretacji (dział obiegu: dowolny wpis)
// przypina wpływ do przedsięwzięcia ZE; status wpisu bez zmian. Po dekretacji skany trafiają
// do poufnego folderu KORESPONDENCJA w plikach projektu.
export const assignRegisterProject = (id: string, projectId: string) => {
  const fd = new FormData();
  fd.append("target_project", projectId);
  return unwrap<CorrespondenceRegister>(
    apiClient.post(`/correspondence-register/${id}/assign-project/`, fd, MULTIPART),
  );
};

// Odpięcie projektu — WYŁĄCZNIE adresat dekretacji albo dyrektor; lustrzane pliki wpisu
// znikają z zakładki Pliki przedsięwzięcia (także z folderów, do których je przeniesiono).
export const unassignRegisterProject = (id: string) =>
  unwrap<CorrespondenceRegister>(
    apiClient.post(`/correspondence-register/${id}/unassign-project/`, new FormData(), MULTIPART),
  );

// Dodanie / usunięcie adresata po dekretacji. Dodaje obecny adresat albo dyrektor / dział
// obiegu; usuwa wyłącznie dyrektor / dział obiegu (backend re-enforces both).
const recipientsForm = (userIds: string[]) => {
  const fd = new FormData();
  userIds.forEach((uid) => fd.append("target_users", uid));
  return fd;
};

export const addRegisterRecipients = (id: string, userIds: string[]) =>
  unwrap<CorrespondenceRegister>(
    apiClient.post(`/correspondence-register/${id}/add-recipients/`, recipientsForm(userIds), MULTIPART),
  );

export const removeRegisterRecipients = (id: string, userIds: string[]) =>
  unwrap<CorrespondenceRegister>(
    apiClient.post(`/correspondence-register/${id}/remove-recipients/`, recipientsForm(userIds), MULTIPART),
  );

// Anulowanie własnego pisma w kolejce wysyłki — AUTOR wpisu wychodzącego wycofuje pismo,
// dopóki dział obiegu go nie wysłał („W kolejce" / „Przygotowane"). Wpis zostaje w dzienniku
// jako „Anulowane" (z historią), znika tylko z kolejki „Do wysłania". `note` = powód.
export const cancelOutgoingRegister = (id: string, note?: string) => {
  const fd = new FormData();
  if (note) fd.append("note", note);
  return unwrap<CorrespondenceRegister>(
    apiClient.post(`/correspondence-register/${id}/cancel/`, fd, MULTIPART),
  );
};

// Potwierdzenie odbioru — the dekretacja target confirms receipt (FILED → ACKNOWLEDGED).
// Empty FormData keeps the request multipart so the viewset's parser accepts it (no body needed).
export const acknowledgeRegister = (id: string) =>
  unwrap<CorrespondenceRegister>(apiClient.post(`/correspondence-register/${id}/acknowledge/`, new FormData(), MULTIPART));

// Pobierz wybrane załączniki (z dowolnych wpisów) jako jeden ZIP. Uwierzytelniony widok →
// blob + zapis po stronie klienta. Pliki wysyłamy jako powtórzone części `file_ids` (getlist).
export const downloadRegisterZip = async (fileIds: string[]) => {
  const fd = new FormData();
  fileIds.forEach((id) => fd.append("file_ids", id));
  // MULTIPART is required: axios 1.x serialises FormData as JSON when the instance's
  // default `Content-Type: application/json` is left in place, so the multipart-only
  // viewset never sees `file_ids` → 400 → "Nie udało się pobrać plików." (same reason
  // every other FormData POST here passes MULTIPART explicitly).
  const resp = await apiClient.post(`/correspondence-register/download-zip/`, fd, {
    ...MULTIPART,
    responseType: "blob",
  });
  saveBlobResponse(resp, "korespondencja_pliki.zip");
};

// ── wypisy (land registry excerpts) ─────────────────────────────────────────
export const getWypisy = (connectionId: string) =>
  unwrap<WypisFile[]>(apiClient.get(`${BASE}/parcel-wypisy/?connection=${connectionId}`));

export const uploadWypisFile = (connectionId: string, file: File, parcelIds: string[], note: string) => {
  const fd = new FormData();
  fd.append("connection", connectionId);
  fd.append("file", file);
  parcelIds.forEach((id) => fd.append("parcel_ids", id));
  if (note) fd.append("note", note);
  return unwrap<WypisFile>(apiClient.post(`${BASE}/parcel-wypisy/`, fd, MULTIPART));
};

export const setWypisPageMap = (id: string, pageMap: WypisPageEntry[], note?: string) =>
  unwrap<WypisFile>(
    apiClient.patch(`${BASE}/parcel-wypisy/${id}/page-map/`, {
      page_map: pageMap,
      ...(note !== undefined ? { note } : {}),
    }),
  );

export const deleteWypisFile = (id: string) =>
  unwrap<{ deleted: boolean }>(apiClient.delete(`${BASE}/parcel-wypisy/${id}/`));

// Odpięcie wypisu od JEDNEJ działki (odwrotność analizy AI): plik zostaje w projekcie,
// a przy removeOwners znikają właściciele wpisani z tego wypisu — po to, żeby działkę
// dało się zamówić w wypisach jeszcze raz.
export const detachWypisParcel = (id: string, parcelId: string, removeOwners: boolean) =>
  unwrap<WypisFile & { detach_summary: WypisDetachSummary }>(
    apiClient.post(`${BASE}/parcel-wypisy/${id}/detach-parcel/`, {
      parcel_id: parcelId,
      remove_owners: removeOwners,
    }),
  );

// AI: analiza pojedynczego wypisu (bez zamówienia) — ta sama ekstrakcja co przy
// zamówieniu wypisów. Zwraca task_id; propozycja przychodzi w wyniku zadania.
export const extractWypisFile = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/parcel-wypisy/${id}/extract/`, {}));

export const applyWypisFileExtraction = (id: string, parcels: WypisProposalParcel[]) =>
  unwrap<WypisFile>(apiClient.post(`${BASE}/parcel-wypisy/${id}/apply/`, { parcels }));

// ── księgi wieczyste (KW) — analiza AI ────────────────────────────────────────
export const getParcelKwFiles = (parcelId: string) =>
  unwrap<ParcelKwFile[]>(apiClient.get(`${BASE}/parcel-kw/?parcel=${parcelId}`));

// Scalony PDF wszystkich działów (kolejne strony) — serwer buduje na żądanie.
export const getParcelKwMergedPdf = (id: string) =>
  apiClient.get<Blob>(`${BASE}/parcel-kw/${id}/sections-pdf/`, { responseType: "blob" });

// Zakładka „KW" projektu: status ksiąg wszystkich działek z numerem KW.
export const getKwOverview = (connectionId: string) =>
  unwrap<KwOverview>(apiClient.get(`${BASE}/parcel-kw/kw-overview/?connection=${connectionId}`));

// Tryb globalny (strona Działki): te same wiersze dla filtrów strony.
export const getKwOverviewGlobal = (filters: ParcelExportFilters) =>
  unwrap<KwOverview>(apiClient.post(`${BASE}/parcel-kw/kw-overview/`, filters));

// „Resetuj dane z KW": usuwa WSZYSTKIE dane księgi działki (pliki, treści, analizy).
export const resetParcelKw = (parcelId: string) =>
  unwrap<{ deleted: number }>(apiClient.post(`${BASE}/parcel-kw/reset/`, { parcel: parcelId }));

export const uploadParcelKw = (parcelId: string, file: File) => {
  const fd = new FormData();
  fd.append("parcel", parcelId);
  fd.append("file", file);
  return unwrap<ParcelKwFile & { task_id: string | null }>(
    apiClient.post(`${BASE}/parcel-kw/`, fd, MULTIPART),
  );
};

export const analyzeParcelKw = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/parcel-kw/${id}/analyze/`, {}));

export const applyKwOwners = (id: string, owners: KwOwnerRow[]) =>
  unwrap<ParcelKwFile & { owners_created: number }>(
    apiClient.post(`${BASE}/parcel-kw/${id}/apply-owners/`, { owners }),
  );

export const deleteParcelKw = (id: string) =>
  unwrap<{ deleted: boolean }>(apiClient.delete(`${BASE}/parcel-kw/${id}/`));

// Rozbieżność nazw właściciela („GMINA WAGANIEC" ↔ „Urząd Gminy Waganiec") uznana
// za w porządku — od tej chwili nie liczy się jako różnica w analizie KW.
export const acceptKwOwnerDiff = (
  id: string, v: { kwName?: string; dbName?: string; note?: string },
) =>
  unwrap<ParcelKwFile>(
    apiClient.post(`${BASE}/parcel-kw/${id}/accept-diff/`, {
      kw_name: v.kwName ?? "", db_name: v.dbName ?? "", note: v.note ?? "",
    }),
  );

export const unacceptKwOwnerDiff = (id: string, v: { kwName?: string; dbName?: string }) =>
  unwrap<ParcelKwFile>(
    apiClient.post(`${BASE}/parcel-kw/${id}/unaccept-diff/`, {
      kw_name: v.kwName ?? "", db_name: v.dbName ?? "",
    }),
  );

// Automatyczne pobranie KW z EKW (jeśli włączone na serwerze) → zapis + analiza.
export const fetchKwFromEkw = (parcelId: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/parcel-kw/fetch-ekw/`, { parcel: parcelId }));

// Treść KW wklejona przez użytkownika (Ctrl+A / Ctrl+C na stronie EKW) → analiza AI.
export const pasteKwContent = (
  parcelId: string, text: string, kwNumber?: string, sections?: Record<string, string>,
) =>
  unwrap<ParcelKwFile>(
    apiClient.post(`${BASE}/parcel-kw/from-extension/`, {
      parcel: parcelId, text, kw_number: kwNumber, sections,
    }),
  );

// ── wnioski urzędowe (silnik pakietów) ────────────────────────────────────────
export const getApplicationRegistry = () =>
  unwrap<ApplicationTypeConfig[]>(apiClient.get(`${BASE}/official-applications/registry/`));

export const getApplicationsOverview = (connectionId: string) =>
  unwrap<ApplicationsOverview>(
    apiClient.get(`${BASE}/official-applications/overview/`, { params: { connection: connectionId } }),
  );

export const getApplications = (params: Record<string, string> = {}) =>
  unwrap<OfficialApplicationRow[]>(
    apiClient.get(`${BASE}/official-applications/`, { params }),
  );

export const getApplication = (id: string) =>
  unwrap<OfficialApplicationDetail>(apiClient.get(`${BASE}/official-applications/${id}/`));

// Kolejka „Do akceptacji dyrektora" ze wszystkich projektów (najdłużej czekające
// pierwsze) — tylko wnioski, które zalogowany może zatwierdzić.
export const getApplicationApprovalQueue = () =>
  unwrap<OfficialApplicationQueueRow[]>(
    apiClient.get(`${BASE}/official-applications/approval-queue/`),
  );

export const createApplication = (payload: {
  application_type: string;
  connection: string;
  // Wniosek uniwersalny (typ z `template_pick`): szablon pisma rodzaju „Uniwersalny" —
  // dla takich typów WYMAGANY, bo to on jest treścią wniosku.
  doc_template?: string;
  recipient_office?: string;
  decision?: string;
  wpw_parcel?: string;
  wpw_owner?: string;
  commune?: string;
  county?: string;
  parcel_ids?: string[];
  // Odrolnienie: obręb, którego dotyczy wniosek (1 obręb = 1 wniosek).
  obreb?: string;
  // Świadome założenie kolejnego wniosku mimo reguły typu (1 gmina = 1 ULICP itd.) —
  // backend odmawia z kodem „duplicate", a kreator pyta i ponawia z tą flagą.
  confirm_duplicate?: boolean;
  // Podpięcie pod ISTNIEJĄCĄ otwartą sprawę tego projektu (zamiast zakładania nowej przy
  // wysyłce), opcjonalnie pod jej pozycję „wniosek" bez pakietu.
  proceeding?: string;
  proceeding_item?: string;
}) => unwrap<OfficialApplicationDetail>(apiClient.post(`${BASE}/official-applications/`, payload));

// Podpięcie / przepięcie / odpięcie sprawy do niezłożonego pakietu (null = odepnij);
// złożony da się odpiąć tylko od sprawy automatycznej — z `delete_case` (sprawa znika).
export const linkApplicationProceeding = (
  id: string,
  payload: {
    proceeding: string | null; proceeding_item?: string | null; reopen_case?: boolean;
    delete_case?: boolean;
  },
) => unwrap<OfficialApplicationDetail>(
  apiClient.post(`${BASE}/official-applications/${id}/link-proceeding/`, payload),
);

// Komplet wniosków lokalizacyjnych jednym kliknięciem — po JEDNYM na właściciela
// instytucjonalnego (działka współwłasna = wniosek dla każdego współwłaściciela).
// Idempotentne: kto ma już wniosek, wraca w `skipped`, a nie dostaje drugiego.
export const bulkCreateApplications = (payload: {
  connection: string;
  // puste = wszyscy właściciele projektu, którzy nie mają jeszcze wniosku
  offices?: string[];
}) => unwrap<BulkCreateApplicationsResult>(
  apiClient.post(`${BASE}/official-applications/bulk-create/`, payload),
);

// Formatki dla całego kompletu wniosków: po JEDNYM zestawie na wniosek (własna
// numeracja arkuszy), na szablonie wskazanym przez człowieka — bez wskazania szablon
// dobiera się sam z siatki (operator projektu × kubełek urzędowy).
export const bulkGenerateFormatki = (payload: {
  connection: string;
  // puste = wszystkie aktywne wnioski lokalizacyjne projektu
  applications?: string[];
  template?: string;
  scale_denominator?: number;
  field_values?: Record<string, string>;
  include_orientation?: boolean;
}) => unwrap<BulkGenerateFormatkiResult>(
  apiClient.post(`${BASE}/official-applications/bulk-generate-formatki/`, payload),
);

export const updateApplication = (id: string, payload: Record<string, unknown>) =>
  unwrap<OfficialApplicationDetail>(apiClient.patch(`${BASE}/official-applications/${id}/`, payload));

// `allowUnpaidFees` (faza „poa”): świadome zatwierdzenie, zanim opłata dostanie
// potwierdzenie przelewu — kompilacja i wysyłka i tak na nie czekają.
export const approveApplicationPhase = (id: string, phase: string, allowUnpaidFees = false) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/approve-phase/`, {
      phase,
      ...(allowUnpaidFees ? { allow_unpaid_fees: true } : {}),
    }),
  );

export const reopenApplicationPhase = (id: string, phase: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/reopen-phase/`, { phase }),
  );

export const resolveApplicationPoa = (id: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/resolve-poa/`, {}),
  );

export const getApplicationPoaCandidates = (id: string) =>
  unwrap<ApplicationPoaCandidate[]>(
    apiClient.get(`${BASE}/official-applications/${id}/poa-candidates/`),
  );

export const attachApplicationPoa = (id: string, poaId: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/attach-poa/`, { poa: poaId }),
  );

export const detachApplicationPoa = (id: string, linkId: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/detach-poa/`, { link: linkId }),
  );

// PNG podglądu zakresu (obiekty projektu + działki wniosku na niebiesko, reszta
// na szaro) — render synchroniczny, nadpisuje poprzedni podgląd.
export const generateApplicationScopeMap = (id: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/generate-scope-map/`, {}),
  );

// Teren inwestycji ULICP: obrys (GeoJSON 4326) + powierzchnia liczone NA ŻYWO
// z zapisanej szerokości pasa od sieci. PATCH zmienia szerokość i zwraca przeliczony
// wynik — powierzchni nie da się nadpisać ręcznie, bo musi zgadzać się z obrysem.
export const getApplicationUlicpArea = (id: string) =>
  unwrap<UlicpArea>(apiClient.get(`${BASE}/official-applications/${id}/ulicp-area/`));

export const setApplicationUlicpBuffer = (id: string, buffer_m: number) =>
  unwrap<UlicpArea>(
    apiClient.patch(`${BASE}/official-applications/${id}/ulicp-area/`, { buffer_m }),
  );

// Obrys narysowany na mapie (GeoJSON 4326) — od tej chwili to ON jest terenem
// inwestycji; pas od sieci przestaje go nadpisywać.
export const saveApplicationUlicpGeometry = (id: string, geometry: unknown) =>
  unwrap<UlicpArea>(
    apiClient.post(`${BASE}/official-applications/${id}/ulicp-area/geometry/`, { geometry }),
  );

// Porzuć ręczny obrys — teren wraca do liczenia z pasa od sieci.
export const resetApplicationUlicpGeometry = (id: string) =>
  unwrap<UlicpArea>(
    apiClient.post(`${BASE}/official-applications/${id}/ulicp-area/reset/`, {}),
  );

// Pas służebności przesyłu (wniosek regulacyjny): obrys (GeoJSON 4326) + powierzchnia
// liczone NA ŻYWO z wytycznych TAURON. Szerokości NIE da się tu ustawić — biorą się
// z rodzaju urządzenia; podaje się tylko obrysy urządzeń, których nie ma w rysunku.
export const getApplicationRegulacjaArea = (id: string) =>
  unwrap<RegulacjaArea>(
    apiClient.get(`${BASE}/official-applications/${id}/regulacja-area/`),
  );

// Obrys narysowany na mapie (GeoJSON 4326) — od tej chwili to ON jest pasem.
export const saveApplicationRegulacjaGeometry = (id: string, geometry: unknown) =>
  unwrap<RegulacjaArea>(
    apiClient.post(`${BASE}/official-applications/${id}/regulacja-area/geometry/`, {
      geometry,
    }),
  );

// Porzuć ręczny obrys — pas wraca do liczenia z wytycznych.
export const resetApplicationRegulacjaGeometry = (id: string) =>
  unwrap<RegulacjaArea>(
    apiClient.post(`${BASE}/official-applications/${id}/regulacja-area/reset/`, {}),
  );

// Obrysy urządzeń, których nie niesie rysunek (stacja, złącze, mufa to pojedyncze
// pinezki). Klucz = id obiektu albo rodzaj urządzenia (wymiar raz dla całej trasy).
// Wartości mogą być TEKSTEM prosto z pola formularza (także z przecinkiem dziesiętnym):
// walidacja siedzi na backendzie, który jako jedyny umie powiedzieć, co jest nie tak.
// Filtrowanie po drodze oznaczałoby, że zła wartość znika bez komunikatu.
export const setApplicationRegulacjaDimensions = (
  id: string,
  dimensions: Record<string, { width_m: string | number; length_m: string | number }>,
) =>
  unwrap<RegulacjaArea>(
    apiClient.post(`${BASE}/official-applications/${id}/regulacja-dimensions/`, {
      dimensions,
    }),
  );

// Opisy urządzeń do pól formularza OMI. {mode:"ai"} układa treść z faktów mapy i zapisuje
// do pól działki; {projektowane?, istniejace?} zapisuje ręcznie poprawione treści.
// mode:"ai_geoportal" — istniejąca sieć czytana przez model wizyjny Z MAPY UZBROJENIA
// (GESUT): rysunek projektu zwykle jej nie niesie, geoportal ją rysuje.
export const setApplicationRegulacjaDeviceDescriptions = (
  id: string,
  payload: { mode?: "ai" | "ai_geoportal"; projektowane?: string; istniejace?: string },
) =>
  unwrap<{ projektowane: string; istniejace: string }>(
    apiClient.post(
      `${BASE}/official-applications/${id}/regulacja-device-descriptions/`,
      payload,
    ),
  );

// Parametry obiektu z Załącznika C druku ULICP (powierzchnia trwale zajęta, szerokość,
// wysokość, uwagi) ułożone przez AI z wykazu urządzeń mapy — zapisują się na wniosku
// i zostają edytowalne (PATCH ulicp_object_params).
export const generateApplicationUlicpParams = (id: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/ulicp-object-params/`, {}),
  );

// Wspólny opis urządzeń ENERGA dla wszystkich działek wniosku (AI z rozbić mapy)
// — zapisuje się na wniosku, edytowalny PATCH-em (energa_work_description).
export const generateApplicationEnergaDescription = (id: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/generate-energa-description/`, {}),
  );

// Opisy urządzeń per RODZAJ formularza WPW ({kind: tekst}) — zapis do kubełków
// consent_work_descriptions działki (wspólne dla pakietów wszystkich jej właścicieli).
export const setApplicationWpwFormDescriptions = (id: string, descriptions: Record<string, string>) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/wpw-form-descriptions/`, { descriptions }),
  );

// Opisy urządzeń per rodzaj formularza WPW ułożone AI z rozbicia mapy działki —
// lądują w edytowalnych polach (wpw-form-descriptions), żeby dało się je poprawić
// PRZED generacją druków. `ai_errors` to kubełki, których AI nie ułożyło: zostają
// z dotychczasową treścią (nic nie jest kasowane) i wymagają ręcznego wpisu.
export type WpwDescriptionsResult = OfficialApplicationDetail & {
  ai_written?: string[];
  ai_errors?: string[];
  ai_assumed_projektowane?: string[];
};

export const generateApplicationWpwDescriptions = (id: string) =>
  unwrap<WpwDescriptionsResult>(
    apiClient.post(`${BASE}/official-applications/${id}/generate-wpw-descriptions/`, {}),
  );

// Komplet formularzy zgody WPW (1–3 druki ENERGA / porozumienie TD) — jedno kliknięcie,
// jeden scalony PDF jako dokument wniosku. Bez `kinds` bierze rodzaje dobrane z mapy.
export const generateApplicationWpwForms = (id: string, kinds?: string[]) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/official-applications/${id}/generate-wpw-forms/`, kinds ? { kinds } : {}),
  );

// Dane drogi krajowej (GDDKiA): numer drogi, kilometraż i strona liczone z API mapy
// GDDKiA z przebiegu trasy przez działki wniosku — zapisują się na wniosku
// i zostają edytowalne (PATCH road_data).
export const computeApplicationRoadData = (id: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/compute-road-data/`, {}),
  );

// Relacja/odcinek drogi krajowej — propozycja AI (numer drogi + kilometraż +
// okolica działek), zapisuje się w road_data.relacja i zostaje edytowalna.
export const suggestApplicationRelacja = (id: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/suggest-relacja/`, {}),
  );

// Z fazy MAPY: zestaw formatek dla działek pakietu (ramki z automatu; szablon,
// skala i wartości #zmiennych# z modala) + start generowania ZIP-a w tle.
export const generateApplicationFormatki = (
  id: string,
  opts?: {
    template?: string; scale_denominator?: number; field_values?: Record<string, string>;
    include_orientation?: boolean;
  },
) =>
  unwrap<{ task_id: string; sheet_set_id: string; frames_count: number; skipped_parcels: string[] }>(
    apiClient.post(`${BASE}/official-applications/${id}/generate-formatki/`, opts ?? {}),
  );

export const setApplicationPoaFee = (
  id: string,
  payload: { link: string; mode: "new" | "existing" | "not_required"; fee?: string; reason?: string;
             office_account_number?: string; stamp_duty_office?: string },
) => unwrap<OfficialApplicationDetail>(
  apiClient.post(`${BASE}/official-applications/${id}/poa-fee/`, payload),
);

/** Ręczna korekta urzędu opłaty skarbowej za pełnomocnictwo dla całego wniosku
 *  (pusty string = powrót do rozpoznania z siedziby mocodawcy). */
export const setApplicationStampDutyOffice = (id: string, office: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/stamp-duty-office/`,
                   { stamp_duty_office: office }),
  );

/** Opłata skarbowa OD SAMEGO WNIOSKU (ULICP: 598 zł za decyzję ULICP) — druga opłata
 *  wniosku, obok 17 zł za pełnomocnictwo. Tryby jak przy pełnomocnictwie; `new` parkuje
 *  wniosek w „czeka na opłatę" do czasu potwierdzenia przelewu. */
export const setApplicationFee = (
  id: string,
  payload: { mode: "new" | "existing" | "not_required"; fee?: string; reason?: string;
             amount?: string; office_account_number?: string; transfer_name?: string;
             description?: string; stamp_duty_office?: string },
) => unwrap<OfficialApplicationDetail>(
  apiClient.post(`${BASE}/official-applications/${id}/application-fee/`, payload),
);

/** Ręczna korekta urzędu opłaty OD WNIOSKU (pusty string = powrót do adresata wniosku).
 *  Osobne pole niż urząd 17 zł — te opłaty idą do różnych urzędów. */
export const setApplicationFeeOffice = (id: string, office: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/application-fee-office/`,
                   { stamp_duty_office: office }),
  );

/** Istniejące opłaty NIE-pełnomocnictwowe do wskazania jako opłata od wniosku
 *  (opłaty projektu wniosku pierwsze). */
export const getApplicationFeeCandidates = (id: string) =>
  unwrap<(PoaFeeCandidate & { same_project?: boolean })[]>(
    apiClient.get(`${BASE}/official-applications/${id}/application-fee-candidates/`),
  );

// Opłata „za pełnomocnictwo" do podpięcia pod ogniwo łańcucha: opłacona (z datą
// opłacenia i plikami potwierdzeń — to one wchodzą do pakietu) ALBO jeszcze
// nieopłacona (można podpiąć; wniosek zaczeka wtedy na potwierdzenie przelewu).
export interface PoaFeeCandidate {
  id: string;
  office_name: string;
  amount: string;
  status: string;
  transfer_name: string;
  created_at: string;
  paid: boolean;
  paid_at: string | null;
  confirmations: string[];
}

export const getPoaFeeCandidates = () =>
  unwrap<PoaFeeCandidate[]>(
    apiClient.get(`${BASE}/official-applications/poa-fee-candidates/`),
  );

// Propozycja sklejenia stron wypisów działek wniosku w jeden PDF (bez dubli).
export const getWypisMergeProposal = (id: string) =>
  unwrap<WypisMergeProposal>(
    apiClient.get(`${BASE}/official-applications/${id}/wypis-merge-proposal/`),
  );

export const mergeWypisPages = (id: string, selections: { wypis: string; pages: number[] }[]) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/merge-wypis-pages/`, { selections }),
  );

// Pobranie wyłącznie stron wypisu obejmujących daną działkę (backend tnie PDF wg
// page_map). Uwierzytelniony widok → blob + zapis po stronie klienta.
export const downloadWnioskWypisPages = async (appId: string, wypisId: string, parcelId: string) => {
  const resp = await apiClient.get(`${BASE}/official-applications/${appId}/wypis-pages/`, {
    params: { wypis: wypisId, parcel: parcelId },
    responseType: "blob",
  });
  saveBlobResponse(resp, "wypis.pdf");
};

export interface PoaFeeSuggestion {
  mode: "new" | "existing";
  fee: PoaFeeCandidate | null;
  candidates: PoaFeeCandidate[];
  reason: string;
}
export const getPoaFeeSuggestion = (appId: string) =>
  unwrap<PoaFeeSuggestion>(
    apiClient.get(`${BASE}/official-applications/${appId}/poa-fee-suggestion/`),
  );

export const extractApplicationFeeDoc = (appId: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<{
    payment_title: string;
    doo_number: string; amount: string; account_number: string;
    office_name: string; office_address: string; case_sign: string;
  }>(apiClient.post(`${BASE}/official-applications/${appId}/extract-fee-document/`, fd, MULTIPART));
};

// Poprawki kwalifikacji: werdykt (override) oraz — dla przypadków, których automat nie
// zna — powierzchnia do wyłączenia i klasa gruntu. Puste zdejmuje nadpisanie i przywraca
// wartość liczoną na backendzie.
export const setApplicationQualification = (
  id: string,
  overrides: {
    parcel_id: string;
    override?: boolean | null;
    exclusion_area_override_m2?: number | null;
    exclusion_area_note?: string;
    exclusion_land_class?: string;
  }[],
) => unwrap<OfficialApplicationDetail>(
  apiClient.post(`${BASE}/official-applications/${id}/qualification/`, { overrides }),
);

export const uploadApplicationOwnDocument = (id: string, file: File | null) => {
  const fd = new FormData();
  if (file) fd.append("file", file);
  return unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/own-document/`, fd, MULTIPART),
  );
};

export const getApplicationAutoMaps = (id: string) =>
  unwrap<{ sheet_set_id: string; name: string; template_name: string; covered_teryts: string[];
           covers_all: boolean; stale: boolean; stale_reason: string; created_at: string;
           // zestaw bez scalonego PDF-a: pobierz ZIP (plot w CAD) i wgraj PDF-y arkuszy
           needs_pdf: boolean; zip_url: string | null; zip_name: string | null;
           // Czyja to formatka. Działka współwłasna siedzi w zakresie kilku wniosków, więc
           // każdy widzi tu też cudze zestawy — własny idzie pierwszy i jest oznaczony,
           // a cudzy podpisany numerem wniosku i urzędem (żeby nikt nie podpiął planu
           // zrobionego pod inny zakres).
           own: boolean; source_number: string; source_office: string }[]>(
    apiClient.get(`${BASE}/official-applications/${id}/auto-maps/`),
  );

export const attachApplicationMap = (id: string, sheetSetId: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/attach-map/`, { sheet_set: sheetSetId }),
  );

export const uploadApplicationMap = (id: string, file: File, label?: string) => {
  const fd = new FormData();
  fd.append("file", file);
  if (label) fd.append("label", label);
  return unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/upload-map/`, fd, MULTIPART),
  );
};

export const deleteApplicationMap = (id: string, mapId: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.delete(`${BASE}/official-applications/${id}/maps/${mapId}/`),
  );

export const uploadApplicationAttachment = (id: string, kind: string, files: File[], note?: string) => {
  const fd = new FormData();
  fd.append("kind", kind);
  files.forEach((f) => fd.append("files", f));
  if (note) fd.append("note", note);
  return unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/upload-attachment/`, fd, MULTIPART),
  );
};

export const deleteApplicationAttachment = (id: string, attId: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.delete(`${BASE}/official-applications/${id}/attachments/${attId}/`),
  );

// Opłacone opłaty (/official-fees, z potwierdzeniami przelewu) do podpięcia jako
// załącznik — opłaty projektu wniosku pierwsze, potem reszta.
export const getApplicationPaidFeeCandidates = (id: string) =>
  unwrap<PaidFeeCandidate[]>(
    apiClient.get(`${BASE}/official-applications/${id}/paid-fee-candidates/`),
  );

export const attachApplicationFeeConfirmation = (id: string, kind: string, feeId: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/attach-fee-confirmation/`, {
      kind, fee: feeId,
    }),
  );

// `allowUnpaidFees`: świadoma kompilacja przed przelewem — kompilat nie obejmie
// potwierdzenia opłaty (backend bez tej flagi odpowiada 400 z kodem `unpaid_fee`).
export const compileApplication = (id: string, allowUnpaidFees = false) =>
  unwrap<{ task_id: string }>(apiClient.post(
    `${BASE}/official-applications/${id}/compile/`,
    allowUnpaidFees ? { allow_unpaid_fees: true } : {},
  ));

// Wydany pakiet: ZIP ze scalonym PDF-em + pełnomocnictwem(-ami) i potwierdzeniami opłat
// w ORYGINALNYCH bajtach (podpis elektroniczny weryfikuje się po rozpakowaniu).
export const downloadApplicationPackageZip = async (id: string, number: string) => {
  const resp = await apiClient.get(`${BASE}/official-applications/${id}/package-zip/`, {
    responseType: "blob",
  });
  saveBlobResponse(resp, `Pakiet_${number.replace(/\//g, "_")}.zip`);
};

// Ręcznie poprawiony pakiet zamiast kompilatu — PDF wchodzi wprost, ODT/DOCX/DOC/RTF
// backend konwertuje synchronicznie. Faza kompilacji wraca do zatwierdzenia.
export const uploadApplicationCompiled = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/upload-compiled/`, fd, MULTIPART),
  );
};

// `allowUnpaidFees`: świadoma wysyłka przed przelewem — urząd dostanie wniosek bez
// potwierdzenia opłaty (backend bez tej flagi odpowiada 400 z kodem `unpaid_fee`).
export const submitOfficialApplication = (
  id: string, channel: string, channelRef?: string, allowUnpaidFees = false,
) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/submit/`, {
      channel, channel_ref: channelRef ?? "",
      ...(allowUnpaidFees ? { allow_unpaid_fees: true } : {}),
    }),
  );

// Obieg akceptacji wysyłki (2026-09-25): koordynator przekazuje skompletowany wniosek
// do weryfikacji dyrektora; zatwierdzenie = zwykły submit (kanał podpowiada się
// z przekazania), zwrot do poprawy wraca do szkicu z notatką.
export const requestApplicationApproval = (id: string, channel: string, channelRef?: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/request-approval/`, {
      channel, channel_ref: channelRef ?? "",
    }),
  );

export const withdrawApplicationApproval = (id: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/withdraw-approval/`, {}),
  );

export const rejectApplicationApproval = (id: string, note: string) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/reject-approval/`, { note }),
  );

export const cancelApplication = (id: string) =>
  unwrap<OfficialApplicationDetail>(apiClient.post(`${BASE}/official-applications/${id}/cancel/`, {}));

// Wycofanie ZŁOŻONEGO wniosku, zanim pismo wyszło (papier w kolejce DLC_02, e-Doręczenie przed
// akceptacją) — nieodwracalne: wpis DLC_02 anulowany / przesyłka DLC_09 „Wycofana", sprawa
// automatyczna usunięta (gdy nic więcej w niej nie ma), wniosek wraca do szkicu.
export const withdrawApplicationSubmission = (id: string, confirmUntracked = false) =>
  unwrap<OfficialApplicationDetail>(
    apiClient.post(
      `${BASE}/official-applications/${id}/withdraw-submission/`,
      confirmUntracked ? { confirm_untracked: true } : {},
    ),
  );

// Zakończenie pakietu z OTWARTĄ sprawą zamyka ją razem z pakietem — wtedy `outcome` jest
// wymagane (partial niesie działki uzgodnione `resolved_parcels`; positive = cała sprawa).
// „Zakończ (odpowiedź wpłynęła)": z OTWARTĄ sprawą wymaga rozstrzygnięcia; odpowiedź urzędu
// (znak, data wpływu, skany) trafia do sprawy jako pozycja „odpowiedź" zamykająca wątek.
export type CompleteApplicationPayload = {
  outcome?: string;
  resolved_parcels?: string[];
  note?: string;
  office_ref?: string;
  date_received?: string;
  files?: File[];
  /** Wniosek o klauzulę (outcome=positive): data nadania klauzuli, skan zaświadczenia i
   *  data powrotu ORYGINAŁU decyzji — backend sam stawia stempel na decyzji (mark-clause). */
  final_at?: string;
  clause_file?: File;
  original_returned_at?: string;
};

export const completeApplication = (id: string, payload: CompleteApplicationPayload = {}) => {
  const fd = new FormData();
  if (payload.outcome) fd.append("outcome", payload.outcome);
  for (const p of payload.resolved_parcels ?? []) fd.append("resolved_parcels", p);
  if (payload.note) fd.append("note", payload.note);
  if (payload.office_ref) fd.append("office_ref", payload.office_ref);
  if (payload.date_received) fd.append("date_received", payload.date_received);
  for (const f of payload.files ?? []) fd.append("files", f);
  if (payload.final_at) fd.append("final_at", payload.final_at);
  if (payload.clause_file) fd.append("clause_file", payload.clause_file);
  if (payload.original_returned_at) fd.append("original_returned_at", payload.original_returned_at);
  return unwrap<OfficialApplicationDetail>(
    apiClient.post(`${BASE}/official-applications/${id}/complete/`, fd, MULTIPART),
  );
};

// Obieg ORYGINAŁU decyzji przy wniosku o klauzulę — ręczna korekta dat (klucz pominięty =
// bez zmiany, null = wyczyść). Wysyłka i zakończenie ustawiają je same.
export const setApplicationDecisionOriginal = (
  id: string,
  payload: { sent_at?: string | null; returned_at?: string | null },
) => unwrap<OfficialApplicationDetail>(
  apiClient.post(`${BASE}/official-applications/${id}/decision-original/`, payload),
);

// ── rejestr pełnomocnictw ─────────────────────────────────────────────────────
export const getPowersOfAttorney = (params: Record<string, string> = {}) =>
  unwrap<PowerOfAttorneyRow[]>(apiClient.get(`${BASE}/powers-of-attorney/`, { params }));

export const createPowerOfAttorney = (payload: Partial<PowerOfAttorneyRow>) =>
  unwrap<PowerOfAttorneyRow>(apiClient.post(`${BASE}/powers-of-attorney/`, payload));

export const updatePowerOfAttorney = (id: string, payload: Partial<PowerOfAttorneyRow>) =>
  unwrap<PowerOfAttorneyRow>(apiClient.patch(`${BASE}/powers-of-attorney/${id}/`, payload));

export const deletePowerOfAttorney = (id: string) =>
  unwrap<unknown>(apiClient.delete(`${BASE}/powers-of-attorney/${id}/`));

export const uploadPoaFile = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<PowerOfAttorneyRow>(
    apiClient.post(`${BASE}/powers-of-attorney/${id}/upload-file/`, fd, MULTIPART),
  );
};

export const getExpiringPoas = () =>
  unwrap<PowerOfAttorneyRow[]>(apiClient.get(`${BASE}/powers-of-attorney/expiring/`));

// Optional scoping pickers for the PoA form: przedsięwzięcie list + a client's oddziały.
export const getPoaProjects = (clientId?: string) =>
  unwrap<PoaProjectOption[]>(
    apiClient.get(`${BASE}/powers-of-attorney/projects/`, {
      params: clientId ? { client: clientId } : {},
    }),
  );

export const getClientBranches = (clientId?: string) =>
  unwrap<ClientBranch[]>(
    apiClient.get(`/client-branches/`, {
      params: { active: "true", ...(clientId ? { client: clientId } : {}) },
    }),
  );

export const getPoaClientConfig = (clientId?: string, elektim?: boolean) =>
  unwrap<{ stamp_duty_office_id: string | null; stamp_duty_office_name: string }>(
    apiClient.get(`${BASE}/powers-of-attorney/client-config/`, {
      params: elektim ? { elektim: "1" } : { client: clientId },
    }),
  );

export const setPoaClientConfig = (payload: {
  client?: string; elektim?: boolean; stamp_duty_office?: string;
}) => unwrap<{ saved: boolean }>(apiClient.post(`${BASE}/powers-of-attorney/client-config/`, payload));

export const getPoaOfficeConfig = (officeId: string) =>
  unwrap<{ requires_second_poa: boolean; chain_note: string }>(
    apiClient.get(`${BASE}/powers-of-attorney/office-config/`, { params: { office: officeId } }),
  );

export const setPoaOfficeConfig = (payload: {
  office: string; requires_second_poa: boolean; chain_note?: string;
}) => unwrap<{ saved: boolean }>(apiClient.post(`${BASE}/powers-of-attorney/office-config/`, payload));

export const getPoaConfigs = () =>
  unwrap<{
    stamp_duty: { client_id: string | null; principal_name: string; office_id: string; office_name: string }[];
    chains: { office_id: string; office_name: string; requires_second_poa: boolean; note: string }[];
  }>(apiClient.get(`${BASE}/powers-of-attorney/configs/`));

// ── rejestr decyzji ───────────────────────────────────────────────────────────
export const getDecisions = (params: Record<string, string> = {}) =>
  unwrap<AdministrativeDecisionRow[]>(apiClient.get(`${BASE}/decisions/`, { params }));

export const createDecision = (payload: Record<string, unknown>) =>
  unwrap<AdministrativeDecisionRow>(apiClient.post(`${BASE}/decisions/`, payload));

export const updateDecision = (id: string, payload: Record<string, unknown>) =>
  unwrap<AdministrativeDecisionRow>(apiClient.patch(`${BASE}/decisions/${id}/`, payload));

export const uploadDecisionFile = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<AdministrativeDecisionRow>(
    apiClient.post(`${BASE}/decisions/${id}/upload-file/`, fd, MULTIPART),
  );
};

export const markDecisionClause = (id: string, finalAt: string, file?: File) => {
  const fd = new FormData();
  fd.append("final_at", finalAt);
  if (file) fd.append("file", file);
  return unwrap<AdministrativeDecisionRow>(
    apiClient.post(`${BASE}/decisions/${id}/mark-clause/`, fd, MULTIPART),
  );
};

// Decyzja z rejestru → sprawa z Korespondencji JAKO pozycja „odpowiedź" (znak, data
// wydania, plik). Jedna decyzja = jedna sprawa; tylko w obrębie tego samego projektu.
export const attachDecisionToProceeding = (
  id: string,
  payload: { proceeding: string; closes_thread?: boolean; outcome?: string; note?: string },
) => unwrap<AdministrativeDecisionRow>(
  apiClient.post(`${BASE}/decisions/${id}/attach-to-proceeding/`, payload),
);

// Cofnięcie ostateczności (klauzula odnotowana przez pomyłkę) — najpierw podgląd
// konsekwencji do modala, potem samo cofnięcie.
export const getClauseRevertImpact = (id: string) =>
  unwrap<ClauseRevertImpact>(apiClient.get(`${BASE}/decisions/${id}/clause-impact/`));

export const clearDecisionClause = (id: string, reason: string) =>
  unwrap<AdministrativeDecisionRow>(
    apiClient.post(`${BASE}/decisions/${id}/clear-clause/`, { reason }),
  );

// ── wypis orders (zamawianie wypisów EGiB per powiat) ────────────────────────
export interface StarostwoProposal {
  name: string;
  department: string;
  street: string;
  building_no: string;
  postal_code: string;
  city: string;
  phone: string;
  email: string;
  bank_account: string;
  // URL of the page the account number was read from — shown next to the account
  // field so the user can verify that specific value independently of `source`.
  bank_account_source: string;
  website: string;
  // Link to the office's contact / address tab on its official site — a
  // verification link so the user can confirm the address (and gmina vs miasto).
  contact_url: string;
  source: string;
  via_web_search: boolean;
}

export interface StarostwoSuggestion {
  existing: { id: string; name: string; full_address: string }[];
  proposal: StarostwoProposal | null;
  // Present when the registry had nothing — the AI lookup runs as a background
  // task; poll it (useTaskPolling) and read the suggestion from record.result.
  task_id?: string;
}

// Registry lookup answers synchronously; otherwise a background Claude lookup
// (TERYT prefix + web search) starts and the modal streams its log as progress.
export const suggestStarostwo = (county: string, voivodeship?: string, teryts?: string[]) =>
  unwrap<StarostwoSuggestion>(
    apiClient.get(`${BASE}/wypis-orders/suggest-starostwo/`, {
      params: {
        county,
        ...(voivodeship ? { voivodeship } : {}),
        ...(teryts?.length ? { teryts: teryts.slice(0, 5).join(",") } : {}),
      },
    }),
  );

// ── ponowne zamówienie wypisu (zbiorczo, z panelu zamawiania) ──
// Działki z wypisem do zaznaczenia w modalu „Zamów ponownie wypisy”: po projekcie
// (`connection`, tak wybiera modal) albo po powiecie (`county`).
export type WypisReorderOption = {
  id: string; teryt: string; short_number: string; obreb: string; commune: string; county: string;
  voivodeship: string; connection_id: string; project_id: string; project_name: string;
  access_mode: string; owners: string[]; wypisy_total: number;
  // zamówienia wypisów w historii — 0 wypisów + zamówienie = wypis odpięty od działki
  wypis_orders_total: number;
  wypis_needed: boolean; wypis_needed_reason: string; in_active_order: boolean;
};
export const getWypisReorderOptions = (params: { connection?: string; county?: string }) =>
  unwrap<WypisReorderOption[]>(
    apiClient.get(`${BASE}/wypis-orders/reorder-options/`, { params }),
  );
// Zgłoszenie do PONOWNEGO zamówienia: flaga „potrzebny wypis” + powód; działki wracają do
// panelu per powiat i idą do najbliższego zamówienia razem z innymi kandydatami.
export const reorderWypisParcels = (parcelIds: string[], reason: string) =>
  unwrap<{ flagged: number; flagged_ids: string[]; skipped: { teryt: string; why: string }[] }>(
    apiClient.post(`${BASE}/wypis-orders/reorder/`, { parcel_ids: parcelIds, reason }),
  );

export const getWypisCandidates = (params: { county?: string; connection?: string } = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => !!v) as [string, string][],
  ).toString();
  return unwrap<WypisCandidateGroup[]>(
    apiClient.get(`${BASE}/wypis-orders/candidates/${qs ? `?${qs}` : ""}`),
  );
};

export const getWypisOrders = (params: Record<string, string> = {}) => {
  const qs = new URLSearchParams(params).toString();
  return unwrap<WypisOrderRow[]>(apiClient.get(`${BASE}/wypis-orders/${qs ? `?${qs}` : ""}`));
};

export const getWypisOrder = (id: string) =>
  unwrap<WypisOrder>(apiClient.get(`${BASE}/wypis-orders/${id}/`));

export const createWypisOrder = (payload: WypisOrderCreatePayload) =>
  unwrapWithWarnings<WypisOrder>(apiClient.post(`${BASE}/wypis-orders/`, payload));

export const updateWypisOrder = (id: string, payload: Partial<WypisOrderCreatePayload>) =>
  unwrap<WypisOrder>(apiClient.patch(`${BASE}/wypis-orders/${id}/`, payload));

export const generateWypisDocuments = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/wypis-orders/${id}/generate/`, {}));

// Wspólny payload request-fee (wypisy/MZ/MPZP): mode=new (domyślny) tworzy prośbę w
// /official-fees z pól formularza; mode=existing podpina wskazaną WŁASNĄ opłatę.
export interface RequestDooFeePayload {
  mode?: "new" | "existing";
  fee?: string;
  amount?: string;
  office_account_number?: string;
  transfer_name?: string;
  paying_company?: string;
  description?: string;
  investor_name_required_in_title?: boolean;
}

/** Kandydatka do „podepnij istniejącą" w modalu DOO — własna, żywa, nigdzie niepodpięta. */
export interface DooFeeCandidate {
  id: string;
  office_name: string;
  amount: string;
  status: string;
  transfer_name: string;
  project_name: string;
  created_at: string;
}

/** Edycja DOO: pominięte pole zostaje bez zmian; fee: "" odpina opłatę. */
export interface EditDooPayload {
  fee?: string;
  file?: File;
}

const editDooForm = (payload: EditDooPayload) => {
  const fd = new FormData();
  if (payload.fee !== undefined) fd.append("fee", payload.fee);
  if (payload.file) fd.append("file", payload.file);
  return fd;
};

export const requestWypisFee = (id: string, payload: RequestDooFeePayload) =>
  unwrap<WypisOrder>(apiClient.post(`${BASE}/wypis-orders/${id}/request-fee/`, payload));

export const getWypisFeeCandidates = () =>
  unwrap<DooFeeCandidate[]>(apiClient.get(`${BASE}/wypis-orders/fee-candidates/`));

export const editWypisDoo = (id: string, payload: EditDooPayload) =>
  unwrap<WypisOrder>(
    apiClient.post(`${BASE}/wypis-orders/${id}/edit-doo/`, editDooForm(payload), MULTIPART),
  );

export const extractWypisDoo = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<{
    payment_title: string;
    doo_number: string; amount: string; account_number: string;
    office_name: string; office_address: string; case_sign: string;
  }>(apiClient.post(`${BASE}/wypis-orders/${id}/extract-doo/`, fd, MULTIPART));
};

// 17 zł opłata skarbowa za pełnomocnictwo dla zamówienia wypisów — osobna od opłaty za
// wypis. Domyślnie wymagana (bramkuje wysyłkę), z opcją „niepotrzebna". Zamówienie może
// mieć kilka opłat (new/existing dopinają do listy, detach odpina jedną).
// `stamp_duty_office` = ręczna korekta urzędu opłaty (id urzędu; "" = powrót do
// rozpoznania automatycznego); mode "set_office" zmienia sam urząd, bez zakładania opłaty.
export const setWypisPoaFee = (
  id: string,
  payload: { mode: "new" | "existing" | "not_required" | "detach" | "set_office"; fee?: string;
             reason?: string; office_account_number?: string; stamp_duty_office?: string },
) => unwrap<WypisOrder>(apiClient.post(`${BASE}/wypis-orders/${id}/poa-fee/`, payload));

export const getWypisPoaFeeSuggestion = (id: string) =>
  unwrap<PoaFeeSuggestion>(apiClient.get(`${BASE}/wypis-orders/${id}/poa-fee-suggestion/`));

export const markWypisSent = (id: string, sentNote?: string) =>
  unwrap<WypisOrder>(
    apiClient.post(`${BASE}/wypis-orders/${id}/mark-sent/`, sentNote ? { sent_note: sentNote } : {}),
  );

export const markWypisReceived = (id: string) =>
  unwrap<WypisOrder>(apiClient.post(`${BASE}/wypis-orders/${id}/mark-received/`, {}));

export const cancelWypisOrder = (id: string) =>
  unwrap<WypisOrder>(apiClient.post(`${BASE}/wypis-orders/${id}/cancel/`, {}));

// Przepięcie zamawiającego (created_by) na inną osobę z GK — wpis „kto → kto"
// odkłada się w historii zamówienia.
export const changeWypisOrderer = (id: string, userId: string) =>
  unwrap<WypisOrder>(
    apiClient.post(`${BASE}/wypis-orders/${id}/change-orderer/`, { user: userId }),
  );

// Cofnięcie o JEDEN etap — cel wyznacza backend (`revert_to`/`revert_label`).
export const revertWypisOrder = (id: string, reason?: string) =>
  unwrap<WypisOrder>(
    apiClient.post(`${BASE}/wypis-orders/${id}/revert/`, reason ? { reason } : {}),
  );

// `other` = „Inne pliki": trafiają pod other_files, które backend przyjmuje bez
// białej listy rozszerzeń (limit rozmiaru obowiązuje nadal).
export const uploadWypisResultFiles = (id: string, files: File[], other = false) => {
  const fd = new FormData();
  files.forEach((f) => fd.append(other ? "other_files" : "files", f));
  return unwrap<WypisResultFile[]>(
    apiClient.post(`${BASE}/wypis-orders/${id}/result-files/`, fd, MULTIPART),
  );
};

// `reverted_to` != null: skasowano ostatni plik ze starostwa, więc backend cofnął
// status zamówienia (odbiór bez żadnego pliku nie ma podstawy).
export const deleteWypisResultFile = (id: string, resultFileId: string) =>
  unwrap<{ deleted: boolean; status: WypisOrderStatus; reverted_to: WypisOrderStatus | null }>(
    apiClient.delete(`${BASE}/wypis-orders/${id}/result-files/${resultFileId}/`),
  );

export const extractWypisResults = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/wypis-orders/${id}/extract/`, {}));

export const applyWypisExtraction = (id: string, parcels: WypisProposalParcel[]) =>
  unwrap<WypisOrder>(apiClient.post(`${BASE}/wypis-orders/${id}/apply/`, { parcels }));

// ── mapa zasadnicza (zamówienia MZ — formularz P + obrys MDCP jako DXF) ───────
export const getMzSuggestContext = (connectionId: string) =>
  unwrap<MzSuggestContext>(
    apiClient.get(`${BASE}/mz-orders/suggest-context/`, { params: { connection: connectionId } }),
  );

/** Modułowa lista zamówień MDCP ze wszystkich projektów ZE (zakładka „MDCP"). */
export const getMdcpOrders = (params: Record<string, string> = {}) => {
  const qs = new URLSearchParams(params).toString();
  return unwrap<import("./types").MdcpOrderListRow[]>(
    apiClient.get(`${BASE}/mdcp/orders/${qs ? `?${qs}` : ""}`),
  );
};

export const getMzOrders = (params: Record<string, string> = {}) => {
  const qs = new URLSearchParams(params).toString();
  return unwrap<MzOrderRow[]>(apiClient.get(`${BASE}/mz-orders/${qs ? `?${qs}` : ""}`));
};

export const getMzOrder = (id: string) =>
  unwrap<MzOrder>(apiClient.get(`${BASE}/mz-orders/${id}/`));

export const createMzOrder = (payload: MzOrderCreatePayload) =>
  unwrap<MzOrder>(apiClient.post(`${BASE}/mz-orders/`, payload));

export const updateMzOrder = (id: string, payload: Partial<MzOrderCreatePayload>) =>
  unwrap<MzOrder>(apiClient.patch(`${BASE}/mz-orders/${id}/`, payload));

export const generateMzDocuments = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/mz-orders/${id}/generate/`, {}));

export const requestMzFee = (id: string, payload: RequestDooFeePayload) =>
  unwrap<MzOrder>(apiClient.post(`${BASE}/mz-orders/${id}/request-fee/`, payload));

export const getMzFeeCandidates = () =>
  unwrap<DooFeeCandidate[]>(apiClient.get(`${BASE}/mz-orders/fee-candidates/`));

export const editMzDoo = (id: string, payload: EditDooPayload) =>
  unwrap<MzOrder>(
    apiClient.post(`${BASE}/mz-orders/${id}/edit-doo/`, editDooForm(payload), MULTIPART),
  );

export const extractMzDoo = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<{
    amount: string;
    account_number: string;
    payment_title: string;
    doo_number: string;
    case_sign: string;
    office_name: string;
    office_address: string;
  }>(apiClient.post(`${BASE}/mz-orders/${id}/extract-doo/`, fd, MULTIPART));
};

export const setMzPoaFee = (
  id: string,
  payload: {
    mode: "new" | "existing" | "not_required" | "detach" | "set_office";
    fee?: string;
    reason?: string;
    office_account_number?: string;
    /** Ręczna korekta urzędu opłaty ("" = powrót do rozpoznania automatycznego). */
    stamp_duty_office?: string;
  },
) => unwrap<MzOrder>(apiClient.post(`${BASE}/mz-orders/${id}/poa-fee/`, payload));

export const getMzPoaFeeSuggestion = (id: string) =>
  unwrap<PoaFeeSuggestion>(apiClient.get(`${BASE}/mz-orders/${id}/poa-fee-suggestion/`));

export const addMzOrderComment = (id: string, body: string) =>
  unwrap<MzOrder>(apiClient.post(`${BASE}/mz-orders/${id}/comments/`, { body }));

export const updateMzOrderComment = (id: string, commentId: string, body: string) =>
  unwrap<MzOrder>(apiClient.patch(`${BASE}/mz-orders/${id}/comments/${commentId}/`, { body }));

export const deleteMzOrderComment = (id: string, commentId: string) =>
  unwrap<MzOrder>(apiClient.delete(`${BASE}/mz-orders/${id}/comments/${commentId}/`));

export const markMzSent = (id: string, sentNote?: string) =>
  unwrap<MzOrder>(
    apiClient.post(`${BASE}/mz-orders/${id}/mark-sent/`, sentNote ? { sent_note: sentNote } : {}),
  );

export const markMzReceived = (id: string) =>
  unwrap<MzOrder>(apiClient.post(`${BASE}/mz-orders/${id}/mark-received/`, {}));

export const cancelMzOrder = (id: string) =>
  unwrap<MzOrder>(apiClient.post(`${BASE}/mz-orders/${id}/cancel/`, {}));

// Przepięcie zamawiającego (created_by) na inną osobę z GK — wpis „kto → kto"
// odkłada się w historii zamówienia.
export const changeMzOrderer = (id: string, userId: string) =>
  unwrap<MzOrder>(
    apiClient.post(`${BASE}/mz-orders/${id}/change-orderer/`, { user: userId }),
  );

// Cofnięcie o JEDEN etap — cel wyznacza backend (pole `revert_to`/`revert_label`
// zamówienia), więc przycisk nie musi znać reguł cyklu życia.
export const revertMzOrder = (id: string, reason?: string) =>
  unwrap<MzOrder>(
    apiClient.post(`${BASE}/mz-orders/${id}/revert/`, reason ? { reason } : {}),
  );

// Bez pliku mapy backend odmawia z kodem „no_map_file" i pyta — potwierdzenie
// przechodzi flagą (zamówienie bywa zamykane, gdy mapa przyszła inną drogą).
export const completeMzOrder = (id: string, confirmWithoutFiles = false) =>
  unwrap<MzOrder>(
    apiClient.post(`${BASE}/mz-orders/${id}/complete/`,
      confirmWithoutFiles ? { confirm_without_files: true } : {}),
  );

// `kind`: "map" (wektor dla projektanta), "licencja" (PDF/XML/ODT, która wchodzi jako
// załącznik wniosków urzędowych) albo "other" (dowolny format — korespondencja itp.;
// idzie pod other_files, więc backend pomija białą listę rozszerzeń). Bez wskazania
// backend rozpoznaje po rozszerzeniu.
export const uploadMzResultFiles = (
  id: string,
  files: File[],
  kind?: "map" | "licencja" | "other",
) => {
  const fd = new FormData();
  files.forEach((f) => fd.append(kind === "other" ? "other_files" : "files", f));
  if (kind && kind !== "other") fd.append("kind", kind);
  return unwrap<MzResultFile[]>(
    apiClient.post(`${BASE}/mz-orders/${id}/result-files/`, fd, MULTIPART),
  );
};

// `reverted_to` != null: skasowano ostatnią mapę/licencję, więc backend cofnął
// status zamówienia (odbiór bez żadnego pliku ze starostwa nie ma podstawy).
export const deleteMzResultFile = (id: string, resultFileId: string) =>
  unwrap<{ deleted: boolean; status: MzOrderStatus; reverted_to: MzOrderStatus | null }>(
    apiClient.delete(`${BASE}/mz-orders/${id}/result-files/${resultFileId}/`),
  );

// Właściciel-instytucja z wypisu → propozycja urzędu (gmina/starostwo/marszałek/ZDW) +
// dopasowania z rejestru; „lookup" dociąga dane z internetu (AI) — jak JST-assign.
export interface OwnerOfficeSuggestion {
  institution: string | null;
  label: string;
  office_kind: string;
  // Konkretna jednostka wyczytana z nazwy właściciela na wypisie (np. „Nadleśnictwo
  // Krasnystaw") — nadrzędny sygnał doboru; pusta gdy wypis jej nie nazywa.
  unit_name: string;
  reason: string;
  commune: string;
  county: string;
  voivodeship: string;
  teryt: string;
  owner_name: string;
  existing: JstRegistryMatch[];
}
// Office matching serves both flows; `base` selects the viewset that carries the
// endpoints (`wypis-orders` for an order, `parcel-wypisy` for a single manual wypis).
export type OfficeSourceBase = "wypis-orders" | "parcel-wypisy" | "parcels";

export const suggestOwnerOffice = (
  base: OfficeSourceBase,
  id: string,
  payload: { owner_name: string; ownership_code?: string; teryt?: string; parcel_id?: string | null },
) => unwrap<OwnerOfficeSuggestion>(
  apiClient.post(`${BASE}/${base}/${id}/suggest-owner-office/`, payload),
);

export const lookupOwnerOffice = (
  base: OfficeSourceBase,
  id: string,
  payload: {
    institution: string; commune?: string; county?: string; voivodeship?: string;
    teryts?: string[]; unit_name?: string;
  },
) => unwrap<JstOfficeSuggestion>(
  apiClient.post(`${BASE}/${base}/${id}/lookup-office/`, payload),
);

// Zbiorcze dopasowanie urzędów do WSZYSTKICH właścicieli-instytucji z przeglądu
// ekstrakcji: jedna jednostka (gmina/powiat/województwo) = jedno wyszukanie, także
// w internecie gdy rejestru brak. Zadanie w tle; wynik z task.result.
export interface OwnerOfficeMatchRef {
  row_index: number;
  owner_index: number;
  owner_name: string;
}

export interface OwnerOfficeMatchProposal {
  key: string;
  institution: string;
  label: string;
  office_kind: string;
  unit_name: string;
  owners: OwnerOfficeMatchRef[];
  proposal: JstOfficeProposal | null;
}

export interface OwnerOfficeMatchResult {
  assigned: (OwnerOfficeMatchRef & { institution: string; label: string; office: JstRegistryMatch })[];
  proposals: OwnerOfficeMatchProposal[];
  skipped: (OwnerOfficeMatchRef & { reason: string })[];
}

export const matchOwnerOffices = (
  base: OfficeSourceBase,
  id: string,
  rows: {
    row_index: number;
    parcel_id: string | null;
    teryt: string;
    ownership_code: string;
    owners: { owner_index: number; kind: string; full_name: string; office_id?: string | null }[];
  }[],
) => unwrap<{ task_id: string }>(
  apiClient.post(`${BASE}/${base}/${id}/match-owner-offices/`, { rows }),
);

// ── connection file attachments ──────────────────────────────────────────────
export const getConnectionFiles = (connectionId: string) =>
  unwrap<ConnectionFile[]>(apiClient.get(`${BASE}/connection-files/?connection=${connectionId}`));

export const uploadConnectionFile = (
  connectionId: string, file: File, description: string,
  opts?: { milestoneId?: string; folderSlug?: string; milestoneSlot?: string },
) => {
  const fd = new FormData();
  fd.append("connection", connectionId);
  fd.append("file", file);
  if (description) fd.append("description", description);
  // Tag the file with its origin milestone so it's annotated in the Pliki tab.
  if (opts?.milestoneId) fd.append("milestone", opts.milestoneId);
  // Pole kamienia (MDCP vs FOTO) — decyduje o folderze docelowym pliku.
  if (opts?.milestoneSlot) fd.append("milestone_slot", opts.milestoneSlot);
  // Tag the file with a manual folder (folders.py). Automatic slugs are rejected by the API.
  if (opts?.folderSlug) fd.append("folder_slug", opts.folderSlug);
  return unwrap<ConnectionFile>(apiClient.post(`${BASE}/connection-files/`, fd, MULTIPART));
};

// Drzewo folderów projektu (foldery automatyczne read-only + ręczne zapisywalne).
export const getProjectFolders = (connectionId: string) =>
  unwrap<FolderTreeResponse>(apiClient.get(`${BASE}/connections/${connectionId}/folders/`));

// Jawne nadpisanie poufności folderu (folderSlug) ALBO pojedynczego pliku (managedFileId)
// — koordynator/dyrektor/CEO. Nadpisanie działa w dół poddrzewa; podrzędne można
// „odpoufnić" mimo poufnego rodzica. Zwraca świeże drzewo — to samo co getProjectFolders.
export const setFolderConfidentiality = (
  connectionId: string,
  target: { folderSlug?: string; managedFileId?: string },
  isConfidential: boolean,
) =>
  unwrap<FolderTreeResponse>(apiClient.post(
    `${BASE}/connections/${connectionId}/folders/confidentiality/`,
    {
      folder_slug: target.folderSlug,
      managed_file_id: target.managedFileId,
      is_confidential: isConfidential,
    },
  ));

// Nowy folder własny projektu w zakładce Pliki (kierownik przedsięwzięcia/dyrektor/CEO).
// Zwraca świeże drzewo.
export const createProjectFolder = (connectionId: string, label: string) =>
  unwrap<FolderTreeResponse>(apiClient.post(
    `${BASE}/connections/${connectionId}/folders/create/`, { label },
  ));

// Zmiana nazwy folderu własnego projektu (kierownik przedsięwzięcia/dyrektor/CEO).
// Zwraca świeże drzewo.
export const renameProjectFolder = (connectionId: string, slug: string, label: string) =>
  unwrap<FolderTreeResponse>(apiClient.post(
    `${BASE}/connections/${connectionId}/folders/rename/`, { slug, label },
  ));

// Usunięcie folderu własnego — jego pliki wracają do kosza „Inne / bez folderu".
// Zwraca świeże drzewo + `moved_to_inne`.
export const deleteProjectFolder = (connectionId: string, slug: string) =>
  unwrap<FolderTreeResponse>(apiClient.post(
    `${BASE}/connections/${connectionId}/folders/delete/`, { slug },
  ));

// Odpięcie wypisu od działek z zakładki Pliki (wpis `parcel_wypis` w folderze WYPISY;
// kierownik/dyrektor/CEO). Kasuje tylko powiązanie (działki + mapa stron) — plik zostaje
// w projekcie i wraca do „Inne / bez folderu". Zwraca świeże drzewo.
export const detachWypisFromFolder = (connectionId: string, wypisId: string) =>
  unwrap<FolderTreeResponse>(apiClient.post(
    `${BASE}/connections/${connectionId}/folders/detach-wypis/`, { wypis_id: wypisId },
  ));

// Kopiowanie/przenoszenie plików między folderami zakładki Pliki. Cel zawsze
// nieautomatyczny; przenoszenie tylko z nieautomatycznych; z automatycznych — kopia.
// Dokładnie jedno z: managed_file_id (kopia pliku), connection_file_id (przeniesienie
// pliku), source_slug (cała zawartość folderu). Zwraca świeże drzewo + `transferred`.
export const transferProjectFiles = (
  connectionId: string,
  payload: {
    operation: "copy" | "move";
    target_slug: string;
    managed_file_id?: string;
    connection_file_id?: string;
    source_slug?: string;
  },
) =>
  unwrap<FolderTreeResponse>(apiClient.post(
    `${BASE}/connections/${connectionId}/folders/transfer/`, payload,
  ));

// Zamówienia przedsięwzięcia, do których można udostępnić foldery/pliki zakładki „Pliki".
export const getShareableOrders = (connectionId: string) =>
  unwrap<{ orders: ShareableOrder[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/folders/shareable-orders/`),
  );

// „Udostępnij w zleceniu": WSZYSTKIE niepoufne pliki projektu (scope: "all" — reguła żywa,
// obejmie też foldery i pliki dołożone później) albo punktowo folder (folder_slug) /
// pojedynczy plik (managed_file_id). Zwraca świeże drzewo zakładki ze znacznikami.
export const shareToOrder = (
  connectionId: string,
  payload: { order: string; scope?: "all"; folder_slug?: string; managed_file_id?: string },
) =>
  unwrap<FolderTreeResponse>(
    apiClient.post(`${BASE}/connections/${connectionId}/folders/share-to-order/`, payload),
  );

// Cofnięcie udostępnienia: całe (scope: "all"), jeden folder albo jeden plik. Czego nie da
// się zdjąć punktowo (trzyma to „wszystkie niepoufne" albo folder nadrzędny), wraca jako
// błąd z komunikatem mówiącym, co zdjąć zamiast tego.
export const unshareFromOrder = (
  connectionId: string,
  payload: { order: string; scope?: "all"; folder_slug?: string; managed_file_id?: string },
) =>
  unwrap<FolderTreeResponse>(
    apiClient.post(`${BASE}/connections/${connectionId}/folders/unshare-from-order/`, payload),
  );

export const updateConnectionFileDescription = (id: string, description: string) =>
  unwrap<ConnectionFile>(apiClient.patch(`${BASE}/connection-files/${id}/`, { description }));

// Zmiana nazwy pliku (ManagedFile.original_filename) — kopie w innych folderach wskazują
// ten sam plik, więc nazwa zmienia się wszędzie.
export const renameConnectionFile = (id: string, filename: string) =>
  unwrap<ConnectionFile>(apiClient.patch(`${BASE}/connection-files/${id}/`, { filename }));

export const deleteConnectionFile = (id: string) =>
  unwrap<{ deleted: boolean }>(apiClient.delete(`${BASE}/connection-files/${id}/`));

export const importOwnersCsv = (parcelId: string, file: File) => {
  const fd = new FormData();
  fd.append("parcel", parcelId);
  fd.append("file", file);
  return unwrap<{ task_id: string }>(apiClient.post(`${BASE}/parcel-owners/import-csv/`, fd, MULTIPART));
};

// Project-wide twin of importOwnersCsv: one CSV with an "Identyfikator działki" column,
// fanning owners out to every matching parcel in the connection. When replaceExisting is set,
// each parcel referenced by the CSV has its existing private owners replaced rather than appended.
export const importOwnersCsvBulk = (connectionId: string, file: File, replaceExisting = false) => {
  const fd = new FormData();
  fd.append("connection", connectionId);
  fd.append("file", file);
  if (replaceExisting) fd.append("replace", "true");
  return unwrap<{ task_id: string }>(apiClient.post(`${BASE}/parcel-owners/import-csv-bulk/`, fd, MULTIPART));
};

// ── Generacja dokumentów — szablony ODF (globally visible, author-owned) ─────
export const getDocTemplates = () =>
  unwrap<DocTemplate[]>(apiClient.get(`/doc-templates/`));

export const getDocVariables = () =>
  unwrap<DocVariableGroup[]>(apiClient.get(`/doc-templates/variables/`));

export const createDocTemplate = (v: { name: string; kind?: string; operator?: string; description?: string; file: File }) => {
  const fd = new FormData();
  fd.append("name", v.name);
  if (v.kind) fd.append("kind", v.kind);
  if (v.operator) fd.append("operator", v.operator);
  if (v.description) fd.append("description", v.description);
  fd.append("file", v.file);
  return unwrap<DocTemplate>(apiClient.post(`/doc-templates/`, fd, MULTIPART));
};

// PATCH is JSON for metadata-only edits; switches to multipart when a replacement
// .odt is attached (the backend then re-scans the #variable# list).
export const updateDocTemplate = (
  id: string,
  v: { name?: string; kind?: string; operator?: string; description?: string; file?: File },
) => {
  if (!v.file) return unwrap<DocTemplate>(apiClient.patch(`/doc-templates/${id}/`, v));
  const fd = new FormData();
  if (v.name != null) fd.append("name", v.name);
  if (v.kind != null) fd.append("kind", v.kind);
  if (v.operator != null) fd.append("operator", v.operator);
  if (v.description != null) fd.append("description", v.description);
  fd.append("file", v.file);
  return unwrap<DocTemplate>(apiClient.patch(`/doc-templates/${id}/`, fd, MULTIPART));
};

export const deleteDocTemplate = (id: string) =>
  apiClient.delete(`/doc-templates/${id}/`);

// Historia wgrań pliku szablonu (najnowsza pierwsza) — każda wersja ma własny
// plik do pobrania, więc starsze rewizje nie giną przy podmianie.
export const getDocTemplateVersions = (id: string) =>
  unwrap<DocTemplateVersion[]>(apiClient.get(`/doc-templates/${id}/versions/`));

// ── Anonimizacja PDF — historia jest PRYWATNA (backend filtruje po user) ──────
// `all` (tylko deweloper; backend i tak sprawdza flagę) dokłada cudze wykonania
// — bez wykonań prezesa.
export const getDocAnonymizations = (all = false) =>
  unwrap<DocAnonymization[]>(
    apiClient.get(`/doc-anonymizations/`, all ? { params: { all: true } } : undefined),
  );

export const getDocAnonymizationModels = () =>
  unwrap<{ models: DocAnonymizationModel[] }>(apiClient.get(`/doc-anonymizations/models/`));

export interface CreateDocAnonymizationInput {
  file: File;
  /** „wszystkie" albo zakres jak przy drukowaniu („1-3, 7"). */
  pages: string;
  model: string;
  /** Tryb przyspieszony — backend pomija drugą analizę obrazu i kontrolę wizyjną. */
  fast_mode: boolean;
  /** Wynik zawiera wyłącznie wybrane strony. Przy „wszystkie" (albo zakresie
   *  na cały plik) backend flagę pomija — nie ma czego wycinać. */
  selected_only: boolean;
}

export const createDocAnonymization = (v: CreateDocAnonymizationInput) => {
  const fd = new FormData();
  fd.append("file", v.file);
  fd.append("pages", v.pages);
  fd.append("model", v.model);
  // Multipart niesie tekst — backend parsuje „true"/„false" sam.
  fd.append("fast_mode", v.fast_mode ? "true" : "false");
  fd.append("selected_only", v.selected_only ? "true" : "false");
  return unwrap<DocAnonymization>(apiClient.post(`/doc-anonymizations/`, fd, MULTIPART));
};

/** „Anuluj" na trwającym przebiegu — zatrzymuje zadanie w tle (jak na liście
 *  zadań automatycznych) i oddaje wiersz już ze statusem „Anulowano". */
export const cancelDocAnonymization = (id: string) =>
  unwrap<DocAnonymization>(apiClient.post(`/doc-anonymizations/${id}/cancel/`));

export const deleteDocAnonymization = (id: string) =>
  apiClient.delete(`/doc-anonymizations/${id}/`);

// Availability check: does every #variable# of the template resolve in the context?
export const checkDocTemplate = (templateId: string, contextKind: string, contextId: string) =>
  unwrap<DocTemplateCheck>(
    apiClient.get(`/doc-templates/${templateId}/check/`, {
      params: { context_kind: contextKind, context_id: contextId },
    }),
  );

// Operator (OSD) projektu danego kontekstu generowania — FE dobiera pod niego
// szablony zgód / regulacji / wniosków (dokładny > wspólny 'inny' > fallback).
export const getContextOperator = (contextKind: string, contextId: string) =>
  unwrap<{ operator: string; operator_display: string }>(
    apiClient.get(`/doc-templates/context-operator/`, {
      params: { context_kind: contextKind, context_id: contextId },
    }),
  );

export const generateDocument = (
  templateId: string,
  contextKind: string,
  contextId: string,
  opts?: {
    overrides?: Record<string, string>;
    emptyMode?: "blank" | "marker";
    // nazwy pól, których ręczne wartości mają się też zapisać NA STAŁE do bazy
    persist?: string[];
  },
) =>
  unwrap<{ task_id: string; generated_document_id: string; persisted: string[] }>(
    apiClient.post(`/doc-templates/${templateId}/generate/`, {
      context_kind: contextKind,
      context_id: contextId,
      overrides: opts?.overrides ?? {},
      empty_mode: opts?.emptyMode ?? "blank",
      persist: opts?.persist ?? [],
    }),
  );

// Sekwencja masowa WPW (order-free): jeden przycisk — utwórz pakiety → opisy AI →
// PLAN SYTUACYJNY → formularze → kompilacja, dla zaznaczonych działek.
// Zwraca task_id do pollingu.
export const wpwMassPrepare = (connectionId: string, parcelIds: string[]) =>
  unwrap<{ task_id: string; parcels: number }>(
    apiClient.post(`${BASE}/connections/${connectionId}/wpw-mass-prepare/`, { parcel_ids: parcelIds }),
  );

// Pakiety WPW MASOWE per WŁAŚCICIEL (najnowszy per właściciel, także w przygotowaniu) —
// zakładka WPW MASOWE + automatyczne komplety wejściowe zapotrzebowania WPW.
export type WpwPackageRow = {
  application_id: string; number: string; parcel_id: string;
  owner_id: string | null; owner_name: string | null;
  // pakiet właściciela nieaktualnego (ponowny wypis) — tylko z include_outdated
  owner_outdated?: boolean; owner_outdated_reason?: string;
  compiled: boolean; file_name: string | null;
  // bramka ścieżki standardowej: pakiet skompilowany i aktualny (bez kontroli AI)
  usable?: { usable: boolean; code: string; reason: string };
  // opcjonalna kontrola AI z „Przygotuj i sprawdź" — informacyjnie
  quality?: { ready: boolean; code: string; reason: string; checked_at?: string };
};

/** Pakiet nadaje się do zlecenia: skompilowany i aktualny. `usable` z serwera, a gdy
 *  starszy backend go nie zwraca — sam fakt kompilacji. Kontrola AI nie jest warunkiem. */
export const isWpwPackageUsable = (pkg: WpwPackageRow | undefined): boolean =>
  !!pkg && (pkg.usable ? pkg.usable.usable : pkg.compiled);
export const getWpwPackages = (connectionId: string, includeOutdated = false) =>
  unwrap<WpwPackageRow[]>(apiClient.get(`${BASE}/connections/${connectionId}/wpw-packages/`, {
    params: includeOutdated ? { include_outdated: "1" } : {},
  }));

// Ustaw SAM oddział klienta przedsięwzięcia (klient bez zmian) — z okna generowania,
// gdy szablon używa pól oddziału, a projekt go nie ma.
export const setProjectBranch = (connectionId: string, branchId: string) =>
  unwrap<unknown>(
    apiClient.patch(`${BASE}/connections/${connectionId}/project-branch/`, {
      client_branch: branchId,
    }),
  );

// Policz zmienną AI „na żywo" dla kontekstu — bez czytania zapisanej wartości
// i bez zapisu; wynik trafia do pola ręcznego okna generowania.
export const computeDocAiValue = (name: string, contextKind: string, contextId: string) =>
  unwrap<{ name: string; value: string }>(
    apiClient.post(`/doc-templates/ai-value/`, {
      name, context_kind: contextKind, context_id: contextId,
    }),
  );

// Pełna historia generatów jednego kontekstu (najnowszy pierwszy) — każde
// generowanie zostawia wiersz z plikiem, nic nie jest nadpisywane.
export const getGeneratedHistory = (contextKind: string, contextId: string) =>
  unwrap<GeneratedDoc[]>(
    apiClient.get(`/doc-templates/generated-history/`, {
      params: { context_kind: contextKind, context_id: contextId },
    }),
  );

export const getGeneratedDocs = (contextKind: string, contextIds: string[]) =>
  unwrap<GeneratedDoc[]>(
    apiClient.get(`/doc-templates/generated/`, {
      params: { context_kind: contextKind, context_ids: contextIds.join(",") },
    }),
  );

// ── TOM II — Tytuły prawne do nieruchomości (generacja na poziomie projektu) ──
// Kontekst generacji `ze_tom_titles`; historia jak wszędzie —
// getGeneratedHistory("ze_tom_titles", connectionId).
export const TOM_TITLES_CONTEXT = "ze_tom_titles";

/** Stan karty TOM II: bramka, szablony, raport kompletności i ostatni generat. */
export const getTomTitlesOverview = (connectionId: string) =>
  unwrap<TomTitlesOverview>(apiClient.get(`${BASE}/connections/${connectionId}/tom-tytuly/`));

/** Zleca złożenie tomu w tle — 202 z id zadania i wiersza historii. */
export const generateTomTitles = (connectionId: string, v?: { template_id?: string }) =>
  unwrap<{ task_id: string; generated_document_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/generate-tom-tytuly/`, v ?? {}),
  );

// ── Załączniki do wniosku regulacyjnego (checklist + ZIP) ────────────────────
export const getRegulatoryChecklist = (parcelId: string) =>
  unwrap<RegulatoryChecklist>(
    apiClient.get(`${BASE}/regulatory-attachments/checklist/`, { params: { parcel: parcelId } }),
  );

export const uploadRegulatoryAttachment = (v: {
  parcel?: string;
  connection?: string;
  kind: string;
  file: File;
  note?: string;
}) => {
  const fd = new FormData();
  if (v.parcel) fd.append("parcel", v.parcel);
  if (v.connection) fd.append("connection", v.connection);
  fd.append("kind", v.kind);
  fd.append("file", v.file);
  if (v.note) fd.append("note", v.note);
  return unwrap<RegulatoryAttachment>(
    apiClient.post(`${BASE}/regulatory-attachments/`, fd, MULTIPART),
  );
};

export const deleteRegulatoryAttachment = (id: string) =>
  apiClient.delete(`${BASE}/regulatory-attachments/${id}/`);

// The ZIP endpoints are authenticated Django views (not presigned S3 links), so fetch
// them as blobs through the API client (Bearer header) and trigger a client-side
// download, taking the filename from Content-Disposition.
export const downloadRegulatoryZip = async (parcelId: string) => {
  const resp = await apiClient.get(`${BASE}/regulatory-attachments/zip/`, {
    params: { parcel: parcelId },
    responseType: "blob",
  });
  saveBlobResponse(resp, "zalaczniki_wniosek_regulacyjny.zip");
};

// Wszystkie zdjęcia MDCP projektu jako jeden ZIP (pliki PUNKT_<nr>__ZDJ_<i>).
export const downloadMdcpPhotosZip = async (connectionId: string) => {
  const resp = await apiClient.get(`${BASE}/connections/${connectionId}/mdcp/photos-zip/`, {
    responseType: "blob",
  });
  saveBlobResponse(resp, "MDCP_zdjecia.zip");
};

// Punkty zdjęciowe zamówienia MDCP do pliku dla geodety: `csv` = zestawienie (Nr, X/Y w PUWG
// 2000 strefy projektu, azymut, WGS84, uwagi), `dxf` = punkty naniesione w układzie projektu
// ze strzałkami azymutu. Nazwa pliku przychodzi z serwera (numer zamówienia).
export const downloadMdcpOrderPoints = async (orderId: string, fmt: "csv" | "dxf") => {
  const resp = await apiClient.get(`${BASE}/mdcp/order/${orderId}/points/export/${fmt}/`, {
    responseType: "blob",
  });
  saveBlobResponse(resp, `MDCP_punkty.${fmt}`);
};

// Pliki zakładki „Pliki" jako ZIP ze strukturą katalogów z drzewa folderów.
// Bez folderSlug — całe drzewo; z folderSlug — poddrzewo jednego folderu.
export const downloadProjectFolderZip = async (connectionId: string, folderSlug?: string) => {
  const resp = await apiClient.get(`${BASE}/connections/${connectionId}/folders/zip/`, {
    params: folderSlug ? { folder: folderSlug } : undefined,
    responseType: "blob",
  });
  saveBlobResponse(resp, folderSlug ? `Pliki__${folderSlug}.zip` : "Pliki.zip");
};

// ── Formatki (plan sytuacyjny — DXF paper-space layouts) ─────────────────────
// Frames travel EXCLUSIVELY in project-SRID meters (SheetFrame.center) — the
// backend puts the same numbers into the DXF viewport, no reprojection anywhere.
export const getFormatkaTemplates = () =>
  unwrap<FormatkaTemplate[]>(apiClient.get(`${BASE}/formatka-templates/`));

export const getFormatkaVariables = () =>
  unwrap<DocVariableGroup[]>(apiClient.get(`${BASE}/formatka-templates/variables/`));

// Siatka domyślnych szablonów: ENERGA/TAURON × działki WPW/urzędowe. Odczyt dla
// wszystkich (front pokazuje, co się dobierze), zapis tylko dyrektor/CEO.
export const getFormatkaDefaults = () =>
  unwrap<FormatkaDefaultCell[]>(apiClient.get(`${BASE}/formatka-templates/defaults/`));

// Zapis POST-em, nie PUT-em: ViewSet szablonów nie dopuszcza PUT (chroni przed
// pełnym update'em szablonu z pominięciem kontroli autorstwa).
export const saveFormatkaDefaults = (
  cells: { operator: string; scope: FormatkaScope; template: string | null }[],
) =>
  unwrap<FormatkaDefaultCell[]>(
    apiClient.post(`${BASE}/formatka-templates/defaults/`, { cells }),
  );

// Template registry CRUD (mirror doc-templates). `is_company` (global) is
// director/CEO-only server-side. The create/patch response carries the scan's
// `layer_warnings` (layers without the FORMATKA_ prefix — informational).
export const createFormatkaTemplate = (v: {
  name: string;
  description?: string;
  is_company?: boolean;
  operator?: string;
  file: File;
}) => {
  const fd = new FormData();
  fd.append("name", v.name);
  if (v.description) fd.append("description", v.description);
  if (v.is_company) fd.append("is_company", "true");
  if (v.operator) fd.append("operator", v.operator);
  fd.append("file", v.file);
  return unwrap<FormatkaTemplate>(apiClient.post(`${BASE}/formatka-templates/`, fd, MULTIPART));
};

export const updateFormatkaTemplate = (
  id: string,
  // `paper` = ręczny override orientacji (tylko 4 standardowe wartości) — wygrywa
  // ze skanem pliku, także gdy w tym samym PATCH-u idzie nowy DXF.
  v: { name?: string; description?: string; operator?: string; paper?: FormatkaPaper; file?: File },
) => {
  if (!v.file) return unwrap<FormatkaTemplate>(apiClient.patch(`${BASE}/formatka-templates/${id}/`, v));
  const fd = new FormData();
  if (v.name != null) fd.append("name", v.name);
  if (v.description != null) fd.append("description", v.description);
  if (v.operator != null) fd.append("operator", v.operator);
  if (v.paper != null) fd.append("paper", v.paper);
  fd.append("file", v.file);
  return unwrap<FormatkaTemplate>(apiClient.patch(`${BASE}/formatka-templates/${id}/`, fd, MULTIPART));
};

export const deleteFormatkaTemplate = (id: string) =>
  apiClient.delete(`${BASE}/formatka-templates/${id}/`);

// Historia wgrań pliku DXF szablonu (najnowsza pierwsza) — każda wersja ma
// własny plik do pobrania, więc starsze rewizje nie giną przy podmianie.
export const getFormatkaTemplateVersions = (id: string) =>
  unwrap<FormatkaTemplateVersion[]>(
    apiClient.get(`${BASE}/formatka-templates/${id}/versions/`),
  );

export const checkFormatkaTemplate = (templateId: string, sheetSetId: string) =>
  unwrap<FormatkaTemplateCheck>(
    apiClient.get(`${BASE}/formatka-templates/${templateId}/check/`, {
      params: { sheet_set: sheetSetId },
    }),
  );

export const getSheetSets = (connection: string) =>
  unwrap<FormatkaSheetSet[]>(
    apiClient.get(`${BASE}/formatka-sheet-sets/`, { params: { connection } }),
  );

export const getSheetSet = (id: string) =>
  unwrap<FormatkaSheetSet>(apiClient.get(`${BASE}/formatka-sheet-sets/${id}/`));

// Creates a DRAFT and synchronously seeds frames (parcel → 1/parcel, project →
// grid over the route, custom → courtesy seed). `skipped_parcels` lists TERYTs
// without geometry the seed had to skip.
export const createSheetSet = (v: {
  connection: string;
  route_version: string;
  mode: FormatkaMode;
  template?: string;
  parcels?: string[];
  scale_denominator?: number;
  formatka_type?: FormatkaType;
  number_adjacent_sheets?: boolean;
  include_orientation?: boolean;
  // Nazwa nadana od razu (picker urzędowy zakłada po zestawie na urząd i podpisuje
  // każdy nazwą urzędu) — bez osobnego PATCH-a.
  name?: string;
}) =>
  unwrap<FormatkaSheetSet & { skipped_parcels?: string[] }>(
    apiClient.post(`${BASE}/formatka-sheet-sets/`, v),
  );

// Wsad „działki prywatne": po jednym zestawie na działkę w JEDNYM żądaniu. Działka bez
// naszych obiektów na wskazanej wersji trasy nie urywa serii — dostaje wiersz ze
// statusem „failure" (bez ramek, powód w `error`) obok pozostałych, a kolejne działki
// jadą dalej. `sets` wraca w kolejności działek.
export const createSheetSetsPerParcel = (v: {
  connection: string;
  route_version: string;
  parcels: string[];
  template?: string;
  scale_denominator?: number;
  formatka_type?: FormatkaType;
  number_adjacent_sheets?: boolean;
  include_orientation?: boolean;
}) =>
  unwrap<{
    sets: FormatkaSheetSet[];
    ok_count: number;
    failed_count: number;
    failed_parcels: string[];
    // Działki bez naszych obiektów (także w pasie 1 m przy granicy): dostały arkusz na
    // samej działce i ostrzeżenie na wierszu — nie błąd.
    empty_parcels: string[];
  }>(apiClient.post(`${BASE}/formatka-sheet-sets/per-parcel/`, v));

// Ponów rozplanowanie zestawu z błędem (bez ramek) — tylko tej działki, opcjonalnie
// na innej wersji trasy. 200 w OBU przypadkach: `ok` mówi, czy ramki się znalazły
// (status wraca do „draft"); gdy nie — wiersz zostaje w „failure" ze świeżym powodem.
export const retrySheetSet = (id: string, routeVersion?: string) =>
  unwrap<FormatkaSheetSet & { ok: boolean; skipped_parcels?: string[] }>(
    apiClient.post(
      `${BASE}/formatka-sheet-sets/${id}/retry/`,
      routeVersion ? { route_version: routeVersion } : {},
    ),
  );

export const updateSheetSet = (
  id: string,
  v: {
    name?: string; template?: string | null; field_values?: Record<string, string>;
    scale_denominator?: number; include_orientation?: boolean;
    formatka_type?: FormatkaType; number_adjacent_sheets?: boolean;
    // Ukryj (true, z notatką) / przywróć (false) ostrzeżenia generatora na karcie.
    warnings_dismissed?: boolean; warnings_dismissed_note?: string;
  },
) => unwrap<FormatkaSheetSet>(apiClient.patch(`${BASE}/formatka-sheet-sets/${id}/`, v));

// Full replace of the arrangement; the response carries renumbered frames.
export const saveSheetFrames = (id: string, frames: SheetFrame[]) =>
  unwrap<FormatkaSheetSet>(
    apiClient.put(`${BASE}/formatka-sheet-sets/${id}/frames/`, { frames }),
  );

export const reseedSheetSet = (id: string) =>
  unwrap<FormatkaSheetSet & { skipped_parcels?: string[] }>(
    apiClient.post(`${BASE}/formatka-sheet-sets/${id}/reseed/`, {}),
  );

export const generateSheetSet = (id: string) =>
  unwrap<{ task_id: string; sheet_set_id: string }>(
    apiClient.post(`${BASE}/formatka-sheet-sets/${id}/generate/`, {}),
  );

export const deleteSheetSet = (id: string) =>
  apiClient.delete(`${BASE}/formatka-sheet-sets/${id}/`);

// Upload the per-sheet PDFs plotted from the ZIP → backend merges (pypdf) into a
// new archived version of PLAN_SYTUACYJNY.pdf.
export const uploadSheetSetPdfs = (id: string, files: File[]) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return unwrap<FormatkaSheetSet>(apiClient.post(`${BASE}/formatka-sheet-sets/${id}/upload-pdfs/`, fd, MULTIPART));
};

// Serwerowe scalenie: renderuje arkusze z DXF-a w paczce prosto do scalonego PDF-a,
// bez wychodzenia po projektanta z CAD-em. Wchodzi tą samą drogą co wgrany plot,
// więc wersjonowanie i mapa stron są identyczne. Akcja istniała od dawna, ale nie
// miała w aplikacji ANI JEDNEGO wywołania — jedyną drogą do PDF-a był ręczny plot.
export const renderSheetSetPdf = (id: string, dpi?: number) =>
  unwrap<FormatkaSheetSet>(
    apiClient.post(`${BASE}/formatka-sheet-sets/${id}/render-pdf/`, dpi ? { dpi } : {}),
  );

// Technical-director override: replace the merged PDF with one uploaded file
// (new archived version).
export const overwriteSheetSetPdf = (id: string, file: File, note?: string) => {
  const fd = new FormData();
  fd.append("file", file);
  if (note) fd.append("note", note);
  return unwrap<FormatkaSheetSet>(apiClient.post(`${BASE}/formatka-sheet-sets/${id}/overwrite-pdf/`, fd, MULTIPART));
};

export const getFormatkaFieldDefaults = () =>
  unwrap<FormatkaFieldDefault[]>(apiClient.get(`${BASE}/formatka-field-defaults/`));

// ── wartości pól tabelki zapamiętane per projekt ────────────────────────────────
export const getFormatkaProjectFields = (connectionId: string) =>
  unwrap<{ id: string; connection: string; key: string; value: string }[]>(
    apiClient.get(`${BASE}/formatka-project-fields/`, { params: { connection: connectionId } }),
  );

export const saveFormatkaProjectFields = (connectionId: string, values: Record<string, string>) =>
  unwrap<{ id: string; key: string; value: string }[]>(
    apiClient.post(`${BASE}/formatka-project-fields/`, { connection: connectionId, values }),
  );

// Podział #zmiennych# szablonu na auto (wypełnią się same: data, numeracja/skala
// per arkusz, dane projektu, dzialka_*) i ręczne — dla modala szybkiej generacji
// z wniosku (bez istniejącego zestawu).
export const getFormatkaManualFields = (templateId: string, connectionId: string) =>
  unwrap<{ manual: { name: string; value: string }[]; auto: string[] }>(
    apiClient.get(`${BASE}/formatka-templates/${templateId}/manual-fields/`, {
      params: { connection: connectionId },
    }),
  );

// Combine several finished sheet sets (same route + shared template) into ONE
// persistent bundle set (mode=BUNDLE) and start its generation. Returns the new
// bundle set (with its generation task_id) — it behaves like any other set:
// one ZIP, one upload/merge target, versioning, INDEPENDENT per-działka numbering.
export const bundleSheetSets = (ids: string[]) =>
  unwrap<FormatkaSheetSet & { task_id?: string }>(
    apiClient.post(`${BASE}/formatka-sheet-sets/bundle/`, { ids }),
  );

// ── cross-project działka export ("Pobierz listę działek") ─────────────────────
// POST the current filters so each option list is narrowed by the OTHER filters (cascading).
export const getParcelExportFacets = (filters: ParcelExportFilters = {}) =>
  unwrap<ParcelExportFacets>(apiClient.post(`${BASE}/parcels/export-facets/`, filters));

export const previewParcelExport = (filters: ParcelExportFilters) =>
  unwrap<ParcelExportPreview>(apiClient.post(`${BASE}/parcels/export-preview/`, filters));

export const startParcelExport = (filters: ParcelExportFilters) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/parcels/export-csv/`, filters));

// Globalna strona "Działki" — paginowana lista działek ze wszystkich projektów ZE,
// filtrowana tym samym kształtem filtrów co eksport CSV.
export const getGlobalParcels = (req: import("./types").GlobalParcelListRequest) =>
  unwrap<import("./types").GlobalParcelList>(
    apiClient.post(`${BASE}/parcels/global-list/`, req),
  );

// „Pobierz działki (.xlsx)" na stronie "Działki" — zadanie w tle buduje arkusz ze
// WSZYSTKICH działek spełniających bieżące filtry (nie tylko z widocznej strony),
// w aktualnym sortowaniu. Postęp śledzi useTaskPolling; wynik = ParcelExportResult.
// variant "address" = wykaz adresowy (Identyfikator TERYT + obręb/numer + adres
// rozbity na Miejscowość / Ulica / Nr domu / Kod pocztowy).
// file_format "csv" = te same kolumny jako CSV (średniki + BOM) zamiast arkusza.
export const startGlobalParcelsXlsx = (req: {
  filters: ParcelExportFilters;
  ordering?: string;
  variant?: "default" | "address";
  file_format?: "xlsx" | "csv";
}) => unwrap<{ task_id: string }>(apiClient.post(`${BASE}/parcels/export-xlsx/`, req));

// ── globalna strona "Zadania KPI" ──────────────────────────────────────────────
// Zakres (moje projekty vs cały portfel) rozstrzyga backend — FE nie wysyła żadnego
// przełącznika, dostaje tylko `scope` w odpowiedzi.
export const getGlobalKpiFacets = (filters: import("./types").GlobalKpiFilters = {}) =>
  unwrap<import("./types").GlobalKpiFacets>(
    apiClient.post(`${BASE}/kpi-action-items/global-facets/`, filters),
  );

export const getGlobalKpiTasks = (req: import("./types").GlobalKpiListRequest) =>
  unwrap<import("./types").GlobalKpiList>(
    apiClient.post(`${BASE}/kpi-action-items/global-list/`, req),
  );

// ── „Analiza zmian" — rozliczenie osoby względem wybranej chwili ───────────────
// Widok dyrekcji technicznej / CEO; pozostałym backend zwraca 403.
export const getChangeAnalysisPeople = () =>
  unwrap<{ people: import("./types").ChangeAnalysisPerson[] }>(
    apiClient.get(`${BASE}/change-analysis/people/`),
  );

export const getChangeAnalysis = (req: import("./types").ChangeAnalysisRequest) =>
  unwrap<import("./types").ChangeAnalysisResult>(
    apiClient.post(`${BASE}/change-analysis/`, req),
  );

// ── masowe pakiety WPW (formularze ENERGA) ─────────────────────────────────────
// Kontekst strony masowej: istniejące zlecenie WPW albo — sekwencja bez zlecenia —
// projekt (connection). Zlecenie powstaje wtedy dopiero w kroku 5.
export type WpwMassCtx = { order: string } | { connection: string };

export const getWpwMassOverview = (ctx: WpwMassCtx) =>
  unwrap<import("./types").WpwMassOverview>(
    apiClient.get(`${BASE}/wpw-mass/overview/`, { params: ctx }),
  );

// Rozkład #zmiennych# szablonów ENERGA działki: wypełnione / puste / nieosiągalne,
// każda ze źródłem (gdzie uzupełnić: działka / klient / projekt / metadane).
export type WpwParcelFields = {
  parcel_id: string;
  kinds: ({ kind: string; template_name: string | null } & DocTemplateCheck)[];
  applied?: string[];
  // pola z wartością, których NIE zapisano (liczone przez AI / rejestr wspólny) —
  // zamiast po cichu je zgubić, front ostrzega (opis AI edytuj przez „Opis urządzeń")
  dropped?: string[];
};

// Pola są per WŁAŚCICIEL (WNU) — `owner` wskazuje, którego właściciela WNU sprawdzamy.
export const getWpwMassParcelFields = (parcelId: string, owner?: string) =>
  unwrap<WpwParcelFields>(apiClient.get(`${BASE}/wpw-mass/parcels/${parcelId}/fields/`,
    owner ? { params: { owner } } : undefined));

// Utrwala ręczne wartości #zmiennych# szablonów WNU (właściciela) w bazie i zwraca świeży
// rozkład — ten sam mechanizm co „zapisz na stałe" w generatorze.
export const persistWpwMassParcelFields = (parcelId: string, values: Record<string, string>, owner?: string) =>
  unwrap<WpwParcelFields>(
    apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/fields/`, { values, ...(owner ? { owner } : {}) }),
  );

// Odpowiedź PATCH-a działki: tylko pola, które backend faktycznie zapisał (kubełki
// przycięte, puste wyrzucone, źródła przeliczone) — trafiają PROSTO do wierszy overview.
export interface WpwMassParcelPatchResult {
  consent_work_descriptions?: Record<string, string>;
  consent_description_sources?: Record<string, "manual" | "ai" | "review">;
  energa_work_description?: string;
  form_kinds?: string[];
}

export const patchWpwMassParcel = (
  parcelId: string,
  v: {
    consent_work_descriptions?: Record<string, string>;
    energa_work_description?: string;
    form_kinds?: string[];
  },
) => unwrap<WpwMassParcelPatchResult>(apiClient.patch(`${BASE}/wpw-mass/parcels/${parcelId}/`, v));

// ── zakres prac z etykiet CAD: zatwierdzenie wątpliwości przez człowieka ─────
// confirm=true zatwierdza BIEŻĄCY zestaw wątpliwości (i, gdy podano form_kinds,
// ustawia dobór ręczny na wszystkich WNU działki); confirm=false cofa zatwierdzenie.
export const confirmWpwScope = (
  parcelId: string,
  v: { confirm: boolean; form_kinds?: string[]; note?: string },
) => unwrap<{ scope_review: WpwMassScopeReview; form_kinds?: string[] }>(
  apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/scope-confirm/`, v),
);

// ── zakres zgód działki: bramka KW + wybór, kto jedzie w teren ───────────────
// Obie akcje zwracają ŚWIEŻY zakres zgód tej działki, żeby kreator nie musiał
// przeładowywać całego overview po każdym kliknięciu.
// `skip: true` — pomiń POMIJALNĄ blokadę (błąd analizy KW) z powodem nadanym przez
// backend; przy blokadzie niepomijalnej albo jej braku backend odpowiada 400.
export const setWpwKwGateOverride = (
  parcelId: string,
  v: { reason: string } | { clear: true } | { skip: true },
) => unwrap<WpwConsentScope>(
  apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/kw-gate-override/`, v),
);

// `owners` to WSZYSCY właściciele jednostki zgody — małżeństwo ma dwa WNU i musi
// wyjść (albo wrócić) w całości.
export const setWpwFieldExclusion = (
  parcelId: string,
  v: { owners: string[]; excluded: boolean; reason?: string },
) => unwrap<WpwConsentScope>(
  apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/field-exclusion/`, v),
);

export const wpwMassEnsureApplications = (ctx: WpwMassCtx, parcelIds: string[]) =>
  unwrap<{ created: { parcel_id: string; number: string }[]; existing: { parcel_id: string; number: string }[] }>(
    apiClient.post(`${BASE}/wpw-mass/bulk-ensure-applications/`, { ...ctx, parcel_ids: parcelIds }),
  );

const wpwMassTask = (path: string) => (ctx: WpwMassCtx, parcelIds: string[], extra?: Record<string, unknown>) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/wpw-mass/${path}/`, { ...ctx, parcel_ids: parcelIds, ...(extra ?? {}) }),
  );

export const wpwMassDescribe = wpwMassTask("bulk-describe");
export const wpwMassGenerateForms = wpwMassTask("bulk-generate-forms");
export const wpwMassCompile = wpwMassTask("bulk-compile");
export const wpwMassHandoff = wpwMassTask("bulk-handoff");

// ── dodatkowe strony A4 pakietu (przed formatkami A3) ─────────────────────────
export type WpwMassExtraFilesResponse = {
  connection_id: string;
  files: import("./types").WpwMassExtraFile[];
  created?: string[];
};
export const getWpwMassExtraFiles = (ctx: WpwMassCtx) =>
  unwrap<WpwMassExtraFilesResponse>(apiClient.get(`${BASE}/wpw-mass/extra-files/`, { params: ctx }));
// Wiele plików naraz; domyślnie dla wszystkich działek, albo tylko dla `parcelIds`.
// `placement` domyślnie "owner" (egzemplarz zostający u właściciela).
export const uploadWpwMassExtraFiles = (
  ctx: WpwMassCtx, files: File[],
  opts: { parcelIds?: string[]; placement?: import("./types").WpwExtraPagePlacement } = {},
) => {
  const fd = new FormData();
  Object.entries(ctx).forEach(([k, v]) => fd.append(k, v));
  files.forEach((f) => fd.append("files", f));
  if (opts.parcelIds) {
    fd.append("all_parcels", "false");
    opts.parcelIds.forEach((id) => fd.append("parcel_ids", id));
  }
  if (opts.placement) fd.append("placement", opts.placement);
  return unwrap<WpwMassExtraFilesResponse>(apiClient.post(`${BASE}/wpw-mass/extra-files/`, fd, MULTIPART));
};
export const patchWpwMassExtraFile = (
  id: string,
  body: {
    label?: string; all_parcels?: boolean; parcel_ids?: string[]; position?: number;
    placement?: import("./types").WpwExtraPagePlacement;
  },
) => unwrap<WpwMassExtraFilesResponse>(apiClient.patch(`${BASE}/wpw-mass/extra-files/${id}/`, body));
export const deleteWpwMassExtraFile = (id: string) =>
  unwrap<WpwMassExtraFilesResponse>(apiClient.delete(`${BASE}/wpw-mass/extra-files/${id}/`));

// ── trwały szkic sekwencji (postęp kreatora: wybrane działki + krok) ──────────
// status: active = w toku; completed = krok 5 „Zakończ bez zamówienia" (pakiety zapisane,
// kreator wraca do niej później — nowe zamówienie albo wniosek o zmianę istniejącego).
export type WpwSequenceStatus = "active" | "completed";
export type WpwSequenceState = {
  id: string; selected_parcels: string[]; step: string;
  status: WpwSequenceStatus; completed_at: string | null;
};

export const saveWpwSequence = (
  connection: string,
  v: { selected_parcel_ids?: string[]; step?: string },
) => unwrap<WpwSequenceState>(
  apiClient.post(`${BASE}/wpw-mass/sequence/`, { connection, ...v }),
);

/** Domknięcie sekwencji z kroku 5: `order` = utworzono zamówienie (→ sfinalizowana);
 *  `without_order` = „Zakończ bez zamówienia" (→ zakończona, `selected_parcel_ids`
 *  utrwala ostatnie zaznaczenie). Idempotentne — bez otwartej sekwencji finalized=false. */
export const finalizeWpwSequence = (
  connection: string,
  v: { order?: string; without_order?: boolean; selected_parcel_ids?: string[] } = {},
) =>
  unwrap<{ finalized: boolean; status: WpwSequenceStatus | "finalized" | null; completed_at: string | null }>(
    apiClient.post(`${BASE}/wpw-mass/sequence/finalize/`, { connection, ...v }),
  );

export type WpwSequenceRow = {
  id: string;
  connection_id: string;
  project_id: string | null;
  project_name: string;
  selected_count: number;
  wnu_draft_count: number;
  step: string;
  has_sequence: boolean;
  /** completed = zakończona bez zamówienia (krok 5) — czeka na zlecenie / wniosek o zmianę */
  status?: WpwSequenceStatus;
  completed_at?: string | null;
  /** zgłoszone (niezatwierdzone) zapotrzebowania WPW na działkach projektu — sekwencja czeka na akceptację */
  pending_requisitions?: string[];
  author: string;
  updated_at: string | null;
};

export type WpwFinalizedSequenceRow = {
  id: string;
  connection_id: string;
  project_id: string | null;
  project_name: string;
  order_id: string | null;
  order_number: string;
  selected_count: number;
  author: string;
  finalized_at: string;
};

export const getWpwSequences = () =>
  unwrap<{ sequences: WpwSequenceRow[]; finalized: WpwFinalizedSequenceRow[] }>(
    apiClient.get(`${BASE}/wpw-mass/sequences/`),
  );

// ── krok 4: dobór mapy / wypisu + korekta wygenerowanych formularzy ───────────
export type WpwMapCandidate = {
  sheet_set_id: string; name: string; template_name: string;
  merged_pdf_id: string | null; needs_pdf: boolean;
  zip_url: string | null; zip_name: string | null;
  covered_teryts: string[]; covers_all: boolean;
  stale: boolean; stale_reason: string; created_at: string;
};
export type WpwMapAttached = {
  map_id: string; label: string; file_id: string; sheet_set_id: string | null;
  stale: boolean; stale_reason: string; hand_uploaded: boolean;
};
export type WpwReadiness =
  | { ok: true; [k: string]: unknown }
  | { ok: false; code: string; reason: string };

export const getWpwMapOptions = (parcelId: string) =>
  unwrap<{ parcel_id: string; attached: WpwMapAttached[]; candidates: WpwMapCandidate[] }>(
    apiClient.get(`${BASE}/wpw-mass/parcels/${parcelId}/map-options/`),
  );
export const attachWpwMap = (parcelId: string, body: { sheet_set_id?: string; auto?: boolean; accept_stale?: boolean }) =>
  unwrap<WpwReadiness>(apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/map-attach/`, body));
// „Użyj mimo to" na już podpiętej, nieaktualnej mapie — bez ponownego doboru formatki.
export const acceptWpwStaleMap = (parcelId: string) =>
  unwrap<WpwReadiness>(apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/map-accept-stale/`, {}));

export type WpwWypisOption = { wypis_id: string; filename: string; page_count: number | null; pages: string };
export const getWpwWypisOptions = (parcelId: string) =>
  unwrap<{ parcel_id: string; options: WpwWypisOption[] }>(
    apiClient.get(`${BASE}/wpw-mass/parcels/${parcelId}/wypis-options/`),
  );
export const assignWpwWypis = (parcelId: string, wypisId: string, pages: string) =>
  unwrap<WpwReadiness>(
    apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/wypis-assign/`, { wypis_id: wypisId, pages }),
  );
export const uploadWpwWypis = (parcelId: string, file: File, pages: string) => {
  const fd = new FormData();
  fd.append("file", file);
  if (pages) fd.append("pages", pages);
  return unwrap<WpwReadiness>(apiClient.post(`${BASE}/wpw-mass/parcels/${parcelId}/wypis-upload/`, fd, MULTIPART));
};
export const replaceWpwFormOdt = (docId: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<{ id: string; kind: string; version: number; status: string; error: string;
    file: import("./types").ManagedFileMini | null; odt_file: import("./types").ManagedFileMini | null }>(
    apiClient.post(`${BASE}/wpw-mass/form-documents/${docId}/replace-odt/`, fd, MULTIPART),
  );
};

// ── zwroty WPW z terenu ────────────────────────────────────────────────────────
export const getWpwReturnBatches = (params: { connection?: string; order?: string }) =>
  unwrap<import("./types").WpwReturnBatch[]>(apiClient.get(`${BASE}/wpw-returns/`, { params }));

export const getWpwReturnBatch = (id: string) =>
  unwrap<import("./types").WpwReturnBatch>(apiClient.get(`${BASE}/wpw-returns/${id}/`));

export const uploadWpwReturnBatch = (orderId: string, file: File) => {
  const fd = new FormData();
  fd.append("order", orderId);
  fd.append("file", file);
  return unwrap<import("./types").WpwReturnBatch>(apiClient.post(`${BASE}/wpw-returns/`, fd, MULTIPART));
};

export const segmentWpwReturnBatch = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/wpw-returns/${id}/segment/`, {}));

export const patchWpwReturnItem = (
  itemId: string,
  v: { decision?: "accept" | "reject" | ""; note?: string; parcel_id?: string; pages?: string },
) => unwrap<import("./types").WpwReturnItem>(apiClient.patch(`${BASE}/wpw-returns/items/${itemId}/`, v));

// Preview streams a PDF slice with the auth header — fetch as a Blob and let the
// caller mint an object URL (window.open on a raw URL would drop the JWT).
export const fetchWpwReturnItemPreview = (itemId: string) =>
  apiClient
    .get<Blob>(`${BASE}/wpw-returns/items/${itemId}/preview/`, { responseType: "blob" })
    .then((r) => r.data);

export const applyWpwReturnBatch = (id: string) =>
  unwrap<{
    applied: { item_id: string; number: string }[];
    rejected_logged: { item_id: string; teryt: string }[];
    errors: { item_id: string; error: string }[];
    pending: number;
    batch_status: string;
  }>(apiClient.post(`${BASE}/wpw-returns/${id}/apply/`, {}));

export const cancelWpwReturnBatch = (id: string) =>
  unwrap<import("./types").WpwReturnBatch>(apiClient.post(`${BASE}/wpw-returns/${id}/cancel/`, {}));

// ── bg task polling ──────────────────────────────────────────────────────────
export const getTask = (id: string) =>
  unwrap<AsyncTaskRecord>(apiClient.get(`/bg-tasks/${id}/`));

// ── Raporty okresowe do ZE ─────────────────────────────────────────────────────
export const getReports = (connection: string) =>
  unwrap<ZEReport[]>(apiClient.get(`${BASE}/reports/`, { params: { connection, page_size: 500 } }));

export const getReportSchedule = (connection: string) =>
  unwrap<ReportSchedule>(apiClient.get(`${BASE}/reports/schedule/`, { params: { connection } }));

export const createReport = (payload: {
  connection: string;
  title: string;
  period_due?: string | null;
  notes?: string;
  files?: File[];
}) => {
  const fd = new FormData();
  fd.append("connection", payload.connection);
  fd.append("title", payload.title);
  if (payload.period_due) fd.append("period_due", payload.period_due);
  if (payload.notes) fd.append("notes", payload.notes);
  (payload.files ?? []).forEach((f) => fd.append("files", f));
  return unwrap<ZEReport>(apiClient.post(`${BASE}/reports/`, fd, MULTIPART));
};

export const updateReport = (id: string, payload: Partial<ZEReport>) =>
  unwrap<ZEReport>(apiClient.patch(`${BASE}/reports/${id}/`, payload));

export const addReportFiles = (id: string, files: File[]) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return unwrap<ZEReport>(apiClient.post(`${BASE}/reports/${id}/files/`, fd, MULTIPART));
};

export const markReportSent = (id: string, sent_at?: string) =>
  unwrap<ZEReport>(apiClient.post(`${BASE}/reports/${id}/mark-sent/`, sent_at ? { sent_at } : {}));

export const unmarkReportSent = (id: string) =>
  unwrap<ZEReport>(apiClient.post(`${BASE}/reports/${id}/unmark-sent/`, {}));

export const deleteReport = (id: string) => apiClient.delete(`${BASE}/reports/${id}/`);

/** ARON pisze treść raportu za okres i składa ją w edytowalnym .docx (w tle).
 *  `formats` wybiera postać dokumentu — pismo do maila, formatka operatora albo obie. */
export const generateReportDraft = (id: string, formats: ZEDocFormat[] = ["letter"]) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/reports/${id}/generate-draft/`, { formats }),
  );

// ── Wnioski o aneks do umowy z ZE ─────────────────────────────────────────────
export const getAnnexRequests = (connection: string) =>
  unwrap<ZEAnnexRequest[]>(
    apiClient.get(`${BASE}/annex-requests/`, { params: { connection, page_size: 200 } }),
  );

export const createAnnexRequest = (payload: {
  connection: string;
  scope?: ZEAnnexScope;
  proposed_deadline?: string | null;
  notes?: string;
}) => unwrap<ZEAnnexRequest>(apiClient.post(`${BASE}/annex-requests/`, payload));

export const updateAnnexRequest = (id: string, payload: Partial<ZEAnnexRequest>) =>
  unwrap<ZEAnnexRequest>(apiClient.patch(`${BASE}/annex-requests/${id}/`, payload));

export const deleteAnnexRequest = (id: string) =>
  apiClient.delete(`${BASE}/annex-requests/${id}/`);

export const addAnnexFiles = (id: string, files: File[]) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return unwrap<ZEAnnexRequest>(apiClient.post(`${BASE}/annex-requests/${id}/files/`, fd, MULTIPART));
};

/** ARON pisze uzasadnienie i składa formularz wniosku o aneks (w tle). */
export const generateAnnexDraft = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/annex-requests/${id}/generate-draft/`, {}));

export const markAnnexSent = (id: string, sent_at?: string) =>
  unwrap<ZEAnnexRequest>(
    apiClient.post(`${BASE}/annex-requests/${id}/mark-sent/`, sent_at ? { sent_at } : {}),
  );

export const unmarkAnnexSent = (id: string) =>
  unwrap<ZEAnnexRequest>(apiClient.post(`${BASE}/annex-requests/${id}/unmark-sent/`, {}));

export const resolveAnnexRequest = (
  id: string,
  payload: {
    outcome: "signed" | "rejected";
    resolved_on?: string;
    annex_number?: string;
    signed_deadline?: string | null;
  },
) => unwrap<ZEAnnexRequest>(apiClient.post(`${BASE}/annex-requests/${id}/resolve/`, payload));

export const reopenAnnexRequest = (id: string) =>
  unwrap<ZEAnnexRequest>(apiClient.post(`${BASE}/annex-requests/${id}/reopen/`, {}));

// ── MPZP (zamawianie wypisu i wyrysu z planu miejscowego w urzędzie gminy) ────
export const getMpzpCandidates = (params: Record<string, string> = {}) => {
  const qs = new URLSearchParams(params).toString();
  return unwrap<MpzpCandidatesResponse>(
    apiClient.get(`${BASE}/mpzp-orders/candidates/${qs ? `?${qs}` : ""}`),
  );
};

/**
 * Wyszukanie urzędu gminy w internecie (Claude + web search) — zadanie w tle.
 * Bez `id` zamówienia, bo urzędu szuka się w modalu, ZANIM zamówienie powstanie:
 * bez adresata nie da się go nawet utworzyć.
 */
export const lookupMpzpOffice = (payload: {
  commune: string;
  county?: string;
  voivodeship?: string;
  teryts?: string[];
}) => unwrap<{ task_id: string }>(apiClient.post(`${BASE}/mpzp-orders/lookup-office/`, payload));

export const getMpzpOfficeMatches = (params: {
  commune: string;
  county?: string;
  voivodeship?: string;
}) =>
  unwrap<{ matches: { id: string; name: string; full_address: string; bank_account: string }[] }>(
    apiClient.get(`${BASE}/mpzp-orders/suggest-office/`, { params }),
  );

export const getMpzpOrders = (params: Record<string, string> = {}) => {
  const qs = new URLSearchParams(params).toString();
  return unwrap<MpzpOrderRow[]>(apiClient.get(`${BASE}/mpzp-orders/${qs ? `?${qs}` : ""}`));
};

export const getMpzpOrder = (id: string) =>
  unwrap<MpzpOrder>(apiClient.get(`${BASE}/mpzp-orders/${id}/`));

export const getMpzpOrdersForParcel = (parcelId: string) =>
  unwrap<MpzpOrderForParcel[]>(
    apiClient.get(`${BASE}/mpzp-orders/for-parcel/`, { params: { parcel: parcelId } }),
  );

export const createMpzpOrder = (payload: MpzpOrderCreatePayload) =>
  unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/`, payload));

/** Wypis i wyrys pozyskany ręcznie poza ELGW5 — z karty działki, same pliki. Powstaje
 * zamówienie „otrzymane" z flagą obtained_manually (bez wniosku, opłat i wysyłki). */
export const addManualMpzpOrder = (parcelId: string, files: File[]) => {
  const fd = new FormData();
  fd.append("parcel", parcelId);
  files.forEach((f) => fd.append("files", f));
  return unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/add-manual/`, fd, MULTIPART));
};

export const updateMpzpOrder = (id: string, payload: Partial<MpzpOrderCreatePayload>) =>
  unwrap<MpzpOrder>(apiClient.patch(`${BASE}/mpzp-orders/${id}/`, payload));

export const deleteMpzpOrder = (id: string) => apiClient.delete(`${BASE}/mpzp-orders/${id}/`);

export const generateMpzpDocuments = (id: string) =>
  unwrap<{ task_id: string }>(apiClient.post(`${BASE}/mpzp-orders/${id}/generate/`, {}));

export const requestMpzpFee = (id: string, payload: RequestDooFeePayload) =>
  unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/${id}/request-fee/`, payload));

export const getMpzpFeeCandidates = () =>
  unwrap<DooFeeCandidate[]>(apiClient.get(`${BASE}/mpzp-orders/fee-candidates/`));

export const editMpzpDoo = (id: string, payload: EditDooPayload) =>
  unwrap<MpzpOrder>(
    apiClient.post(`${BASE}/mpzp-orders/${id}/edit-doo/`, editDooForm(payload), MULTIPART),
  );

/** Wgraj pismo gminy o wysokości opłaty — odczyt kwoty leci w tle (task_id). */
export const uploadMpzpFeeLetter = (id: string, file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/mpzp-orders/${id}/fee-letter/`, fd, MULTIPART),
  );
};

export const setMpzpPoaFee = (
  id: string,
  payload: {
    mode: "new" | "existing" | "not_required" | "detach" | "set_office";
    fee?: string;
    reason?: string;
    office_account_number?: string;
    /** Ręczna korekta urzędu opłaty ("" = powrót do rozpoznania automatycznego). */
    stamp_duty_office?: string;
  },
) => unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/${id}/poa-fee/`, payload));

export const getMpzpPoaFeeSuggestion = (id: string) =>
  unwrap<PoaFeeSuggestion>(apiClient.get(`${BASE}/mpzp-orders/${id}/poa-fee-suggestion/`));

export const markMpzpSent = (id: string) =>
  unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/${id}/mark-sent/`, {}));

export const markMpzpReceived = (id: string) =>
  unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/${id}/mark-received/`, {}));

export const cancelMpzpOrder = (id: string) =>
  unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/${id}/cancel/`, {}));

// Przepięcie zamawiającego (created_by) na inną osobę z GK — wpis „kto → kto"
// odkłada się w historii zamówienia.
export const changeMpzpOrderer = (id: string, userId: string) =>
  unwrap<MpzpOrder>(
    apiClient.post(`${BASE}/mpzp-orders/${id}/change-orderer/`, { user: userId }),
  );

export const completeMpzpOrder = (id: string) =>
  unwrap<MpzpOrder>(apiClient.post(`${BASE}/mpzp-orders/${id}/complete/`, {}));

// Cofnięcie o JEDEN etap — cel wyznacza backend (`revert_to`/`revert_label`).
export const revertMpzpOrder = (id: string, reason?: string) =>
  unwrap<MpzpOrder>(
    apiClient.post(`${BASE}/mpzp-orders/${id}/revert/`, reason ? { reason } : {}),
  );

export const uploadMpzpResultFiles = (id: string, files: File[], other = false) => {
  const fd = new FormData();
  files.forEach((f) => fd.append(other ? "other_files" : "files", f));
  return unwrap<MpzpResultFile[]>(
    apiClient.post(`${BASE}/mpzp-orders/${id}/result-files/`, fd, MULTIPART),
  );
};

// `reverted_to` != null: skasowano ostatni plik z gminy, więc backend cofnął status.
export const deleteMpzpResultFile = (id: string, resultFileId: string) =>
  unwrap<{ deleted: boolean; status: MpzpOrderStatus; reverted_to: MpzpOrderStatus | null }>(
    apiClient.delete(`${BASE}/mpzp-orders/${id}/result-files/${resultFileId}/`),
  );

// ── uzupełnianie danych działek (dwa różne źródła, oba w tle) ─────────────────
/** Województwo/powiat/gmina/obręb z krajowego rejestru działek (ULDK). */
export const fetchConnectionAdminData = (connectionId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/fetch-admin-data/`, {}),
  );

/** Miejscowość + ulica i numer z geokodera (GUGiK UUG → OSM). Inne źródło niż wyżej. */
export const fetchConnectionStreets = (connectionId: string, force = false) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/fetch-streets/`, force ? { force: 1 } : {}),
  );

export const fetchConnectionMpzpCoverage = (connectionId: string, force = false) =>
  unwrap<{ task_id: string }>(
    apiClient.post(
      `${BASE}/connections/${connectionId}/fetch-mpzp-coverage/`,
      force ? { force: 1 } : {},
    ),
  );

export const fetchConnectionProtectedAreas = (connectionId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/fetch-protected-areas/`, {}),
  );

// ── preanaliza dokumentów z urzędów ───────────────────────────────────────────
// Ekran „Dokumenty z urzędów": wrzucone pliki dostają propozycje zamówień, których
// mogą dotyczyć. Sama analiza leci w tle — lista odświeża się, aż wszystko wyjdzie
// ze statusu pending/running.
export const getOfficeDocScans = (params: Record<string, string> = {}) => {
  const qs = new URLSearchParams(params).toString();
  return unwrap<OfficeDocScan[]>(apiClient.get(`${BASE}/office-docs/${qs ? `?${qs}` : ""}`));
};

/** `asBatch` = „to jedna sprawa" (np. mapa zasadnicza + licencja z jednego maila):
 *  pliki dostają wspólną paczkę i jeden wspólny wynik rozpoznania. */
export const uploadOfficeDocs = (files: File[], asBatch = false, label = "") => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  if (asBatch) fd.append("as_batch", "1");
  if (label) fd.append("label", label);
  return unwrap<OfficeDocScan[]>(apiClient.post(`${BASE}/office-docs/upload/`, fd, MULTIPART));
};

export const getOfficeDocBatches = () =>
  unwrap<OfficeDocBatch[]>(apiClient.get(`${BASE}/office-docs/batches/`));

/** Łączenie już wgranych dokumentów w paczkę — nie woła modelu, scala to, co wiadomo. */
export const groupOfficeDocs = (ids: string[], label = "", batch?: string) =>
  unwrap<OfficeDocBatch>(
    apiClient.post(`${BASE}/office-docs/group/`, { ids, label, ...(batch ? { batch } : {}) }),
  );

export const ungroupOfficeDoc = (id: string) =>
  unwrap<OfficeDocScan>(apiClient.post(`${BASE}/office-docs/${id}/ungroup/`, {}));

export const reanalyzeOfficeDoc = (id: string) =>
  unwrap<OfficeDocScan>(apiClient.post(`${BASE}/office-docs/${id}/reanalyze/`, {}));

export const deleteOfficeDoc = (id: string) =>
  unwrap<{ deleted: boolean }>(apiClient.delete(`${BASE}/office-docs/${id}/`));

// ── Wody Polskie (przekroczenia cieków + zamówienie u podwykonawcy) ─────────────
export type WodyMode = "internal" | "contractor";
export type HydroCrossingKind =
  | "zblizenie" | "przejscie_ziemne" | "przejscie_napowietrzne"
  | "przebudowa_napowietrzna" | "demontaz_napowietrzny" | "teren_zalewowy";

export interface HydroCrossingRow {
  id: string;
  kind: HydroCrossingKind;
  kind_display: string;
  event_type: "przeciecie_cieku" | "wejscie_dzialka_wp" | "zblizenie_wal" | "teren_zalewowy" | "reczne";
  event_type_display: string;
  feature_name: string;
  source: string;
  parcel: string | null;
  parcel_number: string | null;
  in_oszp_zone: boolean;
  distance_m: number | null;
  needs_review: boolean;
  /** Ile system wie o RODZAJU obiektu. „unlikely" = obiekt wałowy bez cieku w
   *  promieniu 200 m i poza terenem zalewowym, czyli raczej skarpa/nasyp/grobla. */
  confidence: "confirmed" | "review" | "unlikely";
  confidence_display: string;
  is_excluded: boolean;
  is_manual: boolean;
  is_edited: boolean;
  note: string;
  wp_office: { nadzor?: string; zarzad?: string; rzgw?: string };
  lat: number | null;
  lng: number | null;
  is_frozen: boolean;
  order_number: string | null;
  created_at: string;
}

export interface WodyOrderRow {
  id: string;
  order: string;
  order_number: string;
  order_status: string;
  order_status_display: string;
  contractor_name: string | null;
  deadline: string | null;
  kind: "primary" | "correction";
  is_paid: boolean;
  crossings_snapshot: Record<string, unknown>[];
  crossings_count: number;
  created_at: string;
}

export interface WodyPanelState {
  mode: WodyMode;
  can_manage: boolean;
  wody_milestones: {
    key: string; label: string; state: string; state_display: string;
    completed_at: string | null;
  }[];
  analysis: {
    computed_at: string | null;
    route_version: string | null;
    sources: Record<string, string>;
    display_geom: unknown | null;
    task: AsyncTaskRecord | null;
    // Detekcja policzona na INNEJ (starszej) wersji trasy niż bieżąca — wynik poniżej
    // opisuje nieaktualny przebieg. Etykiety obu wersji do komunikatu.
    // Pola opcjonalne: starszy backend ich nie przysyła.
    is_stale?: boolean;
    analysis_route_label?: string | null;
    current_route_label?: string | null;
  };
  crossings: HydroCrossingRow[];
  counts: Partial<Record<HydroCrossingKind, number>>;
  kinds: { value: HydroCrossingKind; label: string }[];
  orders: WodyOrderRow[];
  milestone_proposals: {
    node: string; label: string; current_state: string;
    proposed_state: string; reason: string;
  }[];
  route_geojson: unknown | null;
  milestone_key: string | null;
  proceeding: MdcpPanelState["proceeding"];
  /** Ile zdarzeń wykazu czeka na zlecenie podwykonawcy (0 = wszystko zlecone). */
  pending_count?: number;
  /** Martwe zlecenie / postępowanie, przy którym stoi zamrożony wykaz (z radą, co zrobić). */
  dead_scope?: { kind: "order" | "proceeding"; label: string; hint: string } | null;
}

export const getWodyPanel = (connectionId: string) =>
  unwrap<WodyPanelState>(apiClient.get(`${BASE}/connections/${connectionId}/wody/`));

export const setWodyMode = (connectionId: string, mode: WodyMode) =>
  unwrap<WodyPanelState>(apiClient.patch(`${BASE}/connections/${connectionId}/wody/`, { mode }));

export const createWodyOrder = (connectionId: string, fd: FormData) =>
  unwrap<WodyPanelState>(apiClient.post(`${BASE}/connections/${connectionId}/wody/`, fd, MULTIPART));

export const recomputeWody = (connectionId: string) =>
  unwrap<{ task_id: string }>(
    apiClient.post(`${BASE}/connections/${connectionId}/wody/recompute/`, {}),
  );

export const updateWodyCrossing = (id: string, patch: Partial<{
  kind: HydroCrossingKind; is_excluded: boolean; note: string; feature_name: string;
}>) =>
  unwrap<HydroCrossingRow>(apiClient.patch(`${BASE}/wody-crossings/${id}/`, patch));

export const createWodyCrossing = (body: {
  connection: string; lat: number; lng: number; kind: HydroCrossingKind;
  feature_name?: string; note?: string;
}) =>
  unwrap<HydroCrossingRow>(apiClient.post(`${BASE}/wody-crossings/`, body));

export const deleteWodyCrossing = (id: string) =>
  apiClient.delete(`${BASE}/wody-crossings/${id}/`);

export interface WodyContractorRow {
  id: string;
  name: string;
  matched_items: string[];
  rating: number | null;
  rating_count: number;
}
export const getWodyContractors = (connectionId: string) =>
  unwrap<{ contractors: WodyContractorRow[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/wody/contractors/`),
  ).then((d) => d.contractors);

export interface WodySimulatedContractor extends WodyContractorRow {
  amount_net: string;
  items: {
    line_item_type: string; name: string; unit: string;
    quantity: number; rate: string; total: string; reason: string;
  }[];
  note: string;
}
export const simulateWodyPrices = (connectionId: string) =>
  unwrap<{ contractors: WodySimulatedContractor[]; errors: string[]; skipped: number }>(
    apiClient.post(`${BASE}/connections/${connectionId}/wody/simulate-prices/`, {}),
  );

export const suggestWodyItems = (connectionId: string, contractorId: string) =>
  unwrap<MdcpAiSuggestion>(
    apiClient.post(`${BASE}/connections/${connectionId}/wody/suggest-items/`, { contractor: contractorId }),
  );

export const createWodyProceeding = (connectionId: string, payload: StageProceedingInput) =>
  unwrap<WodyPanelState>(
    apiClient.post(`${BASE}/connections/${connectionId}/wody/proceeding/`, payload),
  );
export const releaseWodyProceeding = (connectionId: string) =>
  unwrap<WodyPanelState>(
    apiClient.delete(`${BASE}/connections/${connectionId}/wody/proceeding/`),
  );

// Wykaz od strony zamówienia (nasz zespół + podwykonawca-właściciel): zamrożony snapshot.
export interface WodyOrderCrossingSnap {
  kind: HydroCrossingKind;
  kind_label: string;
  event_type: string;
  feature_name: string;
  parcel: string;
  in_oszp_zone: boolean;
  note: string;
  wp_office: { nadzor?: string; zarzad?: string; rzgw?: string };
  lat: number | null;
  lng: number | null;
}
export const getWodyOrderCrossings = (orderId: string) =>
  unwrap<{ crossings: WodyOrderCrossingSnap[] }>(
    apiClient.get(`${BASE}/wody/order/${orderId}/crossings/`),
  ).then((d) => d.crossings);

// ── „Planowane do zlecenia" + rejestr eksportów mapy podwykonawcy ─────────────
export interface PlannedAssignment {
  id: string;
  scope: "wpw" | "mdcp_geodezja";
  scope_display: string;
  contractor_id: string;
  contractor_name: string;
  expected_month: string | null; // "RRRR-MM"
  note: string;
  created_at: string;
  created_by_name: string | null;
  withdrawn_at: string | null;
  withdrawn_by_name: string | null;
  fulfilled_at: string | null;
  fulfilled_by_order_number: string | null;
  is_active: boolean;
}

export interface ContractorExportLogRow {
  id: string;
  created_at: string;
  user_name: string | null;
  contractor_name: string;
  order_number: string | null;
  scope: "wpw" | "mdcp_geodezja";
  file_format: string;
  row_count: number;
}

export const getPlannedAssignments = (connectionId: string) =>
  unwrap<{ assignments: PlannedAssignment[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/planned-assignments/`),
  );

export const createPlannedAssignment = (
  connectionId: string,
  body: { scope: string; contractor_id: string; expected_month: string; note?: string },
) =>
  unwrap<PlannedAssignment>(
    apiClient.post(`${BASE}/connections/${connectionId}/planned-assignments/`, body),
  );

export const withdrawPlannedAssignment = (assignmentId: string) =>
  unwrap<PlannedAssignment>(
    apiClient.post(`${BASE}/planned-assignments/${assignmentId}/withdraw/`, {}),
  );

export const getContractorExportLog = (connectionId: string) =>
  unwrap<{ exports: ContractorExportLogRow[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/contractor-exports/`),
  );

export interface ContractorPreviewTarget {
  contractor_id: string;
  contractor_name: string;
  ordered_scopes: string[];
  planned_scopes: string[];
}

export const getContractorPreviewTargets = (connectionId: string) =>
  unwrap<{ targets: ContractorPreviewTarget[] }>(
    apiClient.get(`${BASE}/connections/${connectionId}/contractor-preview-targets/`),
  );

export interface PlannedAssignmentContractorOption {
  id: string;
  name: string;
  matched_items: string[];
  /** Skąd firma jest w kręgu: typ prac w kartotece i/lub pozycja w cenniku. */
  eligible_via?: ("work_type" | "line_item")[];
  work_types?: string[];
}

// Firmy z kręgu zakresu — typ prac w kartotece ALBO pozycja umowy ramowej (jak przy zamawianiu).
export const getPlannedAssignmentContractors = (scope: string) =>
  unwrap<{ contractors: PlannedAssignmentContractorOption[] }>(
    apiClient.get(`${BASE}/planned-assignment-contractors/`, { params: { scope } }),
  );

// ── kalendarz ponagleń (telefony kontrolne) ────────────────────────────────────
export const getControlCalls = (params: Record<string, string>) => {
  const qs = new URLSearchParams(params).toString();
  // Kalendarz potrzebuje też `meta` (zaległości spoza okna, terminy rejestrów),
  // więc bierzemy całą kopertę zamiast samego `data`.
  return apiClient
    .get<{
      data: import("./types").ControlCallCalendar;
      meta: import("./types").ControlCallCalendarMeta;
    }>(`${BASE}/control-calls/?${qs}`)
    .then((r) => r.data);
};

export const markControlCallDone = (id: string, note: string) =>
  unwrap<import("./types").ControlCallReminder>(
    apiClient.post(`${BASE}/control-calls/${id}/done/`, note ? { note } : {}),
  );

/** Ponaglenie własne (rejestr „Własne") — zawsze dla pytającego. */
export const createControlCall = (payload: { title: string; due_date: string }) =>
  unwrap<import("./types").ControlCallReminder>(apiClient.post(`${BASE}/control-calls/`, payload));

export const deleteControlCall = (id: string) => apiClient.delete(`${BASE}/control-calls/${id}/`);

/** Pracownicy do wyboru w filtrze ponagleń (płaska lista, bez podwykonawców). */
export const getSelectableEmployees = () =>
  apiClient
    .get<{ data: { id: string; first_name: string; last_name: string; username: string }[] }>(
      "/auth/users/?selectable=true",
    )
    .then((r) => r.data.data ?? []);
